import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useCallback, useRef } from 'react';
import { getLang } from '../i18n';
import { hasBusinessHubFeatureAccess, hasMailboxIdentityAccess } from '../utils/verificationAccess';
import { BusinessHubShell } from './BusinessHubShell';
import CustomerGuidanceCenter from './CustomerGuidanceCenter';
import AuthorizedRecipientHub from './AuthorizedRecipientHub';
import CompanyDataWizard, { companyDraftFromVerification } from './CompanyDataWizard';
import { normalizeCustomerType } from '../utils/verificationCompanyModel';
import { buildApiAssetUrl as resolveApiAssetUrl } from '../utils/apiUrl';
import { findBusinessAddressDocuments, formatBusinessAddress } from '../utils/businessAddress';
import { buildCustomerDashboardNotices } from '../utils/customerDashboardNotices';
import { getSubscriptionPaymentAction } from '../utils/subscriptionPaymentRecovery';
import { getDetachedOpenSubscriptionChanges, OPEN_SUBSCRIPTION_CHANGE_STATUSES } from '../utils/subscriptionChangePresentation';
import { belongsToLegalEntity, selectLegalEntity, selectBillingAddress } from '../utils/legalEntityScope';
import { CHECKOUT_PREPARATION_TIMEOUT_MS, CHECKOUT_RECOVERY_POLL_MS, checkoutPreparationStage, closeCheckoutPreparationWindow, matchesRecoverableChange, openCheckoutPreparationWindow, openPreparedCheckout } from '../utils/subscriptionCheckoutRecovery';
import CompanyDataChangeHistory from './CompanyDataChangeHistory';
import GoogleReviewSummary from './GoogleReviewSummary';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const WEBSITE_URL = process.env.REACT_APP_WEBSITE_URL || 'https://yourgermanaddress-website.vercel.app/';
const ui = (de, en) => (getLang() === 'en' ? en : de);
const BUSINESS_PLANS = [
  { code: 'basic', name: 'Basic', price: '39,99 €', deadline: '30 Tage', deadlineEn: '30 days' },
  { code: 'standard', name: 'Standard', price: '49,99 €', deadline: '14 Tage', deadlineEn: '14 days' },
  { code: 'pro', name: 'Pro', price: '69,99 €', deadline: '7 Tage', deadlineEn: '7 days' },
  { code: 'premium', name: 'Premium', price: '129,99 €', deadline: '24 Stunden', deadlineEn: '24 hours' },
];

const CHECKOUT_TIMING_KEY = 'yga_subscription_checkout_timing';

function newCheckoutFlow(kind, subscriptionId) {
  const startedAt = Date.now();
  const flowId = globalThis.crypto?.randomUUID?.() || `${startedAt}-${Math.random().toString(16).slice(2)}`;
  const timing = { flowId, kind, subscriptionId: String(subscriptionId), startedAt };
  try { window.sessionStorage.setItem(CHECKOUT_TIMING_KEY, JSON.stringify(timing)); } catch {}
  return timing;
}

function updateCheckoutFlow(patch) {
  try {
    const current = JSON.parse(window.sessionStorage.getItem(CHECKOUT_TIMING_KEY) || '{}');
    window.sessionStorage.setItem(CHECKOUT_TIMING_KEY, JSON.stringify({ ...current, ...patch }));
  } catch {}
}

function wait(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function countText(count, deSingular, dePlural, enSingular, enPlural) {
  const value = Number(count || 0);
  return getLang() === 'en'
    ? `${value} ${value === 1 ? enSingular : enPlural}`
    : `${value} ${value === 1 ? deSingular : dePlural}`;
}

function normalizeText(value) {
  return String(value || '').trim();
}

function legalEntityLabel(entity) {
  if (!entity) return ui('Unternehmensprofil', 'Company profile');
  if (normalizeText(entity.profile_label)) return normalizeText(entity.profile_label);
  if (normalizeText(entity.legal_name)) return normalizeText(entity.legal_name);
  if (entity.entity_type === 'formation') {
    const number = Number(entity.formation_number || 1);
    return ui(`Unternehmen in Gründung ${number}`, `Company in formation ${number}`);
  }
  if (entity.entity_type === 'private') return ui('Privatprofil', 'Private profile');
  return ui('Unternehmensprofil', 'Company profile');
}

const EMPTY_MAIL_FORWARDING_ADDRESS = {
  recipient_name: '',
  recipient_company: '',
  recipient_street: '',
  recipient_address_extra: '',
  recipient_zip: '',
  recipient_city: '',
  recipient_country: '',
  recipient_phone: '',
};

const EMPTY_ADDRESS_BOOK_ENTRY = {
  label: '',
  ...EMPTY_MAIL_FORWARDING_ADDRESS,
  is_default: false,
};

function shippingFieldsFromAddress(address) {
  return Object.keys(EMPTY_MAIL_FORWARDING_ADDRESS).reduce((result, field) => {
    result[field] = address?.[field] || '';
    return result;
  }, {});
}

function ShippingAddressFields({ address, setAddress, addresses }) {
  const update = (field) => (event) => setAddress((current) => ({ ...current, [field]: event.target.value }));
  return (
    <div className="form-grid">
      {addresses.length > 0 ? (
        <label className="full-width">
          {ui('Gespeicherte Adresse verwenden', 'Use a saved address')}
          <select
            defaultValue=""
            onChange={(event) => {
              const saved = addresses.find((item) => String(item.id) === event.target.value);
              if (saved) setAddress(shippingFieldsFromAddress(saved));
            }}
          >
            <option value="">{ui('Bitte auswählen', 'Please select')}</option>
            {addresses.map((saved) => (
              <option key={saved.id} value={saved.id}>
                {saved.label}{saved.is_default ? ui(' (Standard)', ' (default)') : ''}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <label>{ui('Empfängername', 'Recipient name')}<input required autoComplete="name" value={address.recipient_name} onChange={update('recipient_name')} /></label>
      <label>{ui('Firma (optional)', 'Company (optional)')}<input autoComplete="organization" value={address.recipient_company} onChange={update('recipient_company')} /></label>
      <label className="full-width">{ui('Straße und Hausnummer', 'Street and house number')}<input required autoComplete="street-address" value={address.recipient_street} onChange={update('recipient_street')} /></label>
      <label className="full-width">{ui('Adresszusatz (optional)', 'Address line 2 (optional)')}<input value={address.recipient_address_extra} onChange={update('recipient_address_extra')} /></label>
      <label>{ui('PLZ', 'Postal code')}<input required autoComplete="postal-code" value={address.recipient_zip} onChange={update('recipient_zip')} /></label>
      <label>{ui('Ort', 'City')}<input required autoComplete="address-level2" value={address.recipient_city} onChange={update('recipient_city')} /></label>
      <label>{ui('Land', 'Country')}<input required autoComplete="country-name" value={address.recipient_country} onChange={update('recipient_country')} /></label>
      <label>{ui('Telefonnummer', 'Phone number')}<input type="tel" required autoComplete="tel" value={address.recipient_phone} onChange={update('recipient_phone')} /></label>
    </div>
  );
}

function mailForwardingFeeCents(pricing, itemCount) {
  const calculated = Number(pricing?.base_fee_cents || 0) +
    Number(pricing?.item_fee_cents || 0) * Number(itemCount || 0);
  const maximum = Number(pricing?.maximum_fee_cents || 0);
  return maximum > 0 ? Math.min(calculated, maximum) : calculated;
}

function paymentMethodLabel(type) {
  const labels = {
    card: ui('Karte', 'Card'),
    sepa_debit: ui('SEPA-Lastschrift', 'SEPA Direct Debit'),
    paypal: 'PayPal',
  };
  return labels[normalizeText(type).toLowerCase()] || ui('Nicht verfügbar', 'Not available');
}

function formatRefundAmount(preview) {
  const amount = Number(preview?.amount);
  if (!Number.isFinite(amount)) return ui('noch nicht verfügbar', 'not available yet');
  try {
    return new Intl.NumberFormat(getLang() === 'en' ? 'en-GB' : 'de-DE', {
      style: 'currency',
      currency: preview?.currency || 'EUR',
    }).format(amount);
  } catch (err) {
    return `${amount.toFixed(2)} ${preview?.currency || 'EUR'}`;
  }
}

function formatInvoiceAmount(invoice) {
  const amount = Number(invoice?.amount);
  if (!Number.isFinite(amount)) return '-';
  try {
    return new Intl.NumberFormat(getLang() === 'en' ? 'en-GB' : 'de-DE', {
      style: 'currency',
      currency: invoice?.currency || 'EUR',
    }).format(amount);
  } catch (err) {
    return `${amount.toFixed(2)} ${invoice?.currency || 'EUR'}`;
  }
}

function sevdeskInvoiceStatus(status) {
  const labels = {
    50: ui('Deaktiviert', 'Deactivated'),
    100: ui('Entwurf', 'Draft'),
    200: ui('Offen', 'Open'),
    750: ui('Teilweise bezahlt', 'Partially paid'),
    1000: ui('Bezahlt', 'Paid'),
  };
  return labels[Number(status)] || ui('Verfügbar', 'Available');
}

function statusLabel(status) {
  const map = {
    new: ui('Offen', 'Open'), pending: ui('Offen', 'Open'), submitted: ui('Übermittelt', 'Submitted'),
    in_review: ui('Wird geprüft', 'In review'), additional_requested: ui('Nachforderung offen', 'Additional information requested'),
    approved: ui('Bestätigt', 'Approved'), rejected: ui('Abgelehnt', 'Rejected'),
    already_verified: ui('Bereits verifiziert', 'Already verified'), not_required: ui('Nicht erforderlich', 'Not required'),
  };
  return map[normalizeText(status).toLowerCase()] || status || '-';
}

function isExpiredCheckout(payment) {
  const status = normalizeText(payment?.status).toLowerCase();
  if (!payment || ['paid', 'processing'].includes(status)) return false;
  if (status !== 'open') return true;
  const expiresAt = payment.expires_at ? new Date(payment.expires_at).getTime() : NaN;
  return !Number.isFinite(expiresAt) || expiresAt <= Date.now();
}

function paymentRequiresAttention(payment) {
  return payment && payment.status !== 'paid' && payment.status !== 'processing';
}

function requestPaymentAttentionCount(request) {
  const payments = request?.additional_service_payments || [];
  if (payments.length === 0) return 0;
  return payments.filter(paymentRequiresAttention).length;
}

function statusTone(status) {
  const value = normalizeText(status).toLowerCase();
  if (['approved', 'already_verified', 'not_required'].includes(value)) return 'success';
  if (['additional_requested', 'pending', 'new'].includes(value)) return 'warning';
  if (value === 'rejected') return 'danger';
  return 'info';
}

function customerTypeLabel(type) {
  const value = normalizeText(type).toLowerCase();
  const map = {
    privat: ui('Privatkunde', 'Private customer'), gewerbe: ui('Unternehmen', 'Business'), unternehmen: ui('Unternehmen', 'Business'),
    einzelunternehmen: ui('Einzelunternehmen', 'Sole proprietorship'), kapitalgesellschaft: ui('Deutsche Kapitalgesellschaft', 'German corporation'),
    verein: ui('Verein / eingetragener Verein (e. V.)', 'Association / registered association (e. V.)'),
    ausland: ui('Ausländisches Unternehmen', 'Foreign company'), gruendung: ui('Gründung geplant', 'Planned company formation'),
    'gründung': ui('Gründung geplant', 'Planned company formation'), freelancer: ui('Freiberufler', 'Freelancer'),
  };
  return map[value] || type || '';
}

function logisticsServiceLabel(serviceType, packageWeightClass) {
  if (serviceType === 'parcel_forwarding') {
    const weightMap = {
      bis_5kg: ui('Paketweiterleitung bis 5 kg', 'Parcel forwarding up to 5 kg'),
      bis_10kg: ui('Paketweiterleitung bis 10 kg', 'Parcel forwarding up to 10 kg'),
      bis_20kg: ui('Paketweiterleitung bis 20 kg', 'Parcel forwarding up to 20 kg'),
    };
    return weightMap[packageWeightClass] || ui('Paketweiterleitung', 'Parcel forwarding');
  }

  if (serviceType === 'physical_collective_shipping') {
    return ui('Physischer Sammelversand', 'Physical collective shipping');
  }

  return ui('Zusatzleistung', 'Additional service');
}

function logisticsStatusLabel(status) {
  const map = {
    new: ui('Offen', 'Open'),
    form_submitted: ui('Angaben übermittelt', 'Details submitted'),
    expected: ui('Erwartet', 'Expected'),
    received: ui('Eingegangen', 'Received'),
    shipping_costs_open: ui('Versandkosten offen', 'Shipping costs outstanding'),
    paid: ui('Bezahlt', 'Paid'),
    shipped: ui('Versendet', 'Shipped'),
    completed: ui('Abgeschlossen', 'Completed'),
    cancelled: ui('Storniert', 'Cancelled'),
  };
  return map[normalizeText(status).toLowerCase()] || status || ui('Offen', 'Open');
}

function logisticsStatusTone(status) {
  const value = normalizeText(status).toLowerCase();
  if (['completed', 'shipped', 'paid'].includes(value)) return 'success';
  if (['cancelled'].includes(value)) return 'danger';
  if (['new', 'shipping_costs_open'].includes(value)) return 'warning';
  return 'info';
}

function CustomerStatusBadge({ status }) {
  return <span className={`status-pill status-pill--${statusTone(status)}`}>{statusLabel(status)}</span>;
}

function LogisticsStatusBadge({ status }) {
  return <span className={`status-pill status-pill--${logisticsStatusTone(status)}`}>{logisticsStatusLabel(status)}</span>;
}

function subscriptionStatusLabel(status) {
  const value = normalizeText(status).toUpperCase();
  const map = {
    ACTIVE: ui('Aktiv', 'Active'),
    CANCELLED: ui('Gekündigt', 'Cancelled'),
    CANCELED: ui('Gekündigt', 'Cancelled'),
    PAUSED: ui('Pausiert', 'Paused'),
    EXPIRED: ui('Beendet', 'Expired'),
    PENDING_PAYMENT: ui('Zahlung ausstehend', 'Payment pending'),
    PROCESSING: ui('Zahlung wird verarbeitet', 'Payment processing'),
    PAST_DUE: ui('Zahlung fehlgeschlagen', 'Payment failed'),
    ENDED: ui('Beendet', 'Ended'),
  };
  return map[value] || status || '-';
}

function subscriptionStatusTone(status) {
  const value = normalizeText(status).toUpperCase();
  if (value === 'ACTIVE') return 'success';
  if (['CANCELLED', 'CANCELED', 'EXPIRED', 'ENDED'].includes(value)) return 'danger';
  if (['PAUSED', 'PENDING_PAYMENT', 'PROCESSING', 'PAST_DUE'].includes(value)) return 'warning';
  return 'neutral';
}

function SubscriptionStatusBadge({ status }) {
  return <span className={`status-pill status-pill--${subscriptionStatusTone(status)}`}>{subscriptionStatusLabel(status)}</span>;
}

function formatDateTime(value) {
  if (!value) return '-';
  try {
  return new Date(value).toLocaleString(getLang() === 'en' ? 'en-GB' : 'de-DE');
  } catch (err) {
    return value;
  }
}

function formatDate(value) {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleDateString(getLang() === 'en' ? 'en-GB' : 'de-DE');
  } catch (err) {
    return value;
  }
}

function billingIntervalLabel(value) {
  const normalized = normalizeText(value).toLowerCase();
  if (['month', '1 month', 'monthly'].includes(normalized)) return ui('Monat', 'Month');
  if (['year', '1 year', 'yearly', 'annual'].includes(normalized)) return ui('Jahr', 'Year');
  return value || '-';
}

function buildApiAssetUrl(value) {
  return resolveApiAssetUrl(value, API_URL);
}

function CustomerDisplayLogo({ logoUrl }) {
  const [objectUrl, setObjectUrl] = useState('');

  useEffect(() => {
    if (!logoUrl) {
      setObjectUrl('');
      return undefined;
    }

    let cancelled = false;
    let nextObjectUrl = '';

    const loadLogo = async () => {
      try {
        const response = await fetch(buildApiAssetUrl(logoUrl), {
          headers: {
            Authorization: `Bearer ${readCustomerToken()}`,
          },
        });

        if (!response.ok) throw new Error(ui('Logo konnte nicht geladen werden.', 'The logo could not be loaded.'));
        const blob = await response.blob();
        nextObjectUrl = URL.createObjectURL(blob);
        if (!cancelled) setObjectUrl(nextObjectUrl);
      } catch (err) {
        if (!cancelled) setObjectUrl('');
      }
    };

    loadLogo();

    return () => {
      cancelled = true;
      if (nextObjectUrl) URL.revokeObjectURL(nextObjectUrl);
    };
  }, [logoUrl]);

  if (!objectUrl) return null;
  return <img className="customer-display-logo-preview" src={objectUrl} alt="" />;
}

function CustomerMailPreview({ item }) {
  const [objectUrl, setObjectUrl] = useState('');
  const [previewError, setPreviewError] = useState('');

  useEffect(() => {
    if (!item?.preview_url) {
      setObjectUrl('');
      setPreviewError('');
      return undefined;
    }
    let cancelled = false;
    let nextObjectUrl = '';
    const loadFile = async () => {
      try {
        setPreviewError('');
        const response = await fetch(buildApiAssetUrl(item.preview_url), {
          headers: { Authorization: `Bearer ${readCustomerToken()}` },
        });
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.error || ui('Poststück konnte nicht geladen werden.', 'Mail item could not be loaded.'));
        }
        const blob = await response.blob();
        nextObjectUrl = URL.createObjectURL(blob);
        if (!cancelled) setObjectUrl(nextObjectUrl);
      } catch (error) {
        if (!cancelled) setPreviewError(error.message || ui('Vorschau nicht verfügbar.', 'Preview unavailable.'));
      }
    };
    loadFile();
    return () => {
      cancelled = true;
      if (nextObjectUrl) URL.revokeObjectURL(nextObjectUrl);
    };
  }, [item]);

  if (previewError) return <div className="hint-box">{previewError}</div>;
  if (!objectUrl) return <div className="info-box">{ui('Vorschau wird geladen...', 'Loading preview...')}</div>;
  if ((item.mime_type || '').startsWith('image/')) {
    return <img className="customer-mail-preview__image" src={objectUrl} alt={item.filename || ''} />;
  }
  return <iframe className="customer-mail-preview__frame" src={objectUrl} title={item.filename || 'Poststück'} />;
}

function saveCustomerToken(token) {
  localStorage.setItem('customerToken', token);
}

function readCustomerToken() {
  return localStorage.getItem('customerToken') || '';
}

export function CustomerLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const mailEntry = new URLSearchParams(location.search).get('next') === 'mailbox';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submitLogin = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/customer/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(payload.error || 'Login fehlgeschlagen.');
      }

      saveCustomerToken(payload.token);
      navigate(mailEntry ? '/account/dashboard?tab=mailbox' : '/account/dashboard', { replace: true });
    } catch (err) {
      setError(err.message || 'Login fehlgeschlagen.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="page-shell customer-login-shell">
      <div className="customer-login-panel">
        <div className="brand-header customer-login-header">
          <div>
            <p className="eyebrow">YourGermanAddress</p>
            <h1>Business Hub</h1>
            <p className="subtitle">{ui('Ihre zentrale Plattform für Post, Verifizierung, Abonnements und Services.', 'Your central platform for mail, verification, subscriptions and services.')}</p>
            <a className="customer-login-website-link" href={WEBSITE_URL}>
              <span aria-hidden="true">←</span>
              {ui('Zurück zur Website', 'Back to website')}
            </a>
            <div className="customer-login-benefits" aria-label={ui('Funktionen im Business Hub', 'Business Hub features')}>
              <span>{ui('Post & Dokumente', 'Mail & documents')}</span>
              <span>{ui('Abos & Rechnungen', 'Subscriptions & invoices')}</span>
              <span>{ui('Verifizierung & Konto', 'Verification & account')}</span>
            </div>
          </div>
        </div>
        <div className="card customer-login-card">
        <form className="form-grid" onSubmit={submitLogin}>
          <label className="full-width">
            {ui('E-Mail-Adresse', 'Email address')}
            <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
          </label>
          <label className="full-width">
            {ui('Passwort', 'Password')}
            <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} required />
          </label>
          {error ? <div className="hint-box full-width">{error}</div> : null}
          <button className="primary-button full-width" type="submit" disabled={submitting}>
            {submitting ? ui('Login läuft...', 'Signing in...') : ui('Einloggen', 'Sign in')}
          </button>
          <p className="full-width">
            <Link to="/account/forgot-password">{ui('Passwort vergessen', 'Forgot password')}</Link>
          </p>
        </form>
        </div>
        <div className="card account-link-card customer-login-secondary">
        <h2>{ui('Noch kein Business-Hub-Konto?', 'No Business Hub account yet?')}</h2>
        <p>
          {ui(
            'Aktivieren Sie zuerst Ihren Business Hub mit Ihrer vorhandenen Bestell- oder Rechnungsnummer. Der Bestätigungslink geht an die bereits hinterlegte E-Mail-Adresse. Ihre bestehende Bestellung wird zugeordnet – Sie buchen nicht erneut.',
            'First activate your Business Hub using your existing order or invoice number. The confirmation link goes to the email address already on file. Your existing order is linked — you do not book again.'
          )}
        </p>
        <Link className="secondary-link-button" to="/account/activate-existing">
          {ui('Business Hub für bestehende Bestellung aktivieren', 'Activate Business Hub for an existing order')}
        </Link>
        </div>
      </div>
    </main>
  );
}

export function PaymentAccessRequest() {
  const { requestToken } = useParams();
  const requested = React.useRef(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (requested.current) return;
    requested.current = true;
    fetch(`${API_URL}/customer/payment-access/request/${encodeURIComponent(requestToken || '')}`, { method: 'POST' })
      .then((response) => response.json().catch(() => ({})))
      .then((payload) => setMessage(payload.message || ui(
        'Wenn der Link gültig ist, wurde ein neuer sicherer Zugang an die hinterlegte E-Mail-Adresse gesendet.',
        'If the link is valid, a new secure access link was sent to the stored email address.'
      )))
      .catch(() => setMessage(ui(
        'Wenn der Link gültig ist, wurde ein neuer sicherer Zugang an die hinterlegte E-Mail-Adresse gesendet.',
        'If the link is valid, a new secure access link was sent to the stored email address.'
      )));
  }, [requestToken]);

  return (
    <main className="page-shell customer-login-shell">
      <div className="customer-login-panel">
        <div className="brand-header customer-login-header"><div><p className="eyebrow">YourGermanAddress</p><h1>{ui('Sicherer Zahlungszugang', 'Secure payment access')}</h1><p className="subtitle">{ui('Geschützter Zugang zu Ihren Zahlungsdaten', 'Protected access to your payment details')}</p></div></div>
        <div className="card customer-login-card">
          {message
            ? <div className="info-box">{message}</div>
            : <p>{ui('Ein neuer Zugangslink wird angefordert…', 'Requesting a new access link…')}</p>}
          <p>{ui(
            'Bitte prüfen Sie Ihr E-Mail-Postfach. Aus Sicherheitsgründen zeigen wir hier keine Kontodaten an.',
            'Please check your inbox. For security reasons, no account details are shown here.'
          )}</p>
          <Link to="/account/login">{ui('Zum Business-Hub-Login', 'Go to Business Hub sign in')}</Link>
        </div>
      </div>
    </main>
  );
}

export function PaymentAccessConfirm() {
  const { accessToken } = useParams();
  const navigate = useNavigate();
  const [context, setContext] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/customer/payment-access/${encodeURIComponent(accessToken || '')}`)
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.error || ui('Der Zugangslink ist nicht mehr gültig.', 'The access link is no longer valid.'));
        setContext(payload);
      })
      .catch((err) => setError(err.message));
  }, [accessToken]);

  const confirm = async (event) => {
    event.preventDefault();
    setError('');
    if (!context?.account_exists && password !== confirmation) {
      setError(ui('Die Passwörter stimmen nicht überein.', 'The passwords do not match.'));
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/customer/payment-access/${encodeURIComponent(accessToken || '')}/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || ui('Der Zugang konnte nicht bestätigt werden.', 'Access could not be confirmed.'));
      saveCustomerToken(payload.token);
      navigate(payload.destination || '/account/dashboard?tab=subscriptions', { replace: true });
    } catch (err) {
      setError(err.message || ui('Der Zugang konnte nicht bestätigt werden.', 'Access could not be confirmed.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="page-shell customer-login-shell">
      <div className="customer-login-panel">
        <div className="brand-header customer-login-header"><div><p className="eyebrow">YourGermanAddress</p><h1>{ui('Zahlungsart ändern', 'Change payment method')}</h1><p className="subtitle">{ui('Sicher mit Ihrer bestehenden Bestellung fortfahren', 'Continue securely with your existing order')}</p></div></div>
        <div className="card customer-login-card">
          {error ? <div className="hint-box" role="alert">{error}</div> : null}
          {error ? <p>{ui('Verwenden Sie bitte den neuesten Link aus Ihrer E-Mail. Bei einem bestehenden Abonnement kann der Support außerdem jederzeit eine neue offizielle Zahlungsart-E-Mail senden.', 'Please use the latest link from your email. For an existing subscription, support can also send a new official payment-method email at any time.')}</p> : null}
          {!context && !error ? <p>{ui('Sicherer Zugang wird geprüft…', 'Checking secure access…')}</p> : null}
          {context ? (
            <form className="form-grid" onSubmit={confirm}>
              <div className="info-box full-width">
                {context.account_exists
                  ? ui('Melden Sie sich mit Ihrem bestehenden Business-Hub-Passwort an. Danach wird direkt das betroffene Abonnement geöffnet.', 'Sign in with your existing Business Hub password. The affected subscription will open directly afterwards.')
                  : ui('Legen Sie einmalig Ihr Business-Hub-Passwort fest. Ihre bestehende Bestellung und Ihr Abonnement werden automatisch zugeordnet.', 'Set your Business Hub password once. Your existing order and subscription will be linked automatically.')}
              </div>
              <label className="full-width">
                {context.account_exists ? ui('Business-Hub-Passwort', 'Business Hub password') : ui('Neues Business-Hub-Passwort', 'New Business Hub password')}
                <input type="password" minLength="8" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete={context.account_exists ? 'current-password' : 'new-password'} required />
              </label>
              {!context.account_exists ? (
                <label className="full-width">
                  {ui('Passwort wiederholen', 'Repeat password')}
                  <input type="password" minLength="8" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} autoComplete="new-password" required />
                </label>
              ) : null}
              <button className="primary-button full-width" type="submit" disabled={submitting}>
                {submitting ? ui('Zugang wird geöffnet…', 'Opening access…') : ui('Sicher fortfahren', 'Continue securely')}
              </button>
            </form>
          ) : null}
        </div>
      </div>
    </main>
  );
}

export function ExistingCustomerInvitationConfirm() {
  const { invitationToken } = useParams();
  const navigate = useNavigate();
  const [context, setContext] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/customer/account-invitation/${encodeURIComponent(invitationToken || '')}`)
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.error || ui('Der Einladungslink ist nicht gültig.', 'The invitation link is not valid.'));
        setContext(payload);
      })
      .catch((err) => setError(err.message));
  }, [invitationToken]);

  const confirmInvitation = async (event) => {
    event.preventDefault();
    if (password !== confirmation) return setError(ui('Die Passwörter stimmen nicht überein.', 'The passwords do not match.'));
    setSubmitting(true); setError('');
    try {
      const response = await fetch(`${API_URL}/customer/account-invitation/${encodeURIComponent(invitationToken || '')}/confirm`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || ui('Die Aktivierung konnte nicht abgeschlossen werden.', 'Activation could not be completed.'));
      saveCustomerToken(payload.token);
      navigate(payload.destination || '/account/dashboard?tab=subscriptions', { replace: true });
    } catch (err) { setError(err.message); } finally { setSubmitting(false); }
  };

  return (
    <main className="page-shell customer-login-shell"><div className="customer-login-panel">
      <div className="brand-header customer-login-header"><div><p className="eyebrow">YourGermanAddress</p><h1>{ui('Business Hub aktivieren', 'Activate Business Hub')}</h1><p className="subtitle">{ui('Sichere Aktivierung für bestehende Kunden', 'Secure activation for existing customers')}</p></div></div>
      <div className="card customer-login-card">
        {error ? <div className="hint-box" role="alert">{error}</div> : null}
        {!context && !error ? <p>{ui('Einladung wird sicher geprüft…', 'Checking invitation securely…')}</p> : null}
        {context ? <form className="form-grid" onSubmit={confirmInvitation}>
          <div className="info-box full-width">{ui('Diese E-Mail-Adresse ist fest mit der Einladung verbunden. Die Bestätigung erfolgt ausschließlich über diesen Einmallink.', 'This email address is bound to the invitation. Verification occurs only through this one-time link.')}</div>
          <label className="full-width">{ui('Bestätigte E-Mail-Adresse', 'Confirmed email address')}<input type="email" value={context.email} readOnly aria-readonly="true" /></label>
          <label className="full-width">{ui('Neues Business-Hub-Passwort', 'New Business Hub password')}<input type="password" minLength="8" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required /></label>
          <label className="full-width">{ui('Passwort wiederholen', 'Repeat password')}<input type="password" minLength="8" autoComplete="new-password" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} required /></label>
          <button className="primary-button full-width" type="submit" disabled={submitting}>{submitting ? ui('Aktivierung läuft…', 'Activating…') : ui('Business Hub sicher aktivieren', 'Activate Business Hub securely')}</button>
        </form> : null}
      </div>
    </div></main>
  );
}

export function ExistingCustomerRegistration() {
  const [orderReference, setOrderReference] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submitRegistration = async (event) => {
    event.preventDefault();
    setMessage('');
    setError('');

    if (password !== passwordConfirmation) {
      setError(ui('Die Passwörter stimmen nicht überein.', 'The passwords do not match.'));
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/customer/existing-customer-registration/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_reference: orderReference, password }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || ui('Aktivierung konnte nicht vorbereitet werden.', 'Activation could not be prepared.'));
      }
      setMessage(payload.message || ui(
        'Wenn wir Ihre Bestellung eindeutig zuordnen konnten, erhalten Sie eine E-Mail zur Aktivierung.',
        'If we could uniquely match your order, you will receive an activation email.'
      ));
      setPassword('');
      setPasswordConfirmation('');
    } catch (err) {
      setError(err.message || ui('Aktivierung konnte nicht vorbereitet werden.', 'Activation could not be prepared.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="page-shell">
      <div className="brand-header">
        <div>
          <p className="eyebrow">YourGermanAddress</p>
          <h1>{ui('Business Hub aktivieren', 'Activate Business Hub')}</h1>
          <p className="subtitle">
            {ui(
              'Verbinden Sie Ihre vorhandene Bestellung einmalig mit Ihrem persönlichen Business Hub.',
              'Link your existing order to your personal Business Hub once.'
            )}
          </p>
        </div>
      </div>
      <div className="card">
        {message ? <div className="success-box">{message}</div> : null}
        {error ? <div className="hint-box">{error}</div> : null}
        {!message ? (
          <form className="form-grid" onSubmit={submitRegistration}>
            <div className="info-box full-width">
              {ui(
                'Geben Sie die Bestell- oder Rechnungsnummer einer bestehenden Bestellung ein. Wir senden den Aktivierungslink ausschließlich an die dort bereits hinterlegte E-Mail-Adresse.',
                'Enter the order or invoice number of an existing order. We send the activation link only to the email address already stored with that order.'
              )}
            </div>
            <div className="existing-customer-activation-steps full-width" aria-labelledby="existing-customer-activation-title">
              <h2 id="existing-customer-activation-title">{ui('So funktioniert die sichere Zuordnung', 'How secure matching works')}</h2>
              <ol>
                <li>{ui(
                  'Sie geben eine vorhandene Bestell- oder Rechnungsnummer ein – nicht Ihre E-Mail-Adresse.',
                  'Enter an existing order or invoice number — not your email address.'
                )}</li>
                <li>{ui(
                  'Der einmalige Aktivierungslink geht nur an die E-Mail-Adresse, die bereits sicher zu dieser Buchung gespeichert ist.',
                  'The one-time activation link is sent only to the email address already securely stored for that booking.'
                )}</li>
                <li>{ui(
                  'Nach Bestätigung wird Ihr Business Hub mit dem bestehenden Kundenkonto verbunden. Buchungen, Abonnements, Dokumente und Unternehmenszuordnungen werden dabei nicht verändert.',
                  'After confirmation, your Business Hub is linked to the existing customer account. Bookings, subscriptions, documents and company assignments remain unchanged.'
                )}</li>
              </ol>
              <p>{ui(
                'Eine eingegebene oder vorausgefüllte E-Mail-Adresse gilt niemals allein als Identitätsnachweis. Bei einer nicht eindeutigen Nummer wird keine Zuordnung vorgenommen.',
                'An entered or prefilled email address is never treated as identity proof. If the reference is ambiguous, no account is linked.'
              )}</p>
            </div>
            <label className="full-width">
              {ui('Bestell- oder Rechnungsnummer', 'Order or invoice number')}
              <input
                type="text"
                value={orderReference}
                onChange={(event) => setOrderReference(event.target.value)}
                autoComplete="off"
                required
              />
            </label>
            <label className="full-width">
              {ui('Neues App-Passwort', 'New app password')}
              <input
                type="password"
                minLength="8"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
                required
              />
            </label>
            <label className="full-width">
              {ui('Passwort wiederholen', 'Repeat password')}
              <input
                type="password"
                minLength="8"
                value={passwordConfirmation}
                onChange={(event) => setPasswordConfirmation(event.target.value)}
                autoComplete="new-password"
                required
              />
            </label>
            <button className="primary-button full-width" type="submit" disabled={submitting}>
              {submitting
                ? ui('Aktivierung wird vorbereitet...', 'Preparing activation...')
                : ui('Aktivierungslink anfordern', 'Request activation link')}
            </button>
          </form>
        ) : null}
        <p className="account-form-footer">
          <Link to="/account/login">{ui('Zurück zum Login', 'Back to sign in')}</Link>
        </p>
      </div>
    </main>
  );
}

export function ExistingCustomerActivationConfirm() {
  const { claimToken } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const confirmActivation = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    const performConfirmation = async () => {
      try {
        const response = await fetch(`${API_URL}/customer/existing-customer-registration/confirm`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: claimToken, password }),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload.error || ui('Aktivierung konnte nicht bestätigt werden.', 'Activation could not be confirmed.'));
        }
        saveCustomerToken(payload.token);
        setMessage(payload.message || ui(
          'Ihre bestehende Bestellung wurde erfolgreich mit Ihrem Business Hub verbunden.',
          'Your existing order has been linked to your Business Hub successfully.'
        ));
      } catch (err) {
        setError(err.message || ui('Aktivierung konnte nicht bestätigt werden.', 'Activation could not be confirmed.'));
      } finally {
        setSubmitting(false);
      }
    };

    await performConfirmation();
  };

  return (
    <main className="page-shell">
      <div className="brand-header">
        <div>
          <p className="eyebrow">YourGermanAddress</p>
          <h1>{ui('Business Hub verbinden', 'Link Business Hub')}</h1>
          <p className="subtitle">
            {ui('Ihre bestehende Bestellung wird sicher mit der App verbunden.', 'Your existing order is being securely linked to the app.')}
          </p>
        </div>
      </div>
      <div className="card">
        {message ? <div className="success-box">{message}</div> : null}
        {error ? <div className="hint-box">{error}</div> : null}
        {!message ? (
          <form className="form-grid" onSubmit={confirmActivation}>
            <div className="info-box full-width">
              {ui(
                'Bestätigen Sie die Aktivierung mit dem Passwort, das Sie bei der Anforderung des Links gewählt haben.',
                'Confirm activation with the password you chose when requesting this link.'
              )}
            </div>
            <label className="full-width">
              {ui('Gewähltes App-Passwort', 'Chosen app password')}
              <input
                type="password"
                minLength="8"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
                required
              />
            </label>
            <button className="primary-button full-width" type="submit" disabled={submitting}>
              {submitting
                ? ui('Aktivierung wird geprüft...', 'Checking activation...')
                : ui('Business Hub sicher verbinden', 'Securely link Business Hub')}
            </button>
          </form>
        ) : null}
        {message ? (
          <button className="primary-button" type="button" onClick={() => navigate('/account/dashboard?tab=mailbox', { replace: true })}>
            {ui('Post im Business Hub öffnen', 'Open post in the Business Hub')}
          </button>
        ) : null}
        {!submitting && error ? (
          <Link className="primary-link-button" to="/account/login">{ui('Zum Login', 'Go to sign in')}</Link>
        ) : null}
      </div>
    </main>
  );
}

export function CustomerForgotPassword() {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submitRequest = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setMessage('');
    setError('');

    try {
      const response = await fetch(`${API_URL}/customer/password-reset/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Passwort-Reset konnte nicht angefordert werden.');
      setMessage(payload.message || 'Falls ein Business-Hub-Zugang existiert, wurde ein Link zum Zurücksetzen verschickt.');
    } catch (err) {
      setError(err.message || 'Passwort-Reset konnte nicht angefordert werden.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="page-shell">
      <div className="brand-header">
        <div>
          <p className="eyebrow">YourGermanAddress</p>
          <h1>{ui('Passwort vergessen', 'Forgot password')}</h1>
          <p className="subtitle">{ui('Wir senden Ihnen einen Link, mit dem Sie ein neues Passwort festlegen können.', 'We will send you a link to set a new password.')}</p>
        </div>
      </div>
      <div className="card">
        <form className="form-grid" onSubmit={submitRequest}>
          <label className="full-width">
            {ui('E-Mail-Adresse', 'Email address')}
            <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
          </label>
          {message ? <div className="info-box full-width">{message}</div> : null}
          {error ? <div className="hint-box full-width">{error}</div> : null}
          <button className="primary-button full-width" type="submit" disabled={submitting}>
            {submitting ? ui('Link wird versendet...', 'Sending link...') : ui('Neues Passwort anfordern', 'Request a new password')}
          </button>
          <p className="full-width">
            <Link to="/account/login">{ui('Zurück zum Login', 'Back to sign in')}</Link>
          </p>
        </form>
      </div>
    </main>
  );
}

export function CustomerResetPassword() {
  const { resetToken } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [passwordRepeat, setPasswordRepeat] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submitReset = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setMessage('');
    setError('');

    try {
      if (password !== passwordRepeat) {
        throw new Error('Die Passwörter stimmen nicht überein.');
      }

      const response = await fetch(`${API_URL}/customer/password-reset/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, password }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Passwort konnte nicht aktualisiert werden.');
      setMessage(payload.message || 'Passwort wurde aktualisiert.');
      setTimeout(() => navigate('/account/login', { replace: true }), 1500);
    } catch (err) {
      setError(err.message || 'Passwort konnte nicht aktualisiert werden.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="page-shell">
      <div className="brand-header">
        <div>
          <p className="eyebrow">YourGermanAddress</p>
          <h1>{ui('Neues Passwort', 'New password')}</h1>
          <p className="subtitle">{ui('Bitte vergeben Sie ein neues Passwort mit mindestens 8 Zeichen.', 'Please choose a new password with at least 8 characters.')}</p>
        </div>
      </div>
      <div className="card">
        <form className="form-grid" onSubmit={submitReset}>
          <label className="full-width">
            {ui('Neues Passwort', 'New password')}
            <input type="password" minLength="8" value={password} onChange={(event) => setPassword(event.target.value)} required />
          </label>
          <label className="full-width">
            {ui('Passwort wiederholen', 'Repeat password')}
            <input type="password" minLength="8" value={passwordRepeat} onChange={(event) => setPasswordRepeat(event.target.value)} required />
          </label>
          {message ? <div className="info-box full-width">{message}</div> : null}
          {error ? <div className="hint-box full-width">{error}</div> : null}
          <button className="primary-button full-width" type="submit" disabled={submitting}>
            {submitting ? ui('Passwort wird gespeichert...', 'Saving password...') : ui('Passwort speichern', 'Save password')}
          </button>
        </form>
      </div>
    </main>
  );
}

export function CustomerEmailChangeConfirm() {
  const { changeToken } = useParams();
  const [message, setMessage] = useState('');
  const [loginChanged, setLoginChanged] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const confirmChange = async () => {
    setSubmitting(true);
    setMessage('');
    setError('');
    try {
      const response = await fetch(`${API_URL}/customer/email-change/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: changeToken }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'E-Mail-Adresse konnte nicht geändert werden.');
      setMessage(payload.message || 'E-Mail-Adresse wurde geändert.');
      setLoginChanged(payload.login_changed === true);
      if (payload.login_changed === true) localStorage.removeItem('customerToken');
    } catch (err) {
      setError(err.message || 'E-Mail-Adresse konnte nicht geändert werden.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="page-shell">
      <div className="brand-header">
        <div>
          <p className="eyebrow">YourGermanAddress</p>
          <h1>{ui('Neue E-Mail-Adresse bestätigen', 'Confirm new email address')}</h1>
          <p className="subtitle">{ui('Die Änderung wird erst nach Ihrer ausdrücklichen Bestätigung durchgeführt.', 'The change is applied only after your explicit confirmation.')}</p>
        </div>
      </div>
      <div className="card">
        {message ? <div className="info-box">{message}</div> : null}
        {error ? <div className="hint-box">{error}</div> : null}
        {!message ? (
          <button className="primary-button" type="button" onClick={confirmChange} disabled={submitting}>
            {submitting ? ui('Wird bestätigt...', 'Confirming...') : ui('E-Mail-Änderung bestätigen', 'Confirm email change')}
          </button>
        ) : loginChanged ? (
          <Link className="primary-link-button" to="/account/login">{ui('Mit neuer E-Mail-Adresse einloggen', 'Sign in with the new email address')}</Link>
        ) : null}
      </div>
    </main>
  );
}

export function CustomerAccess() {
  const { verificationToken } = useParams();
  const navigate = useNavigate();
  const [access, setAccess] = useState(null);
  const [password, setPassword] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const loadAccess = async () => {
      try {
        const response = await fetch(`${API_URL}/customer/access/${encodeURIComponent(verificationToken)}`);
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.error || 'Zugang konnte nicht geladen werden.');
        setAccess(payload);
      } catch (err) {
        setError(err.message || 'Zugang konnte nicht geladen werden.');
      }
    };
    loadAccess();
  }, [verificationToken]);

  const submitRegister = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/customer/register-from-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ verification_token: verificationToken, password }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Der Business-Hub-Zugang konnte nicht erstellt werden.');
      saveCustomerToken(payload.token);
      navigate('/account/dashboard', { replace: true });
    } catch (err) {
      setError(err.message || 'Der Business-Hub-Zugang konnte nicht erstellt werden.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitLogin = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/customer/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: access.customer.email, password: loginPassword }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Login fehlgeschlagen.');
      saveCustomerToken(payload.token);
      navigate('/account/dashboard', { replace: true });
    } catch (err) {
      setError(err.message || 'Login fehlgeschlagen.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!access && !error) {
    return <main className="page-shell"><div className="card"><p>{ui('Lädt...', 'Loading...')}</p></div></main>;
  }

  return (
    <main className="page-shell">
      <div className="brand-header">
        <div>
          <p className="eyebrow">YourGermanAddress</p>
          <h1>{ui('Business Hub aktivieren', 'Activate Business Hub')}</h1>
          <p className="subtitle">{access.customer.email || ''}</p>
        </div>
      </div>
      <div className="card">
        {error ? <div className="hint-box">{error}</div> : null}
        {access.account_exists ? (
          <form className="form-grid" onSubmit={submitLogin}>
            <div className="info-box full-width">{ui('Ihr Business Hub ist bereits aktiviert. Melden Sie sich mit Ihrem Passwort an.', 'Your Business Hub is already active. Sign in with your password.')}</div>
            <label className="full-width">
              {ui('Passwort', 'Password')}
              <input type="password" value={loginPassword} onChange={(event) => setLoginPassword(event.target.value)} required />
            </label>
            <button className="primary-button full-width" type="submit" disabled={submitting}>
              {submitting ? ui('Login läuft...', 'Signing in...') : ui('Einloggen', 'Sign in')}
            </button>
            <p className="full-width">
              <Link to="/account/forgot-password">{ui('Passwort vergessen', 'Forgot password')}</Link>
            </p>
          </form>
        ) : (
          <form className="form-grid" onSubmit={submitRegister}>
            <div className="info-box full-width">{ui('Aktivieren Sie jetzt Ihren persönlichen Business Hub. Ihre vorhandene Verifizierung wird automatisch übernommen.', 'Activate your personal Business Hub now. Your existing verification will be linked automatically.')}</div>
            <label className="full-width">
              {ui('Sicheres Passwort festlegen', 'Choose a secure password')}
              <input type="password" minLength="8" value={password} onChange={(event) => setPassword(event.target.value)} required />
            </label>
            <button className="primary-button full-width" type="submit" disabled={submitting}>
              {submitting ? ui('Business Hub wird aktiviert...', 'Activating Business Hub...') : ui('Business Hub aktivieren', 'Activate Business Hub')}
            </button>
          </form>
        )}
      </div>
    </main>
  );
}

export function CustomerDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [dashboard, setDashboard] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => {
    const requestedTab = new URLSearchParams(window.location.search).get('tab');
    return ['overview', 'mailbox', 'documents', 'addresses', 'verifications', 'company', 'display', 'subscriptions', 'logistics', 'guides'].includes(requestedTab)
      ? requestedTab
      : 'overview';
  });
  const paymentRecoveryParams = new URLSearchParams(location.search);
  const paymentRecoveryRequested = paymentRecoveryParams.get('payment') === 'recovery';
  const paymentRecoverySubscriptionId = paymentRecoveryParams.get('subscription') || '';
  const [displayDraft, setDisplayDraft] = useState({
    display_name: '',
    display_note: '',
  });
  const [displayLogo, setDisplayLogo] = useState(null);
  const [submittingDisplay, setSubmittingDisplay] = useState(false);
  const [submittingCompany, setSubmittingCompany] = useState(false);
  const [companyWizardOpen, setCompanyWizardOpen] = useState(false);
  const [cancellingSubscriptionId, setCancellingSubscriptionId] = useState(null);
  const [withdrawingSubscriptionId, setWithdrawingSubscriptionId] = useState(null);
  const [openingPaymentMethodId, setOpeningPaymentMethodId] = useState(null);
  const [paymentMethodConfirmationId, setPaymentMethodConfirmationId] = useState(null);
  const [paymentTermsAcceptedId, setPaymentTermsAcceptedId] = useState(null);
  const [subscriptionChanges, setSubscriptionChanges] = useState([]);
  const [resumingChangeId, setResumingChangeId] = useState(null);
  const [checkoutRecovery, setCheckoutRecovery] = useState(null);
  const [planChangeDrafts, setPlanChangeDrafts] = useState({});
  const [planChooserSubscriptionId, setPlanChooserSubscriptionId] = useState(null);
  const [showPreviousSubscriptions, setShowPreviousSubscriptions] = useState(false);
  const [changingPlanId, setChangingPlanId] = useState(null);
  const [checkoutPreparation, setCheckoutPreparation] = useState(null);
  const [checkoutPreparationSeconds, setCheckoutPreparationSeconds] = useState(0);
  const [newBookingPlan, setNewBookingPlan] = useState('pro');
  const [newBookingLoading, setNewBookingLoading] = useState(false);
  const [profileLabelDraft, setProfileLabelDraft] = useState('');
  const [profileLabelSaving, setProfileLabelSaving] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newEmailConfirmation, setNewEmailConfirmation] = useState('');
  const [emailChangePassword, setEmailChangePassword] = useState('');
  const [emailChangeTargets, setEmailChangeTargets] = useState(['login', 'contact']);
  const [requestingEmailChange, setRequestingEmailChange] = useState(false);
  const [emailChangeMessage, setEmailChangeMessage] = useState('');
  const [notificationPreferences, setNotificationPreferences] = useState(null);
  const [notificationPreferencesLoading, setNotificationPreferencesLoading] = useState(false);
  const [notificationPreferencesSaving, setNotificationPreferencesSaving] = useState(false);
  const [notificationPreferencesMessage, setNotificationPreferencesMessage] = useState('');
  const [billingAddressDraft, setBillingAddressDraft] = useState({
    name: '', company: '', street: '', address_extra: '', postal_code: '', city: '', state: '', country: 'DE',
  });
  const [billingAddressSaving, setBillingAddressSaving] = useState(false);
  const [billingAddressMessage, setBillingAddressMessage] = useState('');
  const [billingSubscriptionId, setBillingSubscriptionId] = useState('');
  const [billingRecipientConfirmed, setBillingRecipientConfirmed] = useState(false);
  const [selectedMailId, setSelectedMailId] = useState('');
  const [selectedMailIds, setSelectedMailIds] = useState([]);
  const [mailActionWorking, setMailActionWorking] = useState(false);
  const [mailMessage, setMailMessage] = useState('');
  const [mailMessageIsOrder, setMailMessageIsOrder] = useState(false);
  const [mailCheckoutUrl, setMailCheckoutUrl] = useState('');
  const [creatingMailPaymentId, setCreatingMailPaymentId] = useState(null);
  const [cancellingMailRequestId, setCancellingMailRequestId] = useState(null);
  const [creatingLogisticsPaymentId, setCreatingLogisticsPaymentId] = useState(null);
  const [mailForwardingFormOpen, setMailForwardingFormOpen] = useState(false);
  const [mailForwardingAddress, setMailForwardingAddress] = useState(EMPTY_MAIL_FORWARDING_ADDRESS);
  const [saveMailForwardingAddress, setSaveMailForwardingAddress] = useState(false);
  const [mailForwardingAddressLabel, setMailForwardingAddressLabel] = useState('');
  const [addresses, setAddresses] = useState([]);
  const [addressDraft, setAddressDraft] = useState(EMPTY_ADDRESS_BOOK_ENTRY);
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [addressWorking, setAddressWorking] = useState(false);
  const [addressMessage, setAddressMessage] = useState('');
  const [nextcloudConnectingId, setNextcloudConnectingId] = useState(null);
  const [parcelCheckoutWeight, setParcelCheckoutWeight] = useState('');
  const [selectedParcelWeight, setSelectedParcelWeight] = useState('');
  const [parcelRequestKeys, setParcelRequestKeys] = useState({});
  const [parcelCheckoutFormOpen, setParcelCheckoutFormOpen] = useState(false);
  const [parcelForwardingAddress, setParcelForwardingAddress] = useState(EMPTY_MAIL_FORWARDING_ADDRESS);
  const [saveParcelForwardingAddress, setSaveParcelForwardingAddress] = useState(false);
  const [parcelForwardingAddressLabel, setParcelForwardingAddressLabel] = useState('');
  const [sevdeskInvoices, setSevdeskInvoices] = useState([]);
  const [sevdeskInvoicesEnabled, setSevdeskInvoicesEnabled] = useState(null);
  const [sevdeskInvoicesLoading, setSevdeskInvoicesLoading] = useState(false);
  const [sevdeskInvoicesError, setSevdeskInvoicesError] = useState('');
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState('');
  const [customerDocuments, setCustomerDocuments] = useState([]);
  const [customerDocumentsLoading, setCustomerDocumentsLoading] = useState(false);
  const [customerDocumentsError, setCustomerDocumentsError] = useState('');
  const [downloadingDocumentId, setDownloadingDocumentId] = useState('');
  const [customerDocumentsLoaded, setCustomerDocumentsLoaded] = useState(false);
  const [copiedBusinessAddressId, setCopiedBusinessAddressId] = useState('');
  const [recipientServices, setRecipientServices] = useState([]);
  const [recipientEligibility, setRecipientEligibility] = useState(null);
  const [recipientDefaults, setRecipientDefaults] = useState(null);
  const [selectedLegalEntityId, setSelectedLegalEntityId] = useState('');
  const [agreementDisplayDecision, setAgreementDisplayDecision] = useState('');
  const [agreementGenerating, setAgreementGenerating] = useState(false);
  const [agreementMessage, setAgreementMessage] = useState('');
  const [legalEntityFormOpen, setLegalEntityFormOpen] = useState(false);
  const [legalEntitySaving, setLegalEntitySaving] = useState(false);
  const legalEntityRequest = useRef(null);
  const legalEntityInFlight = useRef(false);
  const [legalEntityDraft, setLegalEntityDraft] = useState({
    entity_type: 'company', legal_name: '', legal_form: 'GmbH', registered_office: '', register_court: '', registration_number: '', country_code: 'DE',
  });

  const [detailsLoading, setDetailsLoading] = useState(false);
  const detailsRequest = useRef(null);
  const detailsRetries = useRef(0);
  const reloadDashboard = useCallback(async () => {
    detailsRequest.current?.abort();
    const controller = new AbortController();
    detailsRequest.current = controller;
    setDetailsLoading(true);
    const timer = setTimeout(() => controller.abort(), 20_000);
    try {
      const response = await fetch(`${API_URL}/customer/dashboard/details`, {
        headers: { Authorization: `Bearer ${readCustomerToken()}` }, signal: controller.signal,
      });
      if (response.status === 401) {
        localStorage.removeItem('customerToken');
        navigate('/account/login', { replace: true });
        return;
      }
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload?.account?.email) throw new Error('Dashboard details unavailable');
      if (detailsRequest.current === controller) setDashboard(payload);
    } catch (error) {
      if (detailsRequest.current === controller) setDashboard((current) => current ? {
        ...current, subscriptions_loading: false, subscription_status: 'unavailable',
        subscription_error: 'Abonnementstatus vorübergehend nicht verfügbar. / Subscription status temporarily unavailable.',
      } : current);
    } finally {
      clearTimeout(timer);
      if (detailsRequest.current === controller) setDetailsLoading(false);
    }
  }, [navigate]);

  useEffect(() => () => {
    const pending = detailsRequest.current;
    detailsRequest.current = null;
    pending?.abort();
  }, []);

  useEffect(() => {
    if (!checkoutPreparation?.startedAt) {
      setCheckoutPreparationSeconds(0);
      return undefined;
    }
    const refresh = () => setCheckoutPreparationSeconds(Math.max(0, Math.floor((Date.now() - checkoutPreparation.startedAt) / 1000)));
    refresh();
    const timer = window.setInterval(refresh, 250);
    return () => window.clearInterval(timer);
  }, [checkoutPreparation]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('plan_change') !== 'payment_confirmed') return;
    try {
      const timing = JSON.parse(window.sessionStorage.getItem(CHECKOUT_TIMING_KEY) || '{}');
      if (!timing?.startedAt || timing?.returnedAt) return;
      const navigationEntry = window.performance?.getEntriesByType?.('navigation')?.[0];
      const returnedAt = Date.now();
      const completed = {
        ...timing,
        returnedAt,
        totalRoundTripMs: returnedAt - timing.startedAt,
        dashboardDocumentLoadMs: Math.round(Number(navigationEntry?.duration || 0)),
      };
      window.sessionStorage.setItem(CHECKOUT_TIMING_KEY, JSON.stringify(completed));
      console.info('[subscription-change] browser timing', completed);
    } catch {}
  }, [location.search]);

  useEffect(() => {
    if (!dashboard?.subscription_error) {
      detailsRetries.current = 0;
      return undefined;
    }
    if (detailsRetries.current >= 2) return undefined;
    const timer = setTimeout(() => {
      detailsRetries.current += 1;
      reloadDashboard();
    }, 3_000 * (detailsRetries.current + 1));
    return () => clearTimeout(timer);
  }, [dashboard, reloadDashboard]);

  useEffect(() => {
    const token = readCustomerToken();
    if (!token) {
      navigate(new URLSearchParams(location.search).get('tab') === 'mailbox' ? '/account/login?next=mailbox' : '/account/login', { replace: true });
      return;
    }

    const loadDashboard = async () => {
      try {
        const response = await fetch(`${API_URL}/customer/dashboard`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const payload = await response.json().catch(() => ({}));
        if (response.status === 401) {
          localStorage.removeItem('customerToken');
          navigate(new URLSearchParams(location.search).get('tab') === 'mailbox' ? '/account/login?next=mailbox' : '/account/login', { replace: true });
          return;
        }
        if (!response.ok) throw new Error(payload.error || 'Kundenbereich konnte nicht geladen werden.');
        if (!payload?.account?.email) throw new Error('Kundenkonto konnte nicht vollständig geladen werden.');
        setDashboard(payload);
        reloadDashboard();
        setSelectedLegalEntityId((current) => current || payload.legal_entities?.find(entity => entity.is_default)?.id || (payload.legal_entities?.length === 1 ? payload.legal_entities[0].id : ''));
        const addressResponse = await fetch(`${API_URL}/customer/addresses`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const addressPayload = await addressResponse.json().catch(() => []);
        if (addressResponse.ok) setAddresses(Array.isArray(addressPayload) ? addressPayload : []);
        const preferenceResponse = await fetch(`${API_URL}/customer/notification-preferences`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const preferencePayload = await preferenceResponse.json().catch(() => ({}));
        if (preferenceResponse.ok) setNotificationPreferences(preferencePayload);
        const firstDisplayProfile = (payload.verifications || []).find((item) => item.display_profile)?.display_profile;
        setDisplayDraft({
          display_name: firstDisplayProfile?.display_name || payload.verifications?.[0]?.customers?.company_name || payload.verifications?.[0]?.customers?.name || '',
          display_note: firstDisplayProfile?.display_note || '',
        });
      } catch (err) {
        setError(err.message || 'Kundenbereich konnte nicht geladen werden.');
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, [navigate, reloadDashboard]);

  useEffect(() => {
    if (activeTab !== 'company' || notificationPreferences || notificationPreferencesLoading) return;
    const loadPreferences = async () => {
      setNotificationPreferencesLoading(true);
      try {
        const response = await fetch(`${API_URL}/customer/notification-preferences`, {
          headers: { Authorization: `Bearer ${readCustomerToken()}` },
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.error || ui('Einstellungen konnten nicht geladen werden.', 'Settings could not be loaded.'));
        setNotificationPreferences(payload);
      } catch (error) {
        setNotificationPreferencesMessage(error.message);
      } finally {
        setNotificationPreferencesLoading(false);
      }
    };
    loadPreferences();
  }, [activeTab, notificationPreferences, notificationPreferencesLoading]);

  useEffect(() => {
    const requestedTab = new URLSearchParams(location.search).get('tab');
    if (['overview', 'mailbox', 'documents', 'addresses', 'verifications', 'company', 'display', 'subscriptions', 'logistics', 'guides'].includes(requestedTab)) {
      setActiveTab(requestedTab);
    }
  }, [location.search]);

  useEffect(() => {
    if (!['overview', 'documents'].includes(activeTab) || customerDocumentsLoading || customerDocumentsLoaded) return;
    const loadDocuments = async () => {
      setCustomerDocumentsLoading(true);
      setCustomerDocumentsError('');
      try {
        const response = await fetch(`${API_URL}/customer/documents`, {
          headers: { Authorization: `Bearer ${readCustomerToken()}` },
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.error || ui('Dokumente konnten nicht geladen werden.', 'Documents could not be loaded.'));
        setCustomerDocuments(Array.isArray(payload.documents) ? payload.documents : []);
      } catch (err) {
        setCustomerDocumentsError(err.message || ui('Dokumente konnten nicht geladen werden.', 'Documents could not be loaded.'));
      } finally {
        setCustomerDocumentsLoaded(true);
        setCustomerDocumentsLoading(false);
      }
    };
    loadDocuments();
  }, [activeTab, customerDocumentsLoaded, customerDocumentsLoading]);

  useEffect(() => {
    if (activeTab !== 'documents') return;
    const loadRecipientServices = async () => {
      try {
        const entityQuery = selectedLegalEntityId ? `?legal_entity_id=${encodeURIComponent(selectedLegalEntityId)}` : '';
        const recipientResponse = await fetch(`${API_URL}/customer/authorized-recipient-services${entityQuery}`, {
          headers: { Authorization: `Bearer ${readCustomerToken()}` },
        });
        const recipientPayload = await recipientResponse.json().catch(() => ({}));
        if (!recipientResponse.ok) throw new Error(recipientPayload.error || ui('Empfangsservices konnten nicht geladen werden.', 'Recipient services could not be loaded.'));
        setRecipientServices(Array.isArray(recipientPayload.services) ? recipientPayload.services : []);
        setRecipientEligibility(recipientPayload.eligibility || null);
        setRecipientDefaults(recipientPayload.defaults || null);
      } catch (err) {
        setCustomerDocumentsError(err.message || ui('Dokumente konnten nicht geladen werden.', 'Documents could not be loaded.'));
      }
    };
    loadRecipientServices();
  }, [activeTab, selectedLegalEntityId]);

  const copyBusinessAddress = async (address) => {
    const addressText = formatBusinessAddress(address);
    if (!addressText) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(addressText);
      } else {
        const textarea = window.document.createElement('textarea');
        textarea.value = addressText;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        window.document.body.appendChild(textarea);
        textarea.select();
        window.document.execCommand('copy');
        textarea.remove();
      }
      setCopiedBusinessAddressId(String(address.customer_id));
      window.setTimeout(() => {
        setCopiedBusinessAddressId((current) => (current === String(address.customer_id) ? '' : current));
      }, 1800);
    } catch (err) {
      setError(ui('Die Adresse konnte nicht kopiert werden. Bitte versuchen Sie es erneut.', 'The address could not be copied. Please try again.'));
    }
  };

  const downloadCustomerDocument = async (document) => {
    setDownloadingDocumentId(document.id);
    setCustomerDocumentsError('');
    try {
      const response = await fetch(buildApiAssetUrl(document.download_url), {
        headers: { Authorization: `Bearer ${readCustomerToken()}` },
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || ui('Dokument konnte nicht geladen werden.', 'Document could not be downloaded.'));
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = window.document.createElement('a');
      anchor.href = url;
      anchor.download = document.filename;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setCustomerDocumentsError(err.message || ui('Dokument konnte nicht geladen werden.', 'Document could not be downloaded.'));
    } finally {
      setDownloadingDocumentId('');
    }
  };

  const downloadRecipientDocument = async (service, type) => {
    const key = `${service.id}:${type}`;
    setDownloadingDocumentId(key);
    setCustomerDocumentsError('');
    try {
      const response = await fetch(`${API_URL}/customer/authorized-recipient-services/${encodeURIComponent(service.id)}/documents/${encodeURIComponent(type)}`, {
        headers: { Authorization: `Bearer ${readCustomerToken()}` },
      });
      if (!response.ok) { const payload = await response.json().catch(() => ({})); throw new Error(payload.error || ui('Dokument konnte nicht geladen werden.', 'Document could not be downloaded.')); }
      const blob = await response.blob(); const url = URL.createObjectURL(blob); const anchor = window.document.createElement('a');
      const contentDisposition = response.headers.get('content-disposition') || '';
      const serverFilename = /filename="([^"]+)"/i.exec(contentDisposition)?.[1];
      anchor.href = url; anchor.download = serverFilename || `${type}-${service.document_code}.pdf`; anchor.click(); URL.revokeObjectURL(url);
      if (type === 'authorization' && service.status === 'draft_created') setRecipientServices((items) => items.map((item) => item.id === service.id ? { ...item, status: 'downloaded' } : item));
    } catch (err) { setCustomerDocumentsError(err.message || ui('Dokument konnte nicht geladen werden.', 'Document could not be downloaded.')); }
    finally { setDownloadingDocumentId(''); }
  };

  const updateRecipientStatus = async (service, status) => {
    setCustomerDocumentsError('');
    try {
      const response = await fetch(`${API_URL}/customer/authorized-recipient-services/${encodeURIComponent(service.id)}/status`, {
        method: 'POST', headers: { Authorization: `Bearer ${readCustomerToken()}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ status }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || ui('Status konnte nicht aktualisiert werden.', 'Status could not be updated.'));
      setRecipientServices((items) => items.map((item) => item.id === service.id ? payload.service : item));
    } catch (err) { setCustomerDocumentsError(err.message || ui('Status konnte nicht aktualisiert werden.', 'Status could not be updated.')); }
  };

  useEffect(() => {
    if (activeTab !== 'subscriptions' || sevdeskInvoicesEnabled !== null || sevdeskInvoicesLoading) return;
    const loadInvoices = async () => {
      setSevdeskInvoicesLoading(true);
      setSevdeskInvoicesError('');
      try {
        const token = readCustomerToken();
        const response = await fetch(`${API_URL}/customer/invoices`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload.error || ui('Rechnungen konnten nicht geladen werden.', 'Invoices could not be loaded.'));
        }
        setSevdeskInvoicesEnabled(Boolean(payload.enabled));
        setSevdeskInvoices(Array.isArray(payload.invoices) ? payload.invoices : []);
      } catch (err) {
        setSevdeskInvoicesEnabled(true);
        setSevdeskInvoicesError(err.message || ui('Rechnungen konnten nicht geladen werden.', 'Invoices could not be loaded.'));
      } finally {
        setSevdeskInvoicesLoading(false);
      }
    };
    loadInvoices();
  }, [activeTab, sevdeskInvoicesEnabled, sevdeskInvoicesLoading]);

  useEffect(() => {
    if (activeTab !== 'subscriptions') return;
    const loadSubscriptionChanges = async () => {
      try {
        const response = await fetch(`${API_URL}/customer/subscription-changes`, {
          headers: { Authorization: `Bearer ${readCustomerToken()}` },
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.error || ui('Tarifwechsel konnten nicht geladen werden.', 'Plan changes could not be loaded.'));
        setSubscriptionChanges(Array.isArray(payload.changes) ? payload.changes : []);
      } catch (err) {
        setError(err.message || ui('Tarifwechsel konnten nicht geladen werden.', 'Plan changes could not be loaded.'));
      }
    };
    loadSubscriptionChanges();
  }, [activeTab]);

  const logout = () => {
    localStorage.removeItem('customerToken');
    navigate('/account/login', { replace: true });
  };

  const createLegalEntity = async (event) => {
    event.preventDefault();
    if (legalEntityInFlight.current) return;
    legalEntityInFlight.current = true;
    const draftKey = JSON.stringify(legalEntityDraft);
    if (legalEntityRequest.current?.draftKey !== draftKey) {
      legalEntityRequest.current = { draftKey, id: crypto.randomUUID() };
    }
    setLegalEntitySaving(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/customer/legal-entities`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${readCustomerToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...legalEntityDraft, creation_request_id: legalEntityRequest.current.id }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || ui('Unternehmensprofil konnte nicht angelegt werden.', 'Company profile could not be created.'));
      // The transaction may also have scoped the legacy profile. Never render
      // its stale unscoped data as data belonging to the newly selected company.
      setDashboard((current) => ({ ...current, legal_entities: [...(current.legal_entities || []), payload.entity],
        verifications: [], subscriptions: [], business_addresses: [], nextcloud_connections: [], portal_requests: [],
        mailbox: { eligible: false, items: [] } }));
      setCustomerDocuments([]);
      setCustomerDocumentsLoaded(false);
      setSelectedLegalEntityId(payload.entity.id);
      await reloadDashboard();
      legalEntityRequest.current = null;
      setLegalEntityFormOpen(false);
      setLegalEntityDraft({ entity_type: 'company', legal_name: '', legal_form: 'GmbH', registered_office: '', register_court: '', registration_number: '', country_code: 'DE' });
    } catch (legalEntityError) {
      setError(legalEntityError.message);
    } finally {
      legalEntityInFlight.current = false;
      setLegalEntitySaving(false);
    }
  };

  const legalEntities = dashboard?.legal_entities || [];
  const selectedLegalEntity = selectLegalEntity(legalEntities, selectedLegalEntityId);
  const newEntityIsRegisteredCompany = legalEntityDraft.entity_type === 'company';
  const newEntityNeedsLegalName = ['company', 'sole_proprietor'].includes(legalEntityDraft.entity_type);
  const inSelectedEntity = (item) => belongsToLegalEntity(item, selectedLegalEntityId, legalEntities.length);
  const activeBillingAddress = selectBillingAddress(dashboard, selectedLegalEntityId, billingSubscriptionId);
  useEffect(() => {
    setProfileLabelDraft(selectedLegalEntity?.profile_label || '');
  }, [selectedLegalEntity?.id, selectedLegalEntity?.profile_label]);
  useEffect(() => {
    setBillingAddressDraft({ name: '', company: '', street: '', address_extra: '', postal_code: '',
      city: '', state: '', country: 'DE', ...(activeBillingAddress?.address || {}) });
    // Refresh for a new request or company, not on background dashboard polling.
  }, [selectedLegalEntityId, activeBillingAddress?.request_id]);
  useEffect(() => { setBillingAddressMessage(''); }, [selectedLegalEntityId]);
  useEffect(() => { setAgreementDisplayDecision(''); setAgreementMessage(''); }, [selectedLegalEntityId]);
  const portalRequests = (dashboard?.portal_requests || []).filter(inSelectedEntity);
  const mailForwardingRequests = portalRequests.filter((request) => request.request_type === 'mail_forwarding');
  const pendingRequests = portalRequests.filter((request) => request.status === 'pending');
  const pendingWithdrawalSubscriptionIds = new Set(
    pendingRequests
      .filter((request) => request.request_type === 'subscription_withdrawal')
      .map((request) => String(request.requested_payload?.subscription_id || ''))
      .filter(Boolean)
  );

  const allVerifications = dashboard?.verifications || [];
  const verifications = allVerifications.filter(inSelectedEntity);
  const logisticsRequests = useMemo(() => dashboard?.logistics_requests || [], [dashboard]);
  const additionalServicesAttentionCount = [...mailForwardingRequests, ...logisticsRequests]
    .reduce((total, request) => total + requestPaymentAttentionCount(request), 0);
  const allSubscriptions = dashboard?.subscriptions || [];
  const subscriptions = allSubscriptions.filter(inSelectedEntity);
  const activeSubscriptions = subscriptions.filter((subscription) => getSubscriptionPaymentAction(subscription) !== 'ended');
  const previousSubscriptions = subscriptions.filter((subscription) => getSubscriptionPaymentAction(subscription) === 'ended');
  const displayedSubscriptions = [...activeSubscriptions, ...previousSubscriptions];
  const detachedSubscriptionChanges = getDetachedOpenSubscriptionChanges(subscriptionChanges, subscriptions);
  const stripeBillingSubscriptions = subscriptions.filter((subscription) => subscription.billing_provider === 'stripe'
    && ['active', 'trialing'].includes(String(subscription.status || subscription.provider_status || '').toLowerCase()));
  useEffect(() => {
    setBillingSubscriptionId((current) => stripeBillingSubscriptions.some((subscription) => subscription.stripe_subscription_id === current)
      ? current : stripeBillingSubscriptions.length === 1 ? stripeBillingSubscriptions[0].stripe_subscription_id : '');
    setBillingRecipientConfirmed(false);
  }, [selectedLegalEntityId, stripeBillingSubscriptions.map((subscription) => subscription.stripe_subscription_id).join('|')]);
  useEffect(() => {
    setShowPreviousSubscriptions(false);
  }, [selectedLegalEntityId]);
  const mailItems = useMemo(
    () => (dashboard?.mailbox?.items || []).filter((item) => belongsToLegalEntity(item, selectedLegalEntityId, legalEntities.length)),
    [dashboard, selectedLegalEntityId, legalEntities.length]
  );
  const nextcloudConnections = (dashboard?.nextcloud_connections || []).filter(inSelectedEntity);
  const scopedCustomerDocuments = customerDocuments.filter(inSelectedEntity);
  const selectedMail = mailItems.find((item) => item.id === selectedMailId) || mailItems[0] || null;
  const primaryDisplayProfile = useMemo(() => {
    return verifications.find((verification) => verification?.display_profile)?.display_profile || null;
  }, [verifications]);
  const hasCompletedVerification = useMemo(() => verifications.some((verification) => {
    return hasBusinessHubFeatureAccess({
      identityStatus: verification?.identity_status,
      businessStatus: verification?.business_status,
      customerType: verification?.customers?.customer_type,
    });
  }), [verifications]);
  // Identity belongs to the account holder.  Company checks remain scoped to
  // each selected profile, but a confirmed person may manage and book another
  // profile without waiting for the first company's registry review.
  const hasApprovedAccountIdentity = dashboard?.account?.identity_verified === true;
  const hasMailAccess = verifications.some(hasMailboxIdentityAccess);
  const primaryVerification = verifications[0] || null;
  const isPlannedFounding = normalizeCustomerType(
    (primaryVerification?.legal_entity_id && primaryVerification?.detected_agreement_type) || primaryVerification?.customers?.customer_type
  ) === 'gruendung';
  const hasUnscopedFounding = (dashboard?.verifications || []).some((verification) =>
    !verification.legal_entity_id && normalizeCustomerType(verification.customers?.customer_type) === 'gruendung'
  );
  const activeCompanyData = companyDraftFromVerification(primaryVerification);
  const account = dashboard?.account || null;
  const nextcloudEmailTargetAvailable = Boolean(account?.email_addresses?.available_targets?.nextcloud);
  const accountEmailGroups = [
    ['login', ui('Anmeldung', 'Sign-in'), true],
    ['contact', ui('Kontakt', 'Contact'), true],
    ['billing', ui('Rechnungen', 'Billing'), true],
    ['stripe', ui('Zahlungskonto', 'Payment account'), account?.email_addresses?.available_targets?.stripe !== false],
    ['shopify_seal', ui('Bestehende Abonnements', 'Existing subscriptions'), Boolean(account?.email_addresses?.available_targets?.shopify_seal)],
    ['nextcloud', ui('Dokumentenportal', 'Document portal'), nextcloudEmailTargetAvailable],
  ].filter(([, , available]) => available).reduce((groups, [key, label]) => {
    const email = account?.email_addresses?.[key] || account?.email;
    if (!email) return groups;
    const existing = groups.find((group) => group.email === email);
    if (existing) existing.labels.push(label);
    else groups.push({ email, labels: [label] });
    return groups;
  }, []);
  const canCompleteFounding = isPlannedFounding && ['approved', 'already_verified', 'freigegeben'].includes(
    normalizeText(primaryVerification?.identity_status).toLowerCase()
  );
  const dashboardNotices = useMemo(() => buildCustomerDashboardNotices(dashboard || {}), [dashboard]);
  const dashboardNoticeCopy = {
    open_payments: (count) => ui(
      `${countText(count, 'Zahlung benötigt', 'Zahlungen benötigen', 'payment requires', 'payments require')} Ihre Aufmerksamkeit.`,
      `${countText(count, 'payment requires', 'payments require', 'payment requires', 'payments require')} your attention.`
    ),
    verification_action: (count) => ui(
      `${countText(count, 'Verifizierung benötigt', 'Verifizierungen benötigen', 'verification requires', 'verifications require')} noch Angaben oder Unterlagen von Ihnen.`,
      `${countText(count, 'verification requires', 'verifications require', 'verification requires', 'verifications require')} information or documents from you.`
    ),
    new_mail: (count) => ui(
      `${countText(count, 'neues Poststück liegt', 'neue Poststücke liegen', 'new mail item is', 'new mail items are')} sicher für Sie bereit.`,
      `${countText(count, 'new mail item is', 'new mail items are', 'new mail item is', 'new mail items are')} ready for you securely.`
    ),
  };
  const dashboardNoticeActions = {
    subscriptions: ui('Zahlung prüfen', 'Review payment'),
    logistics: ui('Offene Zahlung öffnen', 'Open pending payment'),
    verifications: ui('Verifizierung ergänzen', 'Complete verification'),
    mailbox: ui('Posteingang öffnen', 'Open inbox'),
  };

  useEffect(() => {
    if (!loading && !hasCompletedVerification && !(hasMailAccess && ['mailbox', 'documents'].includes(activeTab)) && !(hasApprovedAccountIdentity && ['company', 'logistics'].includes(activeTab)) && !['overview', 'verifications', 'guides', 'subscriptions'].includes(activeTab)) {
      setActiveTab('verifications');
    }
  }, [activeTab, hasApprovedAccountIdentity, hasCompletedVerification, hasMailAccess, loading]);

  const hasPendingCompanyRequest = pendingRequests.some((request) => ['company_data', 'founding_completion'].includes(request.request_type));
  const submittedFoundingRequest = (dashboard?.portal_requests || []).some(
    (request) => request.request_type === 'founding_completion' && ['pending', 'approved'].includes(request.status)
  );

  const downloadSevdeskInvoice = async (invoice) => {
    if (!invoice?.id || downloadingInvoiceId) return;
    setDownloadingInvoiceId(invoice.id);
    setSevdeskInvoicesError('');
    try {
      const token = readCustomerToken();
      const response = await fetch(`${API_URL}/customer/invoices/${encodeURIComponent(invoice.id)}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || ui('Rechnung konnte nicht heruntergeladen werden.', 'Invoice could not be downloaded.'));
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `${invoice.invoice_number || `Rechnung-${invoice.id}`}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      setSevdeskInvoicesError(err.message || ui('Rechnung konnte nicht heruntergeladen werden.', 'Invoice could not be downloaded.'));
    } finally {
      setDownloadingInvoiceId('');
    }
  };

  const startParcelForwardingCheckout = async (packageWeightClass, shippingAddress) => {
    setParcelCheckoutWeight(packageWeightClass);
    setError('');
    try {
      const requestKey = parcelRequestKeys[packageWeightClass] || window.crypto.randomUUID();
      if (!parcelRequestKeys[packageWeightClass]) {
        setParcelRequestKeys((current) => ({ ...current, [packageWeightClass]: requestKey }));
      }
      const response = await fetch(`${API_URL}/customer/logistics/parcel-forwarding/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${readCustomerToken()}`,
        },
        body: JSON.stringify({
          package_weight_class: packageWeightClass,
          request_key: requestKey,
          shipping_address: shippingAddress,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || ui('Zahlungslink konnte nicht erstellt werden.', 'Could not create payment link.'));
      if (!payload.checkout_url) throw new Error(ui('Das Zahlungssystem hat keinen Zahlungslink zurückgegeben.', 'The payment system did not return a payment link.'));
      window.location.assign(payload.checkout_url);
    } catch (checkoutError) {
      setError(checkoutError.message || ui('Paketweiterleitung konnte nicht gestartet werden.', 'Could not start parcel forwarding.'));
      setParcelCheckoutWeight('');
    }
  };

  const connectNextcloud = async (connection) => {
    const customerId = connection.customer_id;
    const connectionKey = `${customerId}:${connection.legal_entity_id || 'legacy'}`;
    const loginWindow = window.open('about:blank', '_blank');
    setNextcloudConnectingId(connectionKey);
    setError('');
    try {
      const response = await fetch(`${API_URL}/customer/nextcloud/${encodeURIComponent(customerId)}/connect/start`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${readCustomerToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ legal_entity_id: connection.legal_entity_id || null }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || ui('Nextcloud-Anmeldung konnte nicht gestartet werden.', 'Could not start Nextcloud sign-in.'));
      const loginUrl = new URL(payload.login_url, window.location.origin).toString();
      if (loginWindow) loginWindow.location = loginUrl;
      else window.location.assign(loginUrl);
      const deadline = Date.now() + 10 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((resolve) => window.setTimeout(resolve, 1500));
        const pollResponse = await fetch(`${API_URL}/customer/nextcloud/${encodeURIComponent(customerId)}/connect/poll`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${readCustomerToken()}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ legal_entity_id: connection.legal_entity_id || null }),
        });
        const pollPayload = await pollResponse.json().catch(() => ({}));
        if (!pollResponse.ok) throw new Error(pollPayload.error || ui('Nextcloud-Anmeldung konnte nicht abgeschlossen werden.', 'Could not complete Nextcloud sign-in.'));
        if (pollPayload.connected) {
          await reloadDashboard();
          setMailMessage(ui('Nextcloud-Konto wurde sicher verbunden.', 'Nextcloud account connected securely.'));
          setMailMessageIsOrder(false);
          return;
        }
      }
      throw new Error(ui('Die Nextcloud-Anmeldung ist abgelaufen.', 'The Nextcloud sign-in expired.'));
    } catch (connectionError) {
      if (loginWindow && !loginWindow.closed) loginWindow.close();
      setError(connectionError.message || ui('Nextcloud-Verbindung fehlgeschlagen.', 'Nextcloud connection failed.'));
    } finally {
      setNextcloudConnectingId(null);
    }
  };

  const disconnectNextcloud = async (connection) => {
    const customerId = connection.customer_id;
    const connectionKey = `${customerId}:${connection.legal_entity_id || 'legacy'}`;
    if (!window.confirm(ui('Nextcloud-Verbindung wirklich trennen? Der Posteingang wird anschließend in der App gesperrt.', 'Disconnect Nextcloud? The inbox will then be locked in the app.'))) return;
    setNextcloudConnectingId(connectionKey);
    setError('');
    try {
      const response = await fetch(`${API_URL}/customer/nextcloud/${encodeURIComponent(customerId)}/disconnect`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${readCustomerToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ legal_entity_id: connection.legal_entity_id || null }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || ui('Nextcloud-Verbindung konnte nicht getrennt werden.', 'Could not disconnect Nextcloud.'));
      await reloadDashboard();
      setMailMessage(ui('Nextcloud-Verbindung wurde getrennt.', 'Nextcloud connection disconnected.'));
      setMailMessageIsOrder(false);
    } catch (connectionError) {
      setError(connectionError.message || ui('Nextcloud-Verbindung konnte nicht getrennt werden.', 'Could not disconnect Nextcloud.'));
    } finally {
      setNextcloudConnectingId(null);
    }
  };

  const submitMailAction = async (action, shippingAddress = null) => {
    const selectedItems = mailItems.filter((item) => selectedMailIds.includes(item.id));
    if (!selectedItems.length) return;
    if (action === 'defer') {
      setSelectedMailIds([]);
      setMailMessage(ui('Die ausgewählten Poststücke bleiben in Ihrem Posteingang.', 'The selected mail items will remain in your inbox.'));
      setMailMessageIsOrder(false);
      return;
    }
    const pricing = dashboard?.mailbox?.forwarding_pricing || {};
    const serviceFee = (mailForwardingFeeCents(pricing, selectedItems.length) / 100).toFixed(2);
    const confirmations = {
      destroy_original: ui(`Möchten Sie ${countText(selectedItems.length, 'physisches Original', 'physische Originale', 'physical original', 'physical originals')} verbindlich vernichten lassen?`, `Would you like us to destroy ${countText(selectedItems.length, 'physisches Original', 'physische Originale', 'physical original', 'physical originals')}?`),
      physical_forwarding: ui(`Möchten Sie den Versand für ${countText(selectedItems.length, 'Poststück', 'Poststücke', 'mail item', 'mail items')} beauftragen? Die Servicegebühr beträgt ${serviceFee} EUR zuzüglich der tatsächlichen Versandkosten.`, `Would you like to request forwarding for ${countText(selectedItems.length, 'Poststück', 'Poststücke', 'mail item', 'mail items')}? The service fee is ${serviceFee} EUR plus actual postage.`),
    };
    if (confirmations[action] && !window.confirm(confirmations[action])) return;
    setMailActionWorking(true);
    setMailMessage('');
    setMailCheckoutUrl('');
    setError('');
    try {
      const response = await fetch(
        `${API_URL}/customer/mailbox/${encodeURIComponent(selectedItems[0].customer_id)}/actions`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${readCustomerToken()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action,
            file_ids: selectedItems.map((item) => item.id),
            legal_entity_id: selectedItems[0].legal_entity_id || null,
            shipping_address: action === 'physical_forwarding' ? shippingAddress : undefined,
          }),
        }
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || ui('Postaktion fehlgeschlagen.', 'Mail action failed.'));
      setMailMessage(payload.message || ui('Postaktion wurde gespeichert.', 'Mail action saved.'));
      setMailMessageIsOrder(true);
      setMailCheckoutUrl(payload.checkout_url || '');
      setSelectedMailId('');
      setSelectedMailIds([]);
      setMailForwardingFormOpen(false);
      await reloadDashboard();
    } catch (mailError) {
      setError(mailError.message || ui('Postaktion fehlgeschlagen.', 'Mail action failed.'));
    } finally {
      setMailActionWorking(false);
    }
  };

  const openMailForwardingForm = () => {
    const selectedItems = mailItems.filter((item) => selectedMailIds.includes(item.id));
    if (!selectedItems.length) return;
    const selectedCustomer = (dashboard?.customers || []).find(
      (customer) => customer.id === selectedItems[0].customer_id
    ) || {};
    const defaultAddress = addresses.find((address) => address.is_default);
    setMailForwardingAddress((current) => defaultAddress
      ? shippingFieldsFromAddress(defaultAddress)
      : ({
        ...current,
        recipient_name: current.recipient_name || selectedCustomer.name || '',
        recipient_company: current.recipient_company || selectedCustomer.company_name || '',
      }));
    setMailForwardingFormOpen(true);
    setSaveMailForwardingAddress(false);
    setMailForwardingAddressLabel('');
    setMailMessage('');
  };

  const openParcelForwardingCheckout = (packageWeightClass) => {
    const defaultAddress = addresses.find((address) => address.is_default);
    const selectedCustomer = dashboard?.customers?.[0] || {};
    setSelectedParcelWeight(packageWeightClass);
    setParcelForwardingAddress(defaultAddress
      ? shippingFieldsFromAddress(defaultAddress)
      : {
        ...EMPTY_MAIL_FORWARDING_ADDRESS,
        recipient_name: selectedCustomer.name || '',
        recipient_company: selectedCustomer.company_name || '',
      });
    setSaveParcelForwardingAddress(false);
    setParcelForwardingAddressLabel('');
    setParcelCheckoutFormOpen(true);
  };

  const createAddressBookEntry = async ({ address, label }) => {
    const response = await fetch(`${API_URL}/customer/addresses`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${readCustomerToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        label,
        ...address,
        is_default: false,
      }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || ui('Adresse konnte nicht gespeichert werden.', 'Address could not be saved.'));
    }
    setAddresses((current) => [...current, payload]);
  };

  const saveAddress = async (event) => {
    event.preventDefault();
    setAddressWorking(true);
    setAddressMessage('');
    setError('');
    try {
      const response = await fetch(
        `${API_URL}/customer/addresses${editingAddressId ? `/${editingAddressId}` : ''}`,
        {
          method: editingAddressId ? 'PUT' : 'POST',
          headers: {
            Authorization: `Bearer ${readCustomerToken()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(addressDraft),
        }
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || ui('Adresse konnte nicht gespeichert werden.', 'Address could not be saved.'));
      const listResponse = await fetch(`${API_URL}/customer/addresses`, {
        headers: { Authorization: `Bearer ${readCustomerToken()}` },
      });
      const listPayload = await listResponse.json().catch(() => []);
      if (listResponse.ok) setAddresses(Array.isArray(listPayload) ? listPayload : []);
      setAddressDraft(EMPTY_ADDRESS_BOOK_ENTRY);
      setEditingAddressId(null);
      setAddressMessage(ui('Adresse wurde gespeichert.', 'Address saved.'));
    } catch (addressError) {
      setError(addressError.message || ui('Adresse konnte nicht gespeichert werden.', 'Address could not be saved.'));
    } finally {
      setAddressWorking(false);
    }
  };

  const editAddress = (address) => {
    setEditingAddressId(address.id);
    setAddressDraft({ ...EMPTY_ADDRESS_BOOK_ENTRY, ...address });
    setAddressMessage('');
  };

  const deleteAddress = async (address) => {
    if (!window.confirm(ui(`Adresse „${address.label}“ wirklich löschen?`, `Delete address “${address.label}”?`))) return;
    setAddressWorking(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/customer/addresses/${address.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${readCustomerToken()}` },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || ui('Adresse konnte nicht gelöscht werden.', 'Address could not be deleted.'));
      setAddresses((current) => current.filter((item) => item.id !== address.id));
      if (editingAddressId === address.id) {
        setEditingAddressId(null);
        setAddressDraft(EMPTY_ADDRESS_BOOK_ENTRY);
      }
      setAddressMessage(ui('Adresse wurde gelöscht.', 'Address deleted.'));
    } catch (addressError) {
      setError(addressError.message || ui('Adresse konnte nicht gelöscht werden.', 'Address could not be deleted.'));
    } finally {
      setAddressWorking(false);
    }
  };

  const recreateMailServiceCheckout = async (requestId) => {
    setCreatingMailPaymentId(requestId);
    setError('');
    try {
      const response = await fetch(`${API_URL}/customer/portal-requests/${requestId}/service-fee-checkout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${readCustomerToken()}` },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || ui('Zahlungslink konnte nicht erstellt werden.', 'Payment link could not be created.'));
      await reloadDashboard();
    } catch (paymentError) {
      setError(paymentError.message || ui('Zahlungslink konnte nicht erstellt werden.', 'Payment link could not be created.'));
    } finally {
      setCreatingMailPaymentId(null);
    }
  };

  const cancelUnpaidMailForwarding = async (requestId) => {
    if (!window.confirm(ui(
      'Versandauftrag wirklich stornieren? Die Poststücke werden wieder in Ihren Posteingang gelegt.',
      'Cancel this forwarding request? The mail items will return to your inbox.'
    ))) return;
    setCancellingMailRequestId(requestId);
    setError('');
    try {
      const response = await fetch(`${API_URL}/customer/portal-requests/${requestId}/cancel-unpaid-forwarding`, {
        method: 'POST', headers: { Authorization: `Bearer ${readCustomerToken()}` },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || ui('Auftrag konnte nicht storniert werden.', 'Request could not be cancelled.'));
      setMailMessage(payload.message || ui('Auftrag wurde storniert.', 'Request cancelled.'));
      await reloadDashboard();
    } catch (cancelError) {
      setError(cancelError.message || ui('Auftrag konnte nicht storniert werden.', 'Request could not be cancelled.'));
    } finally {
      setCancellingMailRequestId(null);
    }
  };

  const recreateLogisticsServiceCheckout = async (requestId) => {
    setCreatingLogisticsPaymentId(requestId);
    setError('');
    try {
      const response = await fetch(`${API_URL}/customer/logistics/${requestId}/service-fee-checkout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${readCustomerToken()}` },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || ui('Zahlungslink konnte nicht erstellt werden.', 'Payment link could not be created.'));
      if (!payload.checkout_url) throw new Error(ui('Das Zahlungssystem hat keinen Zahlungslink zurückgegeben.', 'The payment system did not return a payment link.'));
      window.location.assign(payload.checkout_url);
    } catch (paymentError) {
      setError(paymentError.message || ui('Zahlungslink konnte nicht erstellt werden.', 'Payment link could not be created.'));
    } finally {
      setCreatingLogisticsPaymentId(null);
    }
  };

  const submitCompanyRequest = async (draft, evidence) => {
    setSubmittingCompany(true);
    setError('');

    try {
      const token = readCustomerToken();
      const payload = new FormData();
      Object.entries(draft).forEach(([key, value]) => payload.append(key, value || ''));
      if (selectedLegalEntity?.id) payload.append('legal_entity_id', selectedLegalEntity.id);
      payload.append('workflow_type', isPlannedFounding ? 'founding_completed' : 'company_change');
      if (evidence) payload.append('business_doc', evidence);
      const response = await fetch(`${API_URL}/customer/company-data-request`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: payload,
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || 'Änderung konnte nicht eingereicht werden.');
      }

      alert(data.message || 'Änderung wurde eingereicht.');
      setCompanyWizardOpen(false);
      await reloadDashboard();
    } catch (err) {
      setError(err.message || 'Änderung konnte nicht eingereicht werden.');
    } finally {
      setSubmittingCompany(false);
    }
  };

  const submitDisplayRequest = async (event) => {
    event.preventDefault();
    setSubmittingDisplay(true);
    setError('');

    try {
      const token = readCustomerToken();
      const payload = new FormData();
      payload.append('display_name', displayDraft.display_name || '');
      payload.append('display_note', displayDraft.display_note || '');
      if (selectedLegalEntity?.id) payload.append('legal_entity_id', selectedLegalEntity.id);
      if (displayLogo) {
        payload.append('logo', displayLogo);
      }

      const response = await fetch(`${API_URL}/customer/display-profile-request`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: payload,
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || 'Briefkasten & Logo konnte nicht gespeichert werden.');
      }

      alert(data.message || 'Briefkasten & Logo wurde gespeichert.');
      setDisplayLogo(null);
      await reloadDashboard();
    } catch (err) {
      setError(err.message || 'Briefkasten & Logo konnte nicht gespeichert werden.');
    } finally {
      setSubmittingDisplay(false);
    }
  };

  const generateUsageAgreement = async () => {
    if (!selectedLegalEntity?.id || agreementGenerating) return;
    setAgreementGenerating(true); setAgreementMessage(''); setError('');
    try {
      const response = await fetch(`${API_URL}/customer/usage-agreements/generate`, {
        method: 'POST', headers: { Authorization: `Bearer ${readCustomerToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ legal_entity_id: selectedLegalEntity.id, idempotency_key: window.crypto.randomUUID(), display_profile_decision: agreementDisplayDecision }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || ui('Nutzungsvereinbarung konnte nicht erzeugt werden.', 'The usage agreement could not be generated.'));
      setAgreementMessage(payload.message); await reloadDashboard();
    } catch (agreementError) { setAgreementMessage(agreementError.message); }
    finally { setAgreementGenerating(false); }
  };

  const requestEmailChange = async (event) => {
    event.preventDefault();
    if (normalizeText(newEmail).toLowerCase() !== normalizeText(newEmailConfirmation).toLowerCase()) {
      setError(ui('Die beiden neuen E-Mail-Adressen stimmen nicht überein.', 'The two new email addresses do not match.'));
      return;
    }
    if (!emailChangeTargets.length) {
      setError(ui('Bitte wählen Sie mindestens einen Bereich aus.', 'Please select at least one area.'));
      return;
    }
    setRequestingEmailChange(true);
    setEmailChangeMessage('');
    setError('');
    try {
      const token = readCustomerToken();
      const response = await fetch(`${API_URL}/customer/email-change/request`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          new_email: newEmail,
          current_password: emailChangePassword,
          targets: emailChangeTargets,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'E-Mail-Änderung konnte nicht angefordert werden.');
      setEmailChangeMessage(payload.message || 'Bestätigungslink wurde versendet.');
      setNewEmail('');
      setNewEmailConfirmation('');
      setEmailChangePassword('');
    } catch (err) {
      setError(err.message || 'E-Mail-Änderung konnte nicht angefordert werden.');
    } finally {
      setRequestingEmailChange(false);
    }
  };

  const saveNotificationPreferences = async (event) => {
    event.preventDefault();
    setNotificationPreferencesSaving(true);
    setNotificationPreferencesMessage('');
    try {
      const response = await fetch(`${API_URL}/customer/notification-preferences`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${readCustomerToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ optional: notificationPreferences.optional }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || ui('Einstellungen konnten nicht gespeichert werden.', 'Settings could not be saved.'));
      setNotificationPreferences(payload);
      setNotificationPreferencesMessage(ui('Benachrichtigungsauswahl gespeichert.', 'Notification choices saved.'));
    } catch (error) {
      setNotificationPreferencesMessage(error.message);
    } finally {
      setNotificationPreferencesSaving(false);
    }
  };

  const submitBillingAddress = async (event) => {
    event.preventDefault();
    if (stripeBillingSubscriptions.length && (!billingSubscriptionId || !billingRecipientConfirmed)) {
      setBillingAddressMessage(ui('Bitte wählen Sie den Vertrag und bestätigen Sie den Rechnungsempfänger.', 'Select the contract and confirm the invoice recipient.'));
      return;
    }
    setBillingAddressSaving(true);
    setBillingAddressMessage('');
    try {
      const response = await fetch(`${API_URL}/customer/billing-address`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${readCustomerToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...billingAddressDraft, legal_entity_id: selectedLegalEntity?.id || null,
          stripe_subscription_id: billingSubscriptionId || null,
          expected_request_id: activeBillingAddress?.request_id || null }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok && !payload?.synchronization) throw new Error(payload.error || ui('Rechnungsadresse konnte nicht gespeichert werden.', 'Billing address could not be saved.'));
      setDashboard((current) => ({ ...current, billing_address: payload,
        billing_addresses: [{ ...payload, legal_entity_id: selectedLegalEntity?.id || null },
          ...(current?.billing_addresses || []).filter(row => row.legal_entity_id !== (selectedLegalEntity?.id || null))] }));
      setBillingAddressMessage(payload.status === 'approved'
        ? ui('Die Rechnungsadresse wurde für zukünftige Vorgänge synchronisiert.', 'The billing address was synchronized for future transactions.')
        : payload.synchronization?.mode === 'controlled_contract'
          ? ui('Die Rechnungsadresse wurde zur Prüfung gespeichert. Wir prüfen die Zuordnung zu Ihrem Vertrag; Sie müssen Ihre Zahlungsdaten nicht erneut eingeben.', 'Your billing address was saved for review. We will check its assignment to your contract; you do not need to enter your payment details again.')
        : ui('Die Adresse wurde gespeichert, aber noch nicht in allen verbundenen Systemen synchronisiert.', 'The address was saved but has not yet synchronized to every connected system.'));
      if (payload.status === 'approved') setBillingRecipientConfirmed(false);
    } catch (error) {
      setBillingAddressMessage(error.message || ui('Rechnungsadresse konnte nicht gespeichert werden.', 'Billing address could not be saved.'));
    } finally {
      setBillingAddressSaving(false);
    }
  };

  const retryBillingAddress = async () => {
    const requestId = activeBillingAddress?.request_id;
    if (!requestId) return;
    setBillingAddressSaving(true);
    setBillingAddressMessage('');
    try {
      const response = await fetch(`${API_URL}/customer/billing-address/${requestId}/retry`, {
        method: 'POST', headers: { Authorization: `Bearer ${readCustomerToken()}` },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok && !payload?.synchronization) throw new Error(payload.error || ui('Wiederholung fehlgeschlagen.', 'Retry failed.'));
      setDashboard((current) => ({ ...current, billing_address: payload,
        billing_addresses: [{ ...payload, legal_entity_id: selectedLegalEntity?.id || null },
          ...(current?.billing_addresses || []).filter(row => row.legal_entity_id !== (selectedLegalEntity?.id || null))] }));
      setBillingAddressMessage(payload.status === 'approved'
        ? ui('Die Synchronisierung ist jetzt vollständig.', 'Synchronization is now complete.')
        : payload.synchronization?.mode === 'controlled_contract'
          ? ui('Die Rechnungsadresse liegt weiterhin zur Prüfung vor. Eine erneute Eingabe Ihrer Zahlungsdaten ist nicht erforderlich.', 'Your billing address is still awaiting review. You do not need to re-enter your payment details.')
        : ui('Mindestens ein verbundenes System ist weiterhin nicht erreichbar.', 'At least one connected system is still unavailable.'));
    } catch (error) {
      setBillingAddressMessage(error.message || ui('Wiederholung fehlgeschlagen.', 'Retry failed.'));
    } finally {
      setBillingAddressSaving(false);
    }
  };

  const toggleEmailChangeTarget = (target) => {
    setEmailChangeTargets((current) => current.includes(target)
      ? current.filter((item) => item !== target)
      : [...current, target]);
  };

  const cancelCustomerSubscription = async (subscription) => {
    if (!subscription.id || cancellingSubscriptionId) return;

    const confirmed = window.confirm(
      ui('Möchten Sie dieses Abonnement wirklich kündigen? Ihr Zugang bleibt bis zum Ende des bezahlten Zeitraums aktiv.', 'Do you really want to cancel this subscription? Your access remains active until the end of the paid period.')
    );
    if (!confirmed) return;

    setCancellingSubscriptionId(subscription.id);
    setError('');

    try {
      const token = readCustomerToken();
      const response = await fetch(`${API_URL}/customer/subscriptions/${encodeURIComponent(subscription.id)}/cancel`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || 'Abonnement konnte nicht gekündigt werden.');
      }

      alert(data.message || 'Abonnement wurde gekündigt.');
      await reloadDashboard();
    } catch (err) {
      setError(err.message || 'Abonnement konnte nicht gekündigt werden.');
    } finally {
      setCancellingSubscriptionId(null);
    }
  };

  const saveProfileLabel = async (event) => {
    event.preventDefault();
    if (!selectedLegalEntity?.id || profileLabelSaving) return;
    setProfileLabelSaving(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/customer/legal-entities/${encodeURIComponent(selectedLegalEntity.id)}/profile-label`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${readCustomerToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ profile_label: profileLabelDraft }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || ui('Die Profilbezeichnung konnte nicht gespeichert werden.', 'The profile label could not be saved.'));
      setDashboard((current) => ({
        ...current,
        legal_entities: (current?.legal_entities || []).map((entity) => entity.id === payload.entity.id ? payload.entity : entity),
      }));
    } catch (error) {
      setError(error.message || ui('Die Profilbezeichnung konnte nicht gespeichert werden.', 'The profile label could not be saved.'));
    } finally {
      setProfileLabelSaving(false);
    }
  };

  const startNewCompanyBooking = async () => {
    if (dashboard?.subscriptions_loading || dashboard?.subscription_error) {
      setError(ui('Der bestehende Abonnementstatus muss zuerst sicher geladen werden. Bitte prüfen Sie den Status erneut; es wurde keine neue Buchung gestartet.', 'Your existing subscription status must be loaded safely first. Please retry the status check; no new booking was started.'));
      return;
    }
    if (!hasApprovedAccountIdentity) {
      setError(ui('Bitte schließen Sie zuerst Ihre persönliche Identitätsprüfung ab. Danach können Sie weitere Unternehmensprofile und getrennte Abonnements anlegen.', 'Please complete your personal identity verification first. You can then add further company profiles and separate subscriptions.'));
      return;
    }
    if (!selectedLegalEntity?.id || newBookingLoading) {
      setError(ui('Bitte wählen Sie zuerst ausdrücklich das Unternehmen für die neue Buchung aus.', 'Please explicitly select the company for the new booking first.'));
      return;
    }
    const plan = BUSINESS_PLANS.find((item) => item.code === newBookingPlan);
    if (!plan) return;
    const confirmed = window.confirm(ui(
      `Neue Buchung für „${legalEntityLabel(selectedLegalEntity)}“ im Tarif ${plan.name} (${plan.price} / Monat) starten? Die Buchung und alle folgenden Unterlagen werden diesem Unternehmensprofil zugeordnet.`,
      `Start a new booking for “${legalEntityLabel(selectedLegalEntity)}” on the ${plan.name} plan (${plan.price} / month)? The booking and all subsequent documents will be assigned to this company profile.`
    ));
    if (!confirmed) return;
    setNewBookingLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/customer/billing/stripe/checkout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${readCustomerToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ legal_entity_id: selectedLegalEntity.id, plan_code: plan.code }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || ui('Die neue Buchung konnte nicht gestartet werden.', 'The new booking could not be started.'));
      if (!payload.url) throw new Error(ui('Es wurde kein sicherer Stripe-Link zurückgegeben.', 'No secure Stripe link was returned.'));
      window.location.assign(payload.url);
    } catch (error) {
      setError(error.message || ui('Die neue Buchung konnte nicht gestartet werden.', 'The new booking could not be started.'));
      setNewBookingLoading(false);
    }
  };

  const recoverSlowSealCheckout = async ({ subscriptionId, changeType, targetPlanCode, startedAt }) => {
    const deadline = startedAt + CHECKOUT_PREPARATION_TIMEOUT_MS;
    let lastError = null;
    do {
      try {
        const token = readCustomerToken();
        const changesResponse = await fetch(`${API_URL}/customer/subscription-changes`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const changesPayload = await changesResponse.json().catch(() => ({}));
        if (!changesResponse.ok) throw new Error(changesPayload.error || 'Subscription changes unavailable');
        const change = (Array.isArray(changesPayload.changes) ? changesPayload.changes : []).find((candidate) => matchesRecoverableChange(candidate, {
          subscriptionId, changeType, targetPlanCode, startedAt,
        }));
        if (change) {
          setSubscriptionChanges((current) => [change, ...current.filter((item) => item.id !== change.id)]);
          const resumeResponse = await fetch(`${API_URL}/customer/subscription-changes/${encodeURIComponent(change.id)}/resume-checkout`, {
            method: 'POST', headers: { Authorization: `Bearer ${token}` },
          });
          const resumePayload = await resumeResponse.json().catch(() => ({}));
          if (!resumeResponse.ok) throw new Error(resumePayload.error || 'Stripe confirmation unavailable');
          if (!resumePayload.checkout_url) throw new Error('Stripe confirmation URL missing');
          return { change, checkoutUrl: resumePayload.checkout_url };
        }
      } catch (error) {
        lastError = error;
      }
      if (Date.now() < deadline) await wait(Math.min(CHECKOUT_RECOVERY_POLL_MS, Math.max(0, deadline - Date.now())));
    } while (Date.now() < deadline);
    if (lastError) console.warn('[checkout-recovery] Automatic recovery did not complete:', lastError.message || String(lastError));
    return null;
  };

  const openStripePaymentMethod = async (subscription, systemChangeConfirmed = false) => {
    if (!subscription.id || openingPaymentMethodId) return;
    const changesBillingSystem = subscription.billing_provider === 'shopify_seal';
    if (changesBillingSystem && !systemChangeConfirmed) {
      setPaymentMethodConfirmationId(subscription.id);
      setPaymentTermsAcceptedId(null);
      return;
    }
    const systemChangeQuote = subscription.system_change_quote || null;
    const immediatePaymentRecovery = getSubscriptionPaymentAction(subscription) === 'recover'
      || (paymentRecoveryRequested && String(subscription.id) === String(paymentRecoverySubscriptionId));
    if (changesBillingSystem && (!systemChangeQuote
      || (systemChangeQuote.requires_price_change_consent && paymentTermsAcceptedId !== subscription.id))) {
      setError(ui('Bitte bestätigen Sie zuerst ausdrücklich die angezeigten neuen Abrechnungskonditionen.', 'Please explicitly accept the displayed new billing terms first.'));
      return;
    }
    setPaymentMethodConfirmationId(null);
    const checkoutWindow = changesBillingSystem ? openCheckoutPreparationWindow() : null;
    setOpeningPaymentMethodId(subscription.id);
    setError('');
    const checkoutFlow = newCheckoutFlow('payment_method_system_change', subscription.id);
    setCheckoutPreparation(checkoutFlow);
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), CHECKOUT_PREPARATION_TIMEOUT_MS);
    try {
      const token = readCustomerToken();
      const response = await fetch(`${API_URL}/customer/subscriptions/${encodeURIComponent(subscription.id)}/payment-method`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', 'X-YGA-Flow-Id': checkoutFlow.flowId },
        body: JSON.stringify(changesBillingSystem ? {
          system_change: true,
          immediate_payment_recovery: immediatePaymentRecovery,
          consent: true,
          quote_reference: systemChangeQuote?.reference || null,
          price_change_consent: systemChangeQuote?.requires_price_change_consent
            ? paymentTermsAcceptedId === subscription.id
            : false,
          ...((subscription.legal_entity_id || selectedLegalEntity?.id)
            ? { legal_entity_id: subscription.legal_entity_id || selectedLegalEntity.id }
            : {}),
        } : {}),
        signal: controller.signal,
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        const recovery = changesBillingSystem && response.status >= 500
          ? await recoverSlowSealCheckout({ subscriptionId: subscription.id, changeType: 'payment_method_system_change', startedAt: checkoutFlow.startedAt })
          : null;
        if (recovery) {
          updateCheckoutFlow({ responseAt: Date.now(), redirectAt: Date.now(), requestId: recovery.change.id, recovered: true });
          setCheckoutRecovery({ changeId: recovery.change.id, checkoutUrl: recovery.checkoutUrl });
          openPreparedCheckout(checkoutWindow, recovery.checkoutUrl);
          return;
        }
        throw new Error(payload.error || ui('Zahlungsart konnte nicht geöffnet werden.', 'Payment method could not be opened.'));
      }
      if (payload.provider === 'shopify_seal' && payload.message) {
        closeCheckoutPreparationWindow(checkoutWindow);
        window.alert(payload.message);
        setOpeningPaymentMethodId(null);
        return;
      }
      if (!payload.url) throw new Error(ui('Es wurde kein sicherer Zahlungslink zurückgegeben.', 'No secure payment link was returned.'));
      updateCheckoutFlow({ responseAt: Date.now(), redirectAt: Date.now(), requestId: payload.request?.id || null });
      openPreparedCheckout(checkoutWindow, payload.url);
    } catch (err) {
      const recovery = changesBillingSystem
        ? await recoverSlowSealCheckout({ subscriptionId: subscription.id, changeType: 'payment_method_system_change', startedAt: checkoutFlow.startedAt })
        : null;
      if (recovery) {
        updateCheckoutFlow({ responseAt: Date.now(), redirectAt: Date.now(), requestId: recovery.change.id, recovered: true });
        setCheckoutRecovery({ changeId: recovery.change.id, checkoutUrl: recovery.checkoutUrl });
        openPreparedCheckout(checkoutWindow, recovery.checkoutUrl);
        return;
      }
      closeCheckoutPreparationWindow(checkoutWindow);
      updateCheckoutFlow({ failedAt: Date.now() });
      setError(err.name === 'AbortError'
        ? ui('Die Vorbereitung hat länger als 90 Sekunden gedauert. Es wurde kein zweiter Vorgang gestartet. Bitte laden Sie die Seite neu und setzen Sie den vorhandenen Wechsel fort.', 'Preparation took longer than 90 seconds. No second request was started. Reload the page and continue the existing change.')
        : err.message || ui('Zahlungsart konnte nicht geöffnet werden.', 'Payment method could not be opened.'));
      setOpeningPaymentMethodId(null);
      setCheckoutPreparation(null);
    } finally {
      window.clearTimeout(timeout);
    }
  };

  const requestPlanChange = async (subscription) => {
    const draft = planChangeDrafts[subscription.id] || {};
    if (!draft.target || draft.consent !== true || changingPlanId) return;
    const effectiveDate = formatDate(subscription.next_billing_date || subscription.access_ends_at);
    const confirmed = window.confirm(ui(
      `Der Wechsel zu ${draft.target.toUpperCase()} erfolgt ohne Zwischenabrechnung zum nächsten Abrechnungstermin${effectiveDate !== '-' ? ` (${effectiveDate})` : ''}. Bestehende Daten, Dokumente und Freigaben bleiben erhalten. Fortfahren?`,
      `The change to ${draft.target.toUpperCase()} will take effect without proration at the next billing date${effectiveDate !== '-' ? ` (${effectiveDate})` : ''}. Existing data, documents and approvals remain unchanged. Continue?`
    ));
    if (!confirmed) return;
    const checkoutWindow = subscription.billing_provider === 'shopify_seal' ? openCheckoutPreparationWindow() : null;
    setChangingPlanId(subscription.id);
    setError('');
    const checkoutFlow = newCheckoutFlow('plan_change', subscription.id);
    setCheckoutPreparation(checkoutFlow);
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), CHECKOUT_PREPARATION_TIMEOUT_MS);
    try {
      const response = await fetch(`${API_URL}/customer/subscriptions/${encodeURIComponent(subscription.id)}/change-plan`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${readCustomerToken()}`, 'Content-Type': 'application/json', 'X-YGA-Flow-Id': checkoutFlow.flowId },
        body: JSON.stringify({
          target_plan_code: draft.target,
          consent: true,
          ...((subscription.legal_entity_id || selectedLegalEntity?.id)
            ? { legal_entity_id: subscription.legal_entity_id || selectedLegalEntity.id }
            : {}),
        }),
        signal: controller.signal,
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        const recovery = subscription.billing_provider === 'shopify_seal' && response.status >= 500
          ? await recoverSlowSealCheckout({ subscriptionId: subscription.id, changeType: 'seal_to_stripe', targetPlanCode: draft.target, startedAt: checkoutFlow.startedAt })
          : null;
        if (recovery) {
          updateCheckoutFlow({ responseAt: Date.now(), redirectAt: Date.now(), requestId: recovery.change.id, recovered: true });
          setCheckoutRecovery({ changeId: recovery.change.id, checkoutUrl: recovery.checkoutUrl });
          openPreparedCheckout(checkoutWindow, recovery.checkoutUrl);
          return;
        }
        throw new Error(payload.error || ui('Tarifwechsel konnte nicht vorbereitet werden.', 'Plan change could not be prepared.'));
      }
      if (payload.checkout_url) {
        updateCheckoutFlow({ responseAt: Date.now(), redirectAt: Date.now(), requestId: payload.request?.id || null });
        if (payload.request?.id) {
          setSubscriptionChanges((current) => [
            payload.request,
            ...current.filter((change) => change.id !== payload.request.id),
          ]);
        }
        setCheckoutRecovery({
          changeId: payload.request?.id || null,
          checkoutUrl: payload.checkout_url,
        });
        openPreparedCheckout(checkoutWindow, payload.checkout_url);
        return;
      }
      window.alert(payload.message || ui('Tarifwechsel wurde vorgemerkt.', 'Plan change has been scheduled.'));
      const changesResponse = await fetch(`${API_URL}/customer/subscription-changes`, { headers: { Authorization: `Bearer ${readCustomerToken()}` } });
      const changesPayload = await changesResponse.json().catch(() => ({}));
      if (changesResponse.ok) setSubscriptionChanges(Array.isArray(changesPayload.changes) ? changesPayload.changes : []);
    } catch (err) {
      const recovery = subscription.billing_provider === 'shopify_seal'
        ? await recoverSlowSealCheckout({ subscriptionId: subscription.id, changeType: 'seal_to_stripe', targetPlanCode: draft.target, startedAt: checkoutFlow.startedAt })
        : null;
      if (recovery) {
        updateCheckoutFlow({ responseAt: Date.now(), redirectAt: Date.now(), requestId: recovery.change.id, recovered: true });
        setCheckoutRecovery({ changeId: recovery.change.id, checkoutUrl: recovery.checkoutUrl });
        openPreparedCheckout(checkoutWindow, recovery.checkoutUrl);
        return;
      }
      closeCheckoutPreparationWindow(checkoutWindow);
      updateCheckoutFlow({ failedAt: Date.now() });
      setError(err.name === 'AbortError'
        ? ui('Die Vorbereitung hat länger als 90 Sekunden gedauert. Es wurde kein zweiter Vorgang gestartet. Bitte laden Sie die Seite neu und setzen Sie den vorhandenen Wechsel fort.', 'Preparation took longer than 90 seconds. No second request was started. Reload the page and continue the existing change.')
        : err.message || ui('Tarifwechsel konnte nicht vorbereitet werden.', 'Plan change could not be prepared.'));
      setCheckoutPreparation(null);
    } finally {
      window.clearTimeout(timeout);
      setChangingPlanId(null);
    }
  };

  const resumeSubscriptionChangeCheckout = async (change) => {
    if (!change?.id || resumingChangeId) return;
    const checkoutWindow = openCheckoutPreparationWindow();
    setResumingChangeId(change.id);
    setError('');
    try {
      const response = await fetch(`${API_URL}/customer/subscription-changes/${encodeURIComponent(change.id)}/resume-checkout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${readCustomerToken()}` },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || ui('Stripe-Bestätigung konnte nicht geöffnet werden.', 'Stripe confirmation could not be opened.'));
      if (!payload.checkout_url) throw new Error(ui('Es wurde kein sicherer Zahlungslink zurückgegeben.', 'No secure payment link was returned.'));
      setCheckoutRecovery({ changeId: change.id, checkoutUrl: payload.checkout_url });
      openPreparedCheckout(checkoutWindow, payload.checkout_url);
    } catch (err) {
      closeCheckoutPreparationWindow(checkoutWindow);
      setError(err.message || ui('Der sichere Zahlungsbereich konnte nicht geöffnet werden.', 'The secure payment area could not be opened.'));
      setResumingChangeId(null);
    }
  };

  const renderSubscriptionChange = (change, { detached = false } = {}) => {
    const targetPlan = BUSINESS_PLANS.find((plan) => plan.code === normalizeText(change.target_plan_code).toLowerCase());
    const acceptedSystemChangeQuote = change.metadata?.accepted_system_change_quote || null;
    const systemChangePricingChanged = acceptedSystemChangeQuote?.requires_price_change_consent === true;
    const acceptedTargetPrice = acceptedSystemChangeQuote
      ? new Intl.NumberFormat(getLang() === 'de' ? 'de-DE' : 'en-GB', {
        style: 'currency', currency: String(acceptedSystemChangeQuote.target_currency || 'EUR').toUpperCase(),
      }).format(Number(acceptedSystemChangeQuote.target_amount_minor || 0) / 100)
      : null;
    const initialPaymentFailed = change.failure_code === 'STRIPE_INITIAL_PAYMENT_FAILED';
    const sealMigrationAwaitingPayment = change.status === 'service_grace'
      && ['seal_to_stripe', 'payment_method_system_change'].includes(change.change_type);
    const changeTitle = initialPaymentFailed
      ? ui('Zahlung nicht abgeschlossen', 'Payment not completed')
      : sealMigrationAwaitingPayment
      ? ui('Erste Zahlung noch nicht abgeschlossen', 'First payment not completed yet')
      : change.status === 'scheduled'
      ? ui('Paketwechsel geplant', 'Plan change scheduled')
      : ['scheduling', 'manual_review'].includes(change.status)
        ? ui('Zahlungsart bestätigt', 'Payment method confirmed')
        : change.change_type === 'payment_method_system_change'
          ? ui('Zahlungsartenwechsel fortsetzen', 'Continue payment method change')
          : ui('Paketwechsel fortsetzen', 'Continue plan change');
    return (
      <div className={`info-box plan-change-active plan-change-active--${change.status}${detached ? ' plan-change-active--detached' : ''}`} key={change.id}>
        <div className="plan-change-active__heading">
          <strong>{changeTitle}</strong>
          <span>{initialPaymentFailed ? ui('Zahlungsart prüfen', 'Check payment method') : sealMigrationAwaitingPayment ? ui('Zahlung ausstehend', 'Payment pending') : change.status === 'scheduled' ? ui('Termin bestätigt', 'Date confirmed') : ['scheduling', 'manual_review'].includes(change.status) ? ui('Wird verarbeitet', 'Processing') : ui('Aktion erforderlich', 'Action required')}</span>
        </div>
        {detached ? <p className="plan-change-active__note">{ui('Der Anbieterstatus ist gerade nicht erreichbar. Ihr bereits angelegter Wechsel bleibt erhalten und kann hier sicher fortgesetzt werden.', 'The provider status is temporarily unavailable. Your existing change is preserved and can be continued safely here.')}</p> : null}
        <p>{change.change_type === 'payment_method_system_change'
          ? <>{systemChangePricingChanged && acceptedTargetPrice
            ? ui(`Paket bleibt ${targetPlan?.name || normalizeText(change.target_plan_code).toUpperCase()}; neuer Preis: ${acceptedTargetPrice} pro Monat.`, `Plan remains ${targetPlan?.name || normalizeText(change.target_plan_code).toUpperCase()}; new price: ${acceptedTargetPrice} per month.`)
            : ui('Paket und Preis bleiben unverändert.', 'Plan and price remain unchanged.')}<br />{ui('Wirksam zum', 'Effective on')}: <strong>{formatDate(change.effective_at)}</strong></>
          : <>{ui('Zielpaket', 'Target plan')}: <strong>{targetPlan?.name || normalizeText(change.target_plan_code).toUpperCase()}</strong>{targetPlan ? <> · <strong>{targetPlan.price} {ui('pro Monat', 'per month')}</strong></> : null}<br />{ui('Erste Abbuchung und Paketwechsel am', 'First charge and plan change on')}: <strong>{formatDate(change.effective_at)}</strong><br />{ui('Heute erfolgt keine Belastung.', 'You will not be charged today.')}</>}
        </p>
        {initialPaymentFailed ? <p className="plan-change-active__note">{ui('Die erste Zahlung konnte nicht abgeschlossen werden. Bitte aktualisieren Sie die Zahlungsart; der Vorgang bleibt bis zur erfolgreichen Zahlung offen.', 'The first payment could not be completed. Please update the payment method; the process remains open until payment succeeds.')}</p> : null}
        {sealMigrationAwaitingPayment ? <p className="plan-change-active__note">{ui('Ihre bisherige Abrechnung ist beendet. Ihre Kundenleistung bleibt während der ausstehenden Zahlung ohne Unterbrechung im Kulanzstatus aktiv. Bitte geben Sie die Zahlungsart nicht erneut ein.', 'Your previous billing arrangement has ended. Your service remains active without interruption under a grace status while payment is pending. Please do not enter the payment method again.')}</p> : null}
        {!initialPaymentFailed && ['scheduling', 'manual_review'].includes(change.status) ? <p className="plan-change-active__note">{ui('Ihre Zahlungsart wurde bestätigt. Der Wechsel wird verarbeitet; Sie müssen die Zahlungsart nicht erneut eingeben.', 'Your payment method has been confirmed. The change is being processed; you do not need to enter it again.')}</p> : null}
        {change.status === 'payment_method_pending' ? (
          <button type="button" className="primary-button" disabled={!!resumingChangeId} onClick={() => resumeSubscriptionChangeCheckout(change)}>
            {resumingChangeId === change.id ? ui('Zahlungsbereich wird geöffnet …', 'Opening payment area …') : ui('Zahlungsart sicher bestätigen', 'Confirm payment method securely')}
          </button>
        ) : null}
      </div>
    );
  };

  const withdrawCustomerSubscription = async (subscription) => {
    if (!subscription.id || withdrawingSubscriptionId) return;

    setWithdrawingSubscriptionId(subscription.id);
    setError('');

    try {
      const token = readCustomerToken();
      const previewResponse = await fetch(`${API_URL}/customer/subscriptions/${encodeURIComponent(subscription.id)}/withdrawal-preview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const previewData = await previewResponse.json().catch(() => ({}));
      if (!previewResponse.ok) {
        throw new Error(previewData.error || ui('Die Rückerstattungsvorschau konnte nicht geladen werden.', 'The refund preview could not be loaded.'));
      }

      const paymentPending = previewData.preview?.payment_pending === true;
      const expectedAmount = formatRefundAmount(previewData.preview);
      const confirmed = window.confirm(paymentPending
        ? ui(
          'Die Zahlung wird noch verarbeitet. Möchten Sie den Widerruf trotzdem jetzt fristgerecht einreichen? Falls die Zahlung später erfolgreich eingezogen wird, prüfen wir anschließend die Rückerstattung.',
          'The payment is still being processed. Would you like to submit the withdrawal on time now? If the payment is collected successfully later, we will then review the refund.'
        )
        : ui(
          `Voraussichtliche Rückerstattung: ${expectedAmount}.\n\nMöchten Sie den Widerruf verbindlich anfragen? Es erfolgt noch keine Rückerstattung. Die Anfrage wird zuerst durch uns geprüft. Der endgültige Betrag kann sich bis zur Freigabe ändern.`,
          `Expected refund: ${expectedAmount}.\n\nWould you like to submit a binding withdrawal request? No refund will be issued yet. We will review the request first. The final amount may change before approval.`
        ));
      if (!confirmed) return;

      const confirmation = window.prompt(ui('Sicherheitsbestätigung: Bitte geben Sie WIDERRUFEN ein:', 'Security confirmation: Please enter WIDERRUFEN:'));
      if (normalizeText(confirmation).toUpperCase() !== 'WIDERRUFEN') {
        if (confirmation) setError(ui('Die Sicherheitsbestätigung war nicht korrekt.', 'The security confirmation was incorrect.'));
        return;
      }

      const response = await fetch(`${API_URL}/customer/subscriptions/${encodeURIComponent(subscription.id)}/withdraw`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ confirmation }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || 'Widerruf konnte nicht verarbeitet werden.');
      }

      alert(data.message || 'Widerruf wurde zur Prüfung angefragt.');
      await reloadDashboard();
    } catch (err) {
      setError(err.message || 'Widerruf konnte nicht verarbeitet werden.');
    } finally {
      setWithdrawingSubscriptionId(null);
    }
  };

  if (loading) return (
    <main className="business-hub-loading" aria-live="polite">
      <span className="business-hub-loading__mark" aria-hidden="true">Y</span>
      <div>
        <strong>YourGermanAddress</strong>
        <p>{ui('Ihr Business Hub wird geladen …', 'Your Business Hub is loading …')}</p>
      </div>
    </main>
  );

  if (!dashboard?.account) return (
    <main className="business-hub-loading business-hub-load-error" role="alert">
      <span className="business-hub-loading__mark" aria-hidden="true">!</span>
      <div>
        <strong>{ui('Business Hub konnte nicht geladen werden', 'Business Hub could not be loaded')}</strong>
        <p>{error || ui(
          'Bitte versuchen Sie es erneut. Falls der Fehler bestehen bleibt, melden Sie sich bitte beim Support.',
          'Please try again. If the error persists, please contact support.'
        )}</p>
        <div className="button-row">
          <button className="primary-button" type="button" onClick={() => window.location.reload()}>
            {ui('Erneut versuchen', 'Try again')}
          </button>
          <button className="secondary-link-button" type="button" onClick={logout}>
            {ui('Neu anmelden', 'Sign in again')}
          </button>
        </div>
      </div>
    </main>
  );

  return (
    <BusinessHubShell
      activeTab={activeTab}
      allowAdditionalServices={hasApprovedAccountIdentity}
      allowCompanyDataWorkflow={canCompleteFounding || hasApprovedAccountIdentity}
      email={account.email}
      hasCompletedVerification={hasCompletedVerification}
      hasMailAccess={hasMailAccess}
      companyContextLabel={selectedLegalEntity ? legalEntityLabel(selectedLegalEntity) : ui('Noch kein Unternehmensprofil', 'No company profile yet')}
      logout={logout}
      navigationCounts={{ logistics: additionalServicesAttentionCount }}
      onCompanyContextAction={() => setActiveTab('company')}
      onTabChange={setActiveTab}
      labels={{
        platform: ui('Ihre zentrale Plattform', 'Your central platform'),
        navigation: ui('Business-Hub-Bereiche', 'Business Hub sections'),
        mobileNavigation: ui('Bereich auswählen', 'Choose section'),
        locked: ui('Nach vollständiger Verifizierung verfügbar', 'Available after verification is complete'),
        signedIn: ui('Angemeldet als', 'Signed in as'),
        eyebrow: 'Business Hub',
        title: ui('Alles Wichtige an einem Ort.', 'Everything important in one place.'),
        website: ui('Zur Website', 'Back to website'),
        logout: ui('Ausloggen', 'Sign out'),
        overview: ui('Dashboard', 'Dashboard'),
        mailbox: ui('Posteingang', 'Inbox'),
        documents: ui('Dokumente', 'Documents'),
        addresses: ui('Adressbuch', 'Address book'),
        verifications: ui('Verifizierung', 'Verification'),
        company: ui('Konto & Daten', 'Account & details'),
        display: ui('Briefkasten', 'Mailbox display'),
        subscriptions: ui('Paket & Abrechnung', 'Plan & billing'),
        logistics: ui('Zusatzleistungen', 'Additional services'),
        guides: ui('Anleitungen', 'Guides'),
        groupStart: ui('Start', 'Start'),
        groupMail: ui('Post & Dokumente', 'Mail & documents'),
        groupCompany: ui('Unternehmen', 'Company'),
        groupBilling: ui('Verträge & Service', 'Plans & services'),
        groupHelp: ui('Hilfe', 'Help'),
        companyContext: ui('Ausgewähltes Unternehmen', 'Selected company'),
        companyContextAction: ui('Wechseln oder verwalten', 'Switch or manage'),
      }}
    >
      {error ? <div className="hint-box">{error}</div> : null}

        {activeTab === 'company' ? (
        <section id="company-profiles" className="legal-entity-switcher" aria-labelledby="legal-entity-switcher-heading">
          <div>
            <span className="customer-onboarding__eyebrow">{ui('Konto & Unternehmen', 'Account & companies')}</span>
            <h2 id="legal-entity-switcher-heading">{ui('Unternehmensprofile verwalten', 'Manage company profiles')}</h2>
            <p>{ui('Buchungen, Verifizierungen, Dokumente und Postfächer werden immer diesem Profil zugeordnet.', 'Orders, verifications, documents and mailboxes are always assigned to this profile.')}</p>
          </div>
          {legalEntities.length ? (
            <>
              <div className="legal-entity-hierarchy" role="list" aria-label={ui('Unternehmensprofile dieses Kundenkontos', 'Company profiles in this customer account')}>
                {legalEntities.map((entity) => {
                  const entityVerification = allVerifications.find((item) => item.legal_entity_id === entity.id) || null;
                  const lifecycle = (dashboard?.company_profiles || []).find((item) => item.legal_entity_id === entity.id);
                  const nextStepLabels = {
                    support_review_required: ui('Supportprüfung erforderlich', 'Support review required'), book_subscription: ui('Eigenes Abo buchen', 'Book a separate subscription'),
                    start_verification: ui('Unternehmensprüfung starten', 'Start company verification'), complete_identity_verification: ui('Identität abschließen', 'Complete identity verification'),
                    complete_company_verification: ui('Unternehmensunterlagen vervollständigen', 'Complete company documents'), await_company_review: ui('Prüfung abwarten', 'Wait for review'),
                    contact_support: ui('Support kontaktieren', 'Contact support'), create_usage_agreement: ui('Nutzungsvereinbarung erstellen', 'Create usage agreement'),
                    await_mailbox_setup: ui('Postfach-Einrichtung abwarten', 'Wait for mailbox setup'), ready: ui('Kein weiterer Schritt erforderlich', 'No further step required'),
                  };
                  return (
                    <button
                      type="button"
                      role="listitem"
                      key={entity.id}
                      className={`legal-entity-hierarchy__card${selectedLegalEntity?.id === entity.id ? ' is-active' : ''}`}
                      onClick={() => setSelectedLegalEntityId(entity.id)}
                    >
                      <strong>{legalEntityLabel(entity)}</strong>
                      <span>{ui('Identität', 'Identity')}: {statusLabel(entityVerification?.identity_status || (hasApprovedAccountIdentity ? 'already_verified' : 'pending'))}</span>
                      <span>{ui('Unternehmen', 'Business')}: {statusLabel(entityVerification?.business_status || 'pending')}</span>
                      <span>{ui('Nächster Schritt', 'Next step')}: {nextStepLabels[lifecycle?.next_step] || nextStepLabels.support_review_required}</span>
                    </button>
                  );
                })}
              </div>
              <label>
                <span>{ui('Unternehmen auswählen', 'Select company')}</span>
                <select value={selectedLegalEntity?.id || ''} onChange={(event) => setSelectedLegalEntityId(event.target.value)}>
                  {legalEntities.map((entity) => (
                    <option value={entity.id} key={entity.id}>
                      {legalEntityLabel(entity)}{entity.registration_number ? ` · ${entity.registration_number}` : ''}
                    </option>
                  ))}
                </select>
              </label>
            </>
          ) : hasUnscopedFounding ? <div className="info-box">{ui(
            'Ihre erste Firma darf in Gründung bleiben. Sie können ein weiteres Unternehmen mit eigenem Abo und eigener Verifizierung hinzufügen. Die bestehende Buchung bleibt getrennt erhalten. Nach der Gründung reichen Sie die Unterlagen unter Konto & Daten ein.',
            'Your first company can remain in formation. You can add another company with its own subscription and verification. Your existing booking stays separate. After incorporation, submit the documents under Account & details.'
          )}<br /><button type="button" className="secondary-link-button" onClick={() => setActiveTab('company')}>
            {ui('Zu Konto & Daten', 'Go to Account & details')}
          </button></div> : <div className="info-box">{ui(
            'Ihre bestehenden Vorgänge bleiben unverändert. Für eine neue Buchung legen Sie hier bewusst das passende Unternehmensprofil an.',
            'Your existing records remain unchanged. For a new booking, explicitly create the appropriate company profile here.'
          )}</div>}
          {selectedLegalEntity ? (
            <>
              <form className="legal-entity-profile-label" onSubmit={saveProfileLabel}>
                <label>
                  {ui('Eigene Profilbezeichnung (optional)', 'Custom profile label (optional)')}
                  <input maxLength="80" value={profileLabelDraft} onChange={(event) => setProfileLabelDraft(event.target.value)} placeholder={legalEntityLabel({ ...selectedLegalEntity, profile_label: null })} />
                </label>
                <button type="submit" className="secondary-button" disabled={profileLabelSaving}>
                  {profileLabelSaving ? ui('Wird gespeichert …', 'Saving …') : ui('Bezeichnung speichern', 'Save label')}
                </button>
                <small>{ui('Die Profilbezeichnung dient ausschließlich Ihrer Übersicht. Sie wird niemals für Nextcloud-Ordner, Verträge, Rechnungen oder rechtliche Unternehmensdaten verwendet.', 'The profile label is for your overview only. It is never used for Nextcloud folders, contracts, invoices, or legal company data.')}</small>
              </form>
            </>
          ) : null}
          <button type="button" className="secondary-button" disabled={!hasApprovedAccountIdentity} onClick={() => setLegalEntityFormOpen((open) => !open)}>
            {legalEntityFormOpen ? ui('Abbrechen', 'Cancel') : ui('Neues Unternehmen anlegen', 'Add new company')}
          </button>
          {!hasApprovedAccountIdentity ? <div className="hint-box">{ui(
            'Für weitere Unternehmensprofile benötigen wir zuerst die Bestätigung Ihrer persönlichen Identität. Die Unternehmensprüfung bleibt danach je Profil separat.',
            'We first need to confirm your personal identity before additional company profiles can be added. Company review then remains separate for each profile.'
          )}</div> : null}
          {legalEntityFormOpen ? (
            <a href="?tab=guides#guide-companies" onClick={() => setActiveTab('guides')}>{ui('Bebilderte Anleitung: weitere Firma', 'Illustrated guide: another company')}</a>
          ) : null}
          {legalEntityFormOpen ? (
            <form className="legal-entity-form" onSubmit={createLegalEntity}>
              <p className="form-field-explanation full-width">{ui('Pflichtfelder und optionale Angaben sind direkt gekennzeichnet.', 'Required and optional fields are labelled directly.')}</p>
              <label>{ui('Profilart (Pflichtfeld)', 'Profile type (required)')}<select required value={legalEntityDraft.entity_type} onChange={(event) => setLegalEntityDraft((current) => ({ ...current, entity_type: event.target.value }))}>
                <option value="company">{ui('Gegründete Gesellschaft', 'Registered company')}</option>
                <option value="sole_proprietor">{ui('Einzelunternehmen', 'Sole proprietorship')}</option>
                <option value="formation">{ui('Gründung', 'Formation')}</option>
                <option value="private">{ui('Privatprofil', 'Private profile')}</option>
              </select></label>
              <label>{newEntityNeedsLegalName ? ui('Vollständiger Firmenname (Pflichtfeld)', 'Full legal name (required)') : ui('Vollständiger Firmenname (optional)', 'Full legal name (optional)')}<input required={newEntityNeedsLegalName} value={legalEntityDraft.legal_name} onChange={(event) => setLegalEntityDraft((current) => ({ ...current, legal_name: event.target.value }))} /></label>
              <label>{ui('Rechtsform (optional)', 'Legal form (optional)')}<input value={legalEntityDraft.legal_form} onChange={(event) => setLegalEntityDraft((current) => ({ ...current, legal_form: event.target.value }))} /></label>
              <label>{ui('Sitz (optional)', 'Registered office (optional)')}<input value={legalEntityDraft.registered_office} onChange={(event) => setLegalEntityDraft((current) => ({ ...current, registered_office: event.target.value }))} /></label>
              <label>{newEntityIsRegisteredCompany ? ui('Registergericht (Pflichtfeld)', 'Register court (required)') : ui('Registergericht (optional)', 'Register court (optional)')}<input required={newEntityIsRegisteredCompany} value={legalEntityDraft.register_court} onChange={(event) => setLegalEntityDraft((current) => ({ ...current, register_court: event.target.value }))} /></label>
              <label>{newEntityIsRegisteredCompany ? ui('Registernummer (Pflichtfeld)', 'Registration number (required)') : ui('Registernummer (optional)', 'Registration number (optional)')}<input required={newEntityIsRegisteredCompany} value={legalEntityDraft.registration_number} onChange={(event) => setLegalEntityDraft((current) => ({ ...current, registration_number: event.target.value }))} /></label>
              <label>{ui('Land (ISO, Pflichtfeld)', 'Country (ISO, required)')}<input required pattern="[A-Za-z]{2}" maxLength="2" value={legalEntityDraft.country_code} onChange={(event) => setLegalEntityDraft((current) => ({ ...current, country_code: event.target.value.toUpperCase() }))} /></label>
              <button type="submit" className="primary-button" disabled={legalEntitySaving || !hasApprovedAccountIdentity}>{legalEntitySaving ? ui('Wird angelegt …', 'Creating …') : ui('Unternehmensprofil anlegen', 'Create company profile')}</button>
            </form>
          ) : null}
        </section>
        ) : null}

        {activeTab === 'overview' && (
          <>
            <h2>{ui('Willkommen in Ihrem Business Hub', 'Welcome to your Business Hub')}</h2>
            <div className="customer-overview-layout">
            {submittedFoundingRequest ? (
              <section className="info-box" aria-labelledby="founding-billing-address-heading">
                <strong id="founding-billing-address-heading">{ui('Gründung abgeschlossen: Rechnungsadresse prüfen', 'Formation completed: review billing address')}</strong>
                <p>{ui('Ihre neue Firmenanschrift wird nicht automatisch übernommen. Prüfen und bestätigen Sie die Rechnungsadresse selbst.', 'Your new company address is not applied automatically. Review and confirm your billing address yourself.')}</p>
                <button type="button" className="primary-link-button" onClick={() => setActiveTab('company')}>{ui('Rechnungsadresse öffnen', 'Open billing address')}</button>
              </section>
            ) : null}
            <section className={`customer-notice-center${dashboardNotices.length ? '' : ' is-empty'}`} aria-labelledby="customer-notice-center-heading">
              <div className="customer-notice-center__header">
                <div>
                  <span className="customer-onboarding__eyebrow">{ui('Post & Zahlungen', 'Mail & payments')}</span>
                  <h3 id="customer-notice-center-heading">{ui('Das ist jetzt für Sie wichtig', 'What matters now')}</h3>
                </div>
                <span className={`status-pill ${dashboardNotices.length ? 'status-pill--warning' : 'status-pill--success'}`}>
                  {dashboardNotices.length
                    ? ui(`${dashboardNotices.length} offen`, `${dashboardNotices.length} open`)
                    : ui('Nichts offen', 'Nothing pending')}
                </span>
              </div>
              {dashboardNotices.length ? (
                <ol className="customer-notice-center__list">
                  {dashboardNotices.map((notice) => (
                    <li className={`customer-notice customer-notice--${notice.category}`} key={notice.id}>
                      <div>
                        <strong>{notice.category === 'payment' ? ui('Zahlung', 'Payment') : notice.category === 'mail' ? ui('Neue Post', 'New mail') : ui('Ihre Aktion', 'Your action')}</strong>
                        <p>{dashboardNoticeCopy[notice.messageKey](notice.count)}</p>
                      </div>
                      <button type="button" className="primary-link-button" onClick={() => setActiveTab(notice.actionTab)}>
                        {dashboardNoticeActions[notice.actionTab]}
                      </button>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="customer-notice-center__empty">{ui('Es gibt derzeit keine neue Post, offene Zahlung oder erforderliche Kundenaktion.', 'There is currently no new mail, pending payment or required customer action.')}</p>
              )}
            </section>
            {(dashboard.business_addresses || []).filter(inSelectedEntity).length > 0 ? (
              <section className="business-address-panel" aria-labelledby="business-address-heading">
                <div>
                  <span className="business-address-panel__eyebrow">{ui('Ihre ladungsfähige Anschrift', 'Your serviceable business address')}</span>
                  <h3 id="business-address-heading">{ui('Ihre Geschäftsadresse', 'Your business address')}</h3>
                  <p>{ui('Diese Anschrift steht Ihnen nach erfolgreicher Identitätsprüfung zur Verfügung.', 'This address is available after successful identity verification.')}</p>
                </div>
                <div className="business-address-panel__addresses">
                  {(dashboard.business_addresses || []).filter(inSelectedEntity).map((address) => {
                    const addressDocuments = findBusinessAddressDocuments(customerDocuments, address.customer_id);
                    const copied = copiedBusinessAddressId === String(address.customer_id);
                    return (
                      <article className="business-address-panel__card" key={address.customer_id}>
                        <address className="business-address-panel__address">
                          {address.display_name ? <strong>{address.display_name}</strong> : (
                            <span className="business-address-panel__name-note">
                              {ui(
                                'Die Namenszeile erscheint, sobald Ihre Firmen- oder Namensfreigabe abgeschlossen ist.',
                                'The name line will appear once your company or name approval is complete.'
                              )}
                            </span>
                          )}
                          <span>{address.street}</span>
                          <span>{address.postal_code} {address.city}</span>
                          <span>{address.country}</span>
                        </address>
                        <div className="business-address-panel__actions">
                          <button type="button" className="secondary-button" onClick={() => copyBusinessAddress(address)}>
                            {copied ? ui('Adresse kopiert', 'Address copied') : ui('Adresse kopieren', 'Copy address')}
                          </button>
                          {addressDocuments.addressConfirmation ? (
                            <button
                              type="button"
                              className="secondary-button"
                              disabled={downloadingDocumentId === addressDocuments.addressConfirmation.id}
                              onClick={() => downloadCustomerDocument(addressDocuments.addressConfirmation)}
                            >
                              {ui('Adressbestätigung', 'Address confirmation')}
                            </button>
                          ) : null}
                          {addressDocuments.usageAgreement ? (
                            <button
                              type="button"
                              className="secondary-button"
                              disabled={downloadingDocumentId === addressDocuments.usageAgreement.id}
                              onClick={() => downloadCustomerDocument(addressDocuments.usageAgreement)}
                            >
                              {ui('Nutzungsvereinbarung', 'Usage agreement')}
                            </button>
                          ) : null}
                        </div>
                        <span className="sr-only" aria-live="polite">{copied ? ui('Geschäftsadresse wurde kopiert.', 'Business address copied.') : ''}</span>
                      </article>
                    );
                  })}
                </div>
              </section>
            ) : null}
            <div className="customer-overview-google"><GoogleReviewSummary compact /></div>
            <div className="customer-dashboard-grid">
              <article className="customer-dashboard-card">
                <h3>{ui('Briefkasten', 'Mailbox')}</h3>
                {primaryDisplayProfile?.logo_url ? (
                  <CustomerDisplayLogo logoUrl={primaryDisplayProfile?.logo_url} />
                ) : null}
                <p>{primaryDisplayProfile?.display_name || ui('Legen Sie fest, welcher Name an Ihrem Briefkasten erscheinen soll.', 'Choose the name you would like to appear on your mailbox.')}</p>
                <button type="button" className="primary-link-button" onClick={() => setActiveTab('display')}>
                  {ui('Briefkasten bearbeiten', 'Edit mailbox')}
                </button>
              </article>
              <article className="customer-dashboard-card">
                <h3>{ui('Kundendaten', 'Account details')}</h3>
                <p>{hasPendingCompanyRequest ? ui('Ihre Änderung wird derzeit geprüft.', 'Your changes are currently being reviewed.') : ui('Halten Sie Ihre persönlichen und geschäftlichen Angaben aktuell.', 'Keep your personal and business details up to date.')}</p>
                {hasPendingCompanyRequest ? <CustomerStatusBadge status="in_review" /> : null}
                <button type="button" className="primary-link-button" onClick={() => setActiveTab('company')}>
                  {ui('Kundendaten aktualisieren', 'Update account details')}
                </button>
              </article>
              <article className="customer-dashboard-card">
                <h3>{ui('Abonnements', 'Subscriptions')}</h3>
                <p>{dashboard.subscriptions_loading
                  ? ui('Abonnementstatus wird im Hintergrund geladen …', 'Subscription status is loading in the background …')
                  : dashboard.subscription_error ? ui('Abonnementstatus vorübergehend nicht vollständig verfügbar.', 'Subscription status temporarily incomplete.')
                  : ui(`${countText(subscriptions.length, 'Abonnement', 'Abonnements', 'subscription', 'subscriptions')} in Ihrem Business Hub.`, `${countText(subscriptions.length, 'Abonnement', 'Abonnements', 'subscription', 'subscriptions')} in your Business Hub.`)}</p>
                {subscriptions[0] ? <SubscriptionStatusBadge status={subscriptions[0].status} /> : null}
                <button type="button" className="primary-link-button" onClick={() => setActiveTab('subscriptions')}>
                  {ui('Abonnements verwalten', 'Manage subscriptions')}
                </button>
              </article>
              <article className="customer-dashboard-card">
                <h3>{ui('Posteingang', 'Inbox')}</h3>
                <p>{mailItems.length > 0
                  ? ui(`${countText(mailItems.length, 'neues Poststück', 'neue Poststücke', 'new mail item', 'new mail items')} ${mailItems.length === 1 ? 'wartet' : 'warten'} auf Sie.`, `${countText(mailItems.length, 'neues Poststück', 'neue Poststücke', 'new mail item', 'new mail items')} ${mailItems.length === 1 ? 'is' : 'are'} waiting for you.`)
                    : dashboard.subscriptions_loading || dashboard.subscription_error || !dashboard.mailbox?.mailboxes?.length || dashboard.mailbox.mailboxes.some((box) => box.error)
                      ? ui('Der Postbestand ist derzeit nicht vollständig geprüft. Bitte öffnen Sie den Posteingang für den aktuellen Status.', 'Your mail inventory is not fully checked at present. Please open the inbox for its current status.')
                      : ui('Sie haben derzeit keine neue Post.', 'You currently have no new mail.')}</p>
                <button type="button" className="primary-link-button" onClick={() => setActiveTab('mailbox')}>
                  {ui('Posteingang öffnen', 'Open inbox')}
                </button>
              </article>
              <article className="customer-dashboard-card">
                <h3>{ui('Zusatzleistungen', 'Additional services')}</h3>
                <p>{ui(
                  `${countText(logisticsRequests.length + mailForwardingRequests.length, 'Auftrag', 'Aufträge', 'service request', 'service requests')} in Ihrem Business Hub.`,
                  `${countText(logisticsRequests.length + mailForwardingRequests.length, 'Auftrag', 'Aufträge', 'service request', 'service requests')} in your Business Hub.`
                )}</p>
                {additionalServicesAttentionCount > 0 ? (
                  <span className="status-pill status-pill--warning">
                    {ui(
                      `${countText(additionalServicesAttentionCount, 'Zahlung', 'Zahlungen', 'payment', 'payments')} offen`,
                      `${countText(additionalServicesAttentionCount, 'Zahlung', 'Zahlungen', 'payment', 'payments')} open`
                    )}
                  </span>
                ) : null}
                {logisticsRequests[0] ? <LogisticsStatusBadge status={logisticsRequests[0].status} /> : null}
                <button type="button" className="primary-link-button" onClick={() => setActiveTab('logistics')}>
                  {ui('Zusatzleistungen öffnen', 'Open additional services')}
                </button>
              </article>
            </div>
            </div>
          </>
        )}

        {activeTab === 'guides' && <CustomerGuidanceCenter language={getLang()} onTabChange={setActiveTab} />}

        {activeTab === 'mailbox' && (
          <>
            <h2>{ui('Posteingang', 'Inbox')}</h2>
            <p>{ui(
              'Ihre neue Post steht hier sicher digital für Sie bereit. Wählen Sie ein oder mehrere Poststücke aus, wenn Sie die Originale zugesendet oder vernichtet haben möchten.',
              'Your new mail is available here securely in digital form. Select one or more items if you would like the originals forwarded or destroyed.'
            )}</p>
            {nextcloudConnections.some((connection) => connection.configured) ? (
              <div className="nextcloud-connection-list">
                {nextcloudConnections.filter((connection) => connection.configured).map((connection) => {
                  const customer = (dashboard.customers || []).find((item) => Number(item.id) === Number(connection.customer_id)) || {};
                  return (
                    <div className={`nextcloud-connection-card ${connection.connected ? 'is-connected' : ''}`} key={`nextcloud-${connection.customer_id}-${connection.legal_entity_id || 'legacy'}`}>
                      <div>
                        <strong>{customer.company_name || customer.name || customer.email || ui('Nextcloud-Postfach', 'Nextcloud mailbox')}</strong>
                        <p className="field-hint">{connection.access_mode === 'server_bound'
                          ? ui('Ihr Postfach ist sicher mit diesem Unternehmensprofil verbunden. Eine zusätzliche Nextcloud-Anmeldung ist nicht erforderlich.', 'Your mailbox is securely linked to this company profile. An additional Nextcloud sign-in is not required.')
                          : connection.connected
                          ? ui(`Verbunden mit ${connection.nextcloud_user_id || 'Nextcloud'}. Der Posteingang bleibt auch bei späteren App-Sitzungen freigeschaltet.`, `Connected to ${connection.nextcloud_user_id || 'Nextcloud'}. The inbox remains available in later app sessions.`)
                          : ui('Melden Sie sich einmalig direkt bei Nextcloud an, um dieses persönliche Postfach in der App freizuschalten.', 'Sign in directly to Nextcloud once to unlock this personal mailbox in the app.')
                        }</p>
                      </div>
                      {connection.access_mode === 'server_bound' ? null : connection.connected ? (
                        <button type="button" className="action-button action-button--secondary" disabled={nextcloudConnectingId === `${connection.customer_id}:${connection.legal_entity_id || 'legacy'}`} onClick={() => disconnectNextcloud(connection)}>
                          {ui('Nextcloud trennen', 'Disconnect Nextcloud')}
                        </button>
                      ) : (
                        <button type="button" className="action-button" disabled={nextcloudConnectingId === `${connection.customer_id}:${connection.legal_entity_id || 'legacy'}`} onClick={() => connectNextcloud(connection)}>
                          {nextcloudConnectingId === `${connection.customer_id}:${connection.legal_entity_id || 'legacy'}` ? ui('Anmeldung läuft...', 'Signing in...') : ui('Mit Nextcloud verbinden', 'Connect Nextcloud')}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : null}
            {dashboard.access_restrictions?.post_and_contract_access_deactivated ? (
              <div className="error-message" role="alert">{ui(
                'Ihr Zugriff auf Post und Vertragsdokumente wurde nach Vertragsende deaktiviert. Ihre Daten wurden nicht gelöscht. Bitte kontaktieren Sie uns, wenn wieder ein aktives Abonnement besteht.',
                'Your access to mail and contract documents was deactivated after the contract ended. Your data has not been deleted. Please contact us if an active subscription exists again.'
              )}</div>
            ) : dashboard.subscriptions_loading ? (
              <div className="info-box" role="status">{ui('Abonnementstatus und Postfachberechtigung werden im Hintergrund geladen …', 'Subscription status and mailbox access are loading in the background …')}</div>
            ) : dashboard.subscription_error ? (
              <div className="hint-box" role="status">
                <p>{ui('Ihr Abonnementstatus ist vorübergehend nicht prüfbar. Das bedeutet nicht, dass Ihr Abo beendet oder Ihr Posteingang leer ist.', 'Your subscription status is temporarily unavailable. This does not mean your subscription has ended or your inbox is empty.')}</p>
                <button type="button" className="action-button" disabled={detailsLoading} onClick={reloadDashboard}>{ui('Status erneut prüfen', 'Retry status check')}</button>
              </div>
            ) : !dashboard.mailbox?.active_subscription ? (
              <div className="hint-box">{ui('Für Postaktionen ist ein aktives Abonnement erforderlich.', 'An active subscription is required for mail actions.')}</div>
            ) : null}
            {dashboard.subscription_source === 'confirmed_snapshot' ? (
              <div className="info-box" role="status">{ui('Seal ist vorübergehend nicht erreichbar. Ihr Postzugang verwendet den letzten bestätigten, noch gültigen Prüfstand. Es wird keine zusätzliche Laufzeit gewährt.', 'Seal is temporarily unavailable. Mail access uses the last confirmed, still valid subscription record. No additional subscription time is granted.')}</div>
            ) : null}
            {mailMessage ? (
              <div className="success-box mail-forwarding-created">
                <strong>{mailMessage}</strong>
                {mailMessageIsOrder ? <>
                <p>{ui(
                  'Der Auftrag und alle weiteren Zahlungen sind jetzt unter Zusatzleistungen gespeichert.',
                  'The request and all further payments are now stored under Additional services.'
                )}</p>
                <div className="action-row">
                  {mailCheckoutUrl ? (
                    <a className="primary-link-button" href={mailCheckoutUrl}>
                      {ui('Servicegebühr jetzt bezahlen', 'Pay service fee now')}
                    </a>
                  ) : null}
                  <button type="button" className="action-button action-button--secondary" onClick={() => setActiveTab('logistics')}>
                    {ui('Unter Zusatzleistungen ansehen', 'View under Additional services')}
                  </button>
                </div>
                </> : null}
              </div>
            ) : null}
            {(dashboard.mailbox?.mailboxes || []).some((mailbox) => mailbox.error) ? (
              <div className="hint-box">{ui('Ein Posteingang ist momentan nicht erreichbar. Bitte versuchen Sie es in wenigen Minuten erneut.', 'One inbox is temporarily unavailable. Please try again in a few minutes.')}</div>
            ) : null}
            {mailItems.length === 0 && (dashboard.subscriptions_loading || dashboard.subscription_error || (dashboard.mailbox?.mailboxes || []).some((box) => box.error)) ? null : mailItems.length === 0 ? (
              <div className="info-box">{nextcloudConnections.some((connection) => connection.configured && !connection.connected)
                ? ui('Der Posteingang wird erst nach der persönlichen Nextcloud-Anmeldung angezeigt.', 'The inbox is shown only after personal Nextcloud sign-in.')
                : !dashboard.mailbox?.mailboxes?.length
                  ? ui('Der Postbestand konnte noch nicht bestätigt werden. Bitte wenden Sie sich bei fehlendem Zugang an uns.', 'Your mail inventory has not yet been confirmed. Please contact us if access is missing.')
                  : ui('Aktuell befinden sich keine neuen Poststücke in Ihrem Posteingang.', 'There are currently no new mail items in your inbox.')
              }</div>
            ) : (
              <div className="customer-mailbox-layout">
                <div className="customer-mailbox-list">
                  <div className="customer-mailbox-actions">
                    <button type="button" className="action-button action-button--secondary" disabled={mailActionWorking || selectedMailIds.length === 0} onClick={() => submitMailAction('defer')}>{ui('Auswahl später bearbeiten', 'Handle selection later')}</button>
                    <button type="button" className="action-button" disabled={mailActionWorking || selectedMailIds.length === 0} onClick={openMailForwardingForm}>{ui('Originale per Post erhalten', 'Receive originals by mail')}</button>
                    <button type="button" className="action-button action-button--danger" disabled={mailActionWorking || selectedMailIds.length === 0} onClick={() => submitMailAction('destroy_original')}>{ui('Originale vernichten lassen', 'Request destruction of originals')}</button>
                  </div>
                  {mailForwardingFormOpen ? (
                    <div className="mail-forwarding-step" role="dialog" aria-modal="true" aria-labelledby="mail-forwarding-title">
                    <form
                      className="mail-forwarding-form mail-forwarding-form--large"
                      onSubmit={async (event) => {
                        event.preventDefault();
                        try {
                          if (saveMailForwardingAddress) {
                            await createAddressBookEntry({
                              address: mailForwardingAddress,
                              label: mailForwardingAddressLabel,
                            });
                            setSaveMailForwardingAddress(false);
                          }
                          await submitMailAction('physical_forwarding', mailForwardingAddress);
                        } catch (submitError) {
                          setError(submitError.message || ui('Versandauftrag konnte nicht vorbereitet werden.', 'Could not prepare forwarding request.'));
                        }
                      }}
                    >
                      <div className="mail-forwarding-form__header">
                        <div>
                          <span className="eyebrow">{ui('Versandauftrag prüfen', 'Review forwarding request')}</span>
                          <h2 id="mail-forwarding-title">{ui('Wohin dürfen wir Ihre Originale senden?', 'Where should we send your originals?')}</h2>
                          <p className="field-hint">{ui(
                            'Prüfen Sie die Anschrift sorgfältig. Nach vollständiger Zahlung versenden wir die ausgewählten Originale an diese Adresse.',
                            'Please check the address carefully. Once payment is complete, we will send the selected originals to this address.'
                          )}</p>
                        </div>
                        <button type="button" className="action-button action-button--secondary" onClick={() => setMailForwardingFormOpen(false)}>
                          {ui('Abbrechen', 'Cancel')}
                        </button>
                      </div>
                      <section className="mail-forwarding-selection">
                        <div>
                          <strong>{countText(selectedMailIds.length, 'ausgewähltes Dokument', 'ausgewählte Dokumente', 'selected document', 'selected documents')}</strong>
                          <p>{mailItems
                            .filter((item) => selectedMailIds.includes(item.id))
                            .map((item) => item.filename)
                            .join(' · ')}</p>
                        </div>
                        <button type="button" className="secondary-button" onClick={() => setMailForwardingFormOpen(false)}>
                          {ui('Auswahl ändern', 'Change selection')}
                        </button>
                      </section>
                      <div className="form-grid">
                        {addresses.length > 0 ? (
                          <label className="full-width">
                            {ui('Gespeicherte Adresse verwenden', 'Use a saved address')}
                            <select
                              defaultValue=""
                              onChange={(event) => {
                                const address = addresses.find((item) => String(item.id) === event.target.value);
                                if (address) setMailForwardingAddress(shippingFieldsFromAddress(address));
                              }}
                            >
                              <option value="">{ui('Bitte auswählen', 'Please select')}</option>
                              {addresses.map((address) => (
                                <option key={address.id} value={address.id}>
                                  {address.label}{address.is_default ? ui(' (Standard)', ' (default)') : ''}
                                </option>
                              ))}
                            </select>
                          </label>
                        ) : null}
                        <label>
                          {ui('Empfängername', 'Recipient name')}
                          <input required autoComplete="name" value={mailForwardingAddress.recipient_name} onChange={(event) => setMailForwardingAddress((current) => ({ ...current, recipient_name: event.target.value }))} />
                        </label>
                        <label>
                          {ui('Firma (optional)', 'Company (optional)')}
                          <input autoComplete="organization" value={mailForwardingAddress.recipient_company} onChange={(event) => setMailForwardingAddress((current) => ({ ...current, recipient_company: event.target.value }))} />
                        </label>
                        <label className="full-width">
                          {ui('Straße und Hausnummer', 'Street and house number')}
                          <input required autoComplete="street-address" value={mailForwardingAddress.recipient_street} onChange={(event) => setMailForwardingAddress((current) => ({ ...current, recipient_street: event.target.value }))} />
                        </label>
                        <label className="full-width">
                          {ui('Adresszusatz (optional)', 'Address line 2 (optional)')}
                          <input value={mailForwardingAddress.recipient_address_extra} onChange={(event) => setMailForwardingAddress((current) => ({ ...current, recipient_address_extra: event.target.value }))} />
                        </label>
                        <label>
                          {ui('PLZ', 'Postal code')}
                          <input required autoComplete="postal-code" value={mailForwardingAddress.recipient_zip} onChange={(event) => setMailForwardingAddress((current) => ({ ...current, recipient_zip: event.target.value }))} />
                        </label>
                        <label>
                          {ui('Ort', 'City')}
                          <input required autoComplete="address-level2" value={mailForwardingAddress.recipient_city} onChange={(event) => setMailForwardingAddress((current) => ({ ...current, recipient_city: event.target.value }))} />
                        </label>
                        <label>
                          {ui('Land', 'Country')}
                          <input required autoComplete="country-name" value={mailForwardingAddress.recipient_country} onChange={(event) => setMailForwardingAddress((current) => ({ ...current, recipient_country: event.target.value }))} />
                        </label>
                        <label>
                          {ui('Telefonnummer', 'Phone number')}
                          <input type="tel" required autoComplete="tel" value={mailForwardingAddress.recipient_phone} onChange={(event) => setMailForwardingAddress((current) => ({ ...current, recipient_phone: event.target.value }))} />
                        </label>
                        <label className="full-width checkbox-row mail-forwarding-save-address">
                          <input
                            type="checkbox"
                            checked={saveMailForwardingAddress}
                            onChange={(event) => setSaveMailForwardingAddress(event.target.checked)}
                          />
                          <span>{ui('Diese Adresse zusätzlich im zentralen Adressbuch speichern', 'Also save this address in the central address book')}</span>
                        </label>
                        {saveMailForwardingAddress ? (
                          <label className="full-width">
                            {ui('Name der Adresse im Adressbuch', 'Address book label')}
                            <input
                              required
                              placeholder={ui('z. B. Büro Berlin', 'e.g. Berlin office')}
                              value={mailForwardingAddressLabel}
                              onChange={(event) => setMailForwardingAddressLabel(event.target.value)}
                            />
                          </label>
                        ) : null}
                      </div>
                      <div className="mail-forwarding-form__summary">
                        <span>{ui(
                          countText(selectedMailIds.length, 'Poststück', 'Poststücke', 'mail item', 'mail items'),
                          countText(selectedMailIds.length, 'Poststück', 'Poststücke', 'mail item', 'mail items')
                        )}</span>
                        <strong>{ui(
                          `${(mailForwardingFeeCents(dashboard.mailbox?.forwarding_pricing, selectedMailIds.length) / 100).toFixed(2)} EUR Servicegebühr + tatsächliches Porto`,
                          `${(mailForwardingFeeCents(dashboard.mailbox?.forwarding_pricing, selectedMailIds.length) / 100).toFixed(2)} EUR service fee + actual postage`
                        )}</strong>
                      </div>
                      <button className="primary-button" type="submit" disabled={mailActionWorking}>
                        {mailActionWorking ? ui('Versand wird vorbereitet...', 'Preparing your request...') : ui('Versand beauftragen und sicher bezahlen', 'Request forwarding and pay securely')}
                      </button>
                    </form>
                    </div>
                  ) : null}
                  {mailItems.map((item) => (
                    <div
                      key={`${item.customer_id}:${item.id}`}
                      className={`customer-mailbox-list__item ${selectedMail?.id === item.id ? 'is-active' : ''}`}
                    >
                      <input type="checkbox" aria-label={`${item.filename} auswählen`} checked={selectedMailIds.includes(item.id)} onChange={() => setSelectedMailIds((current) => {
                        if (current.includes(item.id)) return current.filter((id) => id !== item.id);
                        const sameMailbox = mailItems.filter((mailItem) => current.includes(mailItem.id)).every((mailItem) => mailItem.customer_id === item.customer_id);
                        return sameMailbox ? [...current, item.id] : [item.id];
                      })} />
                      <button type="button" className="customer-mailbox-file-link" onClick={() => setSelectedMailId(item.id)}><strong>{item.filename}</strong></button>
                      <span>{formatDateTime(item.modified_at)}</span>
                    </div>
                  ))}
                </div>
                <div className="customer-mailbox-detail">
                  {selectedMail ? (
                    <>
                      <CustomerMailPreview item={selectedMail} />
                      <h3>{selectedMail.filename}</h3>
                      <p className="field-hint">{ui(
                        'Nach Ihrer Beauftragung verschwinden die ausgewählten Poststücke aus diesem Posteingang. Ihren Auftrag und den aktuellen Status können Sie weiterhin in Ihrem Business Hub verfolgen.',
                        'Once requested, the selected items will leave this inbox. You can continue to track your request and its current status in your account.'
                      )}</p>
                    </>
                  ) : null}
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === 'documents' && (
          <section>
            <div className="customer-section-heading">
              <div>
                <h2>{ui('Ihre Dokumente', 'Your documents')}</h2>
                <p>{ui('Hier finden Sie Ihre Nutzungsvereinbarung, Address Confirmation und künftig weitere Vertragsdokumente.', 'Find your usage agreement, address confirmation and future contract documents here.')}</p>
              </div>
            </div>
            {customerDocumentsError ? <div className="hint-box">{customerDocumentsError}</div> : null}
            <AuthorizedRecipientHub locale={getLang()} eligibility={recipientEligibility} defaults={recipientDefaults} services={recipientServices} authToken={readCustomerToken()} onChange={setRecipientServices} onDownload={downloadRecipientDocument} onStatus={updateRecipientStatus} />
            {dashboard.access_restrictions?.post_and_contract_access_deactivated ? (
              <div className="error-message" role="alert">{ui(
                'Ihr Zugriff auf Vertragsdokumente wurde nach Vertragsende deaktiviert. Die Dokumente bleiben gespeichert.',
                'Your access to contract documents was deactivated after the contract ended. The documents remain stored.'
              )}</div>
            ) : null}
            {customerDocumentsLoading ? <div className="info-box">{ui('Dokumente werden geladen …', 'Loading documents …')}</div> : null}
            {!customerDocumentsLoading && scopedCustomerDocuments.length === 0 ? (
              <div className="info-box">{ui('Aktuell sind noch keine Dokumente hinterlegt.', 'No documents are available yet.')}</div>
            ) : (
              <div className="customer-dashboard-grid">
              {scopedCustomerDocuments.map((document) => (
                  <article className="customer-dashboard-card" key={`${document.customer_id}-${document.id}`}>
                    <h3>{document.filename}</h3>
                    <p>{document.modified_at ? new Date(document.modified_at).toLocaleDateString() : ui('Vertragsdokument', 'Contract document')}</p>
                    <button
                      type="button"
                      className="primary-button"
                      disabled={downloadingDocumentId === document.id}
                      onClick={() => downloadCustomerDocument(document)}
                    >
                      {downloadingDocumentId === document.id ? ui('Wird geladen …', 'Downloading …') : ui('PDF herunterladen', 'Download PDF')}
                    </button>
                  </article>
                ))}
              </div>
            )}
          </section>
        )}

        {activeTab === 'verifications' && (
          <>
            <h2>{ui('Verifizierung', 'Verification')}</h2>
            {verifications.length === 0 ? (
              <p>{ui('Für diesen Business Hub liegt noch keine Verifizierung vor.', 'There is no verification for this Business Hub yet.')}</p>
            ) : (
              <div className="customer-dashboard-grid">
                {verifications.map((verification) => {
                  const title = verification.orders?.shopify_order_id ||
                    verification.customers?.company_name ||
                    verification.customers?.name ||
                    `Verifizierung #${verification.id}`;
                  const needsFollowup = ['additional_requested'].includes(normalizeText(verification.identity_status).toLowerCase()) ||
                    ['pending', 'additional_requested'].includes(normalizeText(verification.business_status).toLowerCase());

                  return (
                    <article className="customer-dashboard-card" key={verification.id}>
                      <h3>{title}</h3>
                      <p>{verification.orders?.product_name || customerTypeLabel(verification.customers?.customer_type)}</p>
                      <div className="status-grid status-grid--stack-mobile">
                        <div className="status-box">
                          <span>{ui('Identität', 'Identity')}</span>
                          <CustomerStatusBadge status={verification.identity_status} />
                        </div>
                        <div className="status-box">
                          <span>{ui('Unternehmen', 'Business')}</span>
                          <CustomerStatusBadge status={verification.business_status} />
                        </div>
                      </div>
                      <Link className="primary-link-button" to={`/portal/${encodeURIComponent(verification.token)}`}>
                        {needsFollowup ? ui('Unterlagen ergänzen', 'Submit requested documents') : ui('Verifizierung ansehen', 'View verification')}
                      </Link>
                    </article>
                  );
                })}
              </div>
            )}
          </>
        )}

        {activeTab === 'addresses' && (
          <div className="card-section">
            <h2>{ui('Adressbuch', 'Address book')}</h2>
            <p>{ui(
              'Speichern Sie häufig verwendete Versandadressen und übernehmen Sie sie künftig mit einem Klick in Ihre Aufträge. Bereits erteilte Aufträge bleiben unverändert.',
              'Save frequently used shipping addresses and add them to future requests with one click. Existing requests remain unchanged.'
            )}</p>
            {addressMessage ? <div className="success-box">{addressMessage}</div> : null}
            {addresses.length > 0 ? (
              <div className="address-book-grid">
                {addresses.map((address) => (
                  <article className="customer-dashboard-card" key={address.id}>
                    <h3>{address.label}{address.is_default ? ui(' · Standard', ' · Default') : ''}</h3>
                    <p>
                      <strong>{address.recipient_name}</strong><br />
                      {address.recipient_company ? <>{address.recipient_company}<br /></> : null}
                      {address.recipient_street}<br />
                      {address.recipient_address_extra ? <>{address.recipient_address_extra}<br /></> : null}
                      {address.recipient_zip} {address.recipient_city}<br />
                      {address.recipient_country}<br />
                      {address.recipient_phone}
                    </p>
                    <div className="customer-mailbox-actions">
                      <button type="button" className="action-button action-button--secondary" onClick={() => editAddress(address)}>
                        {ui('Bearbeiten', 'Edit')}
                      </button>
                      <button type="button" className="action-button action-button--danger" disabled={addressWorking} onClick={() => deleteAddress(address)}>
                        {ui('Löschen', 'Delete')}
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            ) : <div className="info-box">{ui('Speichern Sie Ihre erste Versandadresse, um künftige Aufträge schneller auszufüllen.', 'Save your first shipping address to complete future requests more quickly.')}</div>}
            <form className="form-grid address-book-form" onSubmit={saveAddress}>
              <h3 className="full-width">{editingAddressId ? ui('Adresse bearbeiten', 'Edit address') : ui('Neue Adresse', 'New address')}</h3>
              <label className="full-width">
                {ui('Bezeichnung, z. B. Zuhause oder Büro', 'Label, e.g. Home or Office')}
                <input required value={addressDraft.label} onChange={(event) => setAddressDraft((current) => ({ ...current, label: event.target.value }))} />
              </label>
              <label>
                {ui('Empfängername', 'Recipient name')}
                <input required autoComplete="name" value={addressDraft.recipient_name} onChange={(event) => setAddressDraft((current) => ({ ...current, recipient_name: event.target.value }))} />
              </label>
              <label>
                {ui('Firma (optional)', 'Company (optional)')}
                <input autoComplete="organization" value={addressDraft.recipient_company} onChange={(event) => setAddressDraft((current) => ({ ...current, recipient_company: event.target.value }))} />
              </label>
              <label className="full-width">
                {ui('Straße und Hausnummer', 'Street and house number')}
                <input required autoComplete="street-address" value={addressDraft.recipient_street} onChange={(event) => setAddressDraft((current) => ({ ...current, recipient_street: event.target.value }))} />
              </label>
              <label className="full-width">
                {ui('Adresszusatz (optional)', 'Address line 2 (optional)')}
                <input value={addressDraft.recipient_address_extra} onChange={(event) => setAddressDraft((current) => ({ ...current, recipient_address_extra: event.target.value }))} />
              </label>
              <label>
                {ui('PLZ', 'Postal code')}
                <input required autoComplete="postal-code" value={addressDraft.recipient_zip} onChange={(event) => setAddressDraft((current) => ({ ...current, recipient_zip: event.target.value }))} />
              </label>
              <label>
                {ui('Ort', 'City')}
                <input required autoComplete="address-level2" value={addressDraft.recipient_city} onChange={(event) => setAddressDraft((current) => ({ ...current, recipient_city: event.target.value }))} />
              </label>
              <label>
                {ui('Land', 'Country')}
                <input required autoComplete="country-name" value={addressDraft.recipient_country} onChange={(event) => setAddressDraft((current) => ({ ...current, recipient_country: event.target.value }))} />
              </label>
              <label>
                {ui('Telefonnummer', 'Phone number')}
                <input type="tel" required autoComplete="tel" value={addressDraft.recipient_phone} onChange={(event) => setAddressDraft((current) => ({ ...current, recipient_phone: event.target.value }))} />
              </label>
              <label className="checkbox-card full-width">
                <input type="checkbox" checked={addressDraft.is_default} onChange={(event) => setAddressDraft((current) => ({ ...current, is_default: event.target.checked }))} />
                <span>{ui('Als Standardadresse verwenden', 'Use as default address')}</span>
              </label>
              <div className="customer-mailbox-actions full-width">
                <button type="submit" className="primary-button" disabled={addressWorking}>
                  {addressWorking ? ui('Wird gespeichert...', 'Saving...') : ui('Adresse speichern', 'Save address')}
                </button>
                {editingAddressId ? (
                  <button type="button" className="action-button action-button--secondary" onClick={() => { setEditingAddressId(null); setAddressDraft(EMPTY_ADDRESS_BOOK_ENTRY); }}>
                    {ui('Abbrechen', 'Cancel')}
                  </button>
                ) : null}
              </div>
            </form>
          </div>
        )}

        {activeTab === 'subscriptions' && (
          <>
            <h2>{ui('Paket & Abrechnung', 'Plan & billing')}</h2>
            <p>{ui(
              'Hier verwalten Sie Ihr Paket, Ihre Zahlungsart und Rechnungen. Ein Paketwechsel gilt als Fortsetzung Ihres bestehenden Vertrags.',
              'Manage your plan, payment method and invoices here. A plan change continues your existing contract.'
            )}</p>
            {paymentRecoveryRequested && dashboard ? (() => {
              const target = subscriptions.find((subscription) => String(subscription.id) === String(paymentRecoverySubscriptionId));
              const action = getSubscriptionPaymentAction(target || {});
              if (target && (action === 'recover' || target.billing_provider === 'shopify_seal')) return (
                <div className="hint-box" role="status">
                  <strong>{ui('Zahlung fehlgeschlagen', 'Payment failed')}</strong><br />
                  {ui('Das betroffene Abonnement ist unten markiert. Wählen Sie dort „Zahlung jetzt klären“, um Ihre Zahlungsart sicher zu aktualisieren und den offenen Zeitraum zu bezahlen.', 'The affected subscription is marked below. Select “Resolve payment now” to securely update your payment method and pay the outstanding period.')}
                </div>
              );
              if (action === 'ended') return (
                <div className="info-box" role="status">
                  <strong>{ui('Abonnement bereits beendet', 'Subscription already ended')}</strong><br />
                  {ui('Für dieses Abonnement ist keine Zahlungsänderung mehr möglich. Sie können oben eine neue Buchung starten oder uns bei Rückfragen kontaktieren.', 'Payment details can no longer be changed for this subscription. You can start a new booking above or contact us with any questions.')}
                </div>
              );
              if (action === 'manage') return (
                <div className="info-box" role="status">
                  <strong>{ui('Zahlungsstatus wieder aktiv', 'Payment status active again')}</strong><br />
                  {ui('Für dieses Abonnement ist aktuell keine offene Zahlungsaktion mehr erforderlich.', 'No outstanding payment action is currently required for this subscription.')}
                </div>
              );
              return <div className="hint-box" role="status">{ui('Das betroffene Abonnement ist nicht mehr verfügbar. Bitte kontaktieren Sie uns, falls Sie noch eine offene Forderung sehen.', 'The affected subscription is no longer available. Please contact us if you still see an outstanding balance.')}</div>;
            })() : null}
            <section className="customer-dashboard-card new-company-booking" aria-labelledby="new-company-booking-heading">
              <h3 id="new-company-booking-heading">{ui('Neue Buchung', 'New booking')}</h3>
              <p>
                <button type="button" className="secondary-link-button" onClick={() => setActiveTab('company')}>
                  {ui('Unternehmensprofile auswählen oder verwalten', 'Select or manage company profiles')}
                </button>
              </p>
              {dashboard.subscriptions_loading || dashboard.subscription_error ? (
                <div className="hint-box" role="status">{ui(
                  'Eine neue Buchung ist gesperrt, bis der bestehende Abonnementstatus sicher geprüft wurde. Verwenden Sie „Status erneut prüfen“; Ihr vorhandenes Paket wird dadurch nicht verändert.',
                  'A new booking is locked until your existing subscription status has been checked safely. Use “Retry status check”; this does not change your current plan.'
                )}</div>
              ) : selectedLegalEntity ? (
                <>
                  <p>{ui('Ausgewähltes Unternehmen', 'Selected company')}: <strong>{legalEntityLabel(selectedLegalEntity)}</strong></p>
                  <p>{ui(
                    'Die neue Buchung, Verifizierung, Dokumente und das Postfach werden ausschließlich diesem Unternehmensprofil zugeordnet.',
                    'The new booking, verification, documents and mailbox will be assigned exclusively to this company profile.'
                  )}</p>
                  <div className="plan-choice-grid" role="radiogroup" aria-label={ui('Tarif für die neue Buchung', 'Plan for the new booking')}>
                    {BUSINESS_PLANS.map((plan) => (
                      <label className={`plan-choice-card${newBookingPlan === plan.code ? ' plan-choice-card--selected' : ''}`} key={plan.code}>
                        <span className="plan-choice-card__heading">
                          <input
                            type="radio"
                            name="new-company-booking-plan"
                            value={plan.code}
                            checked={newBookingPlan === plan.code}
                            disabled={newBookingLoading}
                            onChange={(event) => setNewBookingPlan(event.target.value)}
                          />
                          <strong>{plan.name}</strong>
                        </span>
                        <span className="plan-choice-card__price">{plan.price} <small>{ui('/ Monat', '/ month')}</small></span>
                        <ul>
                          <li>{ui('Ladungsfähige Geschäftsadresse', 'Summons-capable business address')}</li>
                          <li>{ui(`Post-Digitalisierung innerhalb von ${plan.deadline}`, `Mail digitisation within ${plan.deadlineEn}`)}</li>
                          <li>{ui('Behördlicher Empfangsservice verfügbar', 'Official-authority receiving service available')}</li>
                        </ul>
                      </label>
                    ))}
                  </div>
                  <button type="button" className="primary-button" disabled={newBookingLoading || !hasApprovedAccountIdentity || dashboard.subscriptions_loading || !!dashboard.subscription_error} onClick={startNewCompanyBooking}>
                    {newBookingLoading ? ui('Stripe wird geöffnet …', 'Opening Stripe …') : ui(`Für ${legalEntityLabel(selectedLegalEntity)} buchen`, `Book for ${legalEntityLabel(selectedLegalEntity)}`)}
                  </button>
                </>
              ) : (
                <div className="hint-box">{ui(
                  'Wählen Sie oben ausdrücklich ein Unternehmensprofil aus oder legen Sie ein neues an. Erst danach kann eine neue Buchung gestartet werden.',
                  'Explicitly select a company profile above or create a new one. A new booking can only be started afterwards.'
                )}</div>
              )}
            </section>
            {dashboard.subscriptions_loading ? (
              <div className="info-box" role="status">{ui('Abonnements werden im Hintergrund geladen …', 'Subscriptions are loading in the background …')}</div>
            ) : null}
            {dashboard.subscription_error ? (
              <div className="hint-box" role="status">
                <p>{ui('Abonnementstatus vorübergehend nicht verfügbar. Bitte versuchen Sie es erneut.', 'Subscription status temporarily unavailable. Please try again.')}</p>
                <button type="button" className="action-button" disabled={detailsLoading} onClick={reloadDashboard}>{ui('Status erneut prüfen', 'Retry status check')}</button>
              </div>
            ) : null}
            {checkoutPreparation ? (
              <div className="checkout-preparation" role="status" aria-live="polite">
                <div className="checkout-preparation__bar" aria-hidden="true"><span /></div>
                <strong>{checkoutPreparationStage(checkoutPreparationSeconds) === 'request'
                  ? ui('Anfrage wird sicher übermittelt …', 'Sending the request securely …')
                  : checkoutPreparationStage(checkoutPreparationSeconds) === 'provider'
                    ? ui('Aktueller Vertrag und Zahlungsstatus werden geprüft …', 'Checking the current contract and payment status …')
                    : checkoutPreparationStage(checkoutPreparationSeconds) === 'contract'
                      ? ui('Tarif, Unternehmen und Abrechnungstermin werden abgeglichen …', 'Checking the plan, company and billing date …')
                      : checkoutPreparationStage(checkoutPreparationSeconds) === 'stripe'
                        ? ui('Sicherer Zahlungsbereich wird erstellt …', 'Creating the secure payment area …')
                        : ui('Die Prüfung läuft weiter – bitte diese Seite geöffnet lassen und keinen zweiten Wechsel starten …', 'The check is still running — please keep this page open and do not start a second change …')}</strong>
                <span>{ui('Die Vorbereitung dauert normalerweise bis zu etwa 60 Sekunden; in Ausnahmefällen warten wir sicher bis zu 90 Sekunden.', 'Preparation normally takes up to about 60 seconds; in exceptional cases we safely wait for up to 90 seconds.')}</span>
                <span>{ui('Der sichere Zahlungsbereich ist bereits geöffnet und wird automatisch weitergeleitet.', 'The secure payment area is already open and will continue automatically.')}</span>
                <span>{ui(`Seit ${checkoutPreparationSeconds} Sekunden`, `${checkoutPreparationSeconds} seconds elapsed`)}</span>
              </div>
            ) : null}
            {checkoutRecovery?.checkoutUrl ? (
              <div className="info-box checkout-recovery" role="status">
                <strong>{ui('Sicherer Zahlungsbereich vorbereitet', 'Secure payment area is ready')}</strong>
                <p>{ui('Falls die automatische Weiterleitung nicht geöffnet wurde, setzen Sie denselben Wechsel hier fort. Bitte starten Sie keinen zweiten Paketwechsel.', 'If the automatic redirect did not open, continue the same change here. Do not start a second plan change.')}</p>
                <a className="primary-button" href={checkoutRecovery.checkoutUrl}>{ui('Jetzt fortfahren', 'Continue now')}</a>
              </div>
            ) : null}
            {detachedSubscriptionChanges.length ? (
              <section className="detached-subscription-changes" aria-label={ui('Offene Paketwechsel', 'Open plan changes')}>
                {detachedSubscriptionChanges.map((change) => renderSubscriptionChange(change, { detached: true }))}
              </section>
            ) : null}
            {!dashboard.subscriptions_loading && !dashboard.subscription_error && subscriptions.length === 0 ? (
              <p>{ui('Für diesen Business Hub ist derzeit kein Abonnement hinterlegt.', 'There is currently no subscription for this Business Hub.')}</p>
            ) : (
              <div className="customer-dashboard-grid subscription-card-grid">
                {displayedSubscriptions.map((subscription, subscriptionIndex) => {
                  const nextBillingDate = formatDate(subscription.next_billing_date);
                  const currentPlanCode = normalizeText(subscription.plan_code).toLowerCase();
                  const currentPlan = BUSINESS_PLANS.find((plan) => plan.code === currentPlanCode) || null;
                  const currentPlanKnown = Boolean(currentPlan);
                  const systemChangeQuote = subscription.system_change_quote || null;
                  const formatQuoteMoney = (amountMinor, currency) => new Intl.NumberFormat(
                    getLang() === 'de' ? 'de-DE' : 'en-GB',
                    { style: 'currency', currency: String(currency || 'EUR').toUpperCase() },
                  ).format(Number(amountMinor || 0) / 100);
                  const legacyAmountMinor = Number(subscription.legacy_display?.amount_minor);
                  const legacyPrice = Number.isSafeInteger(legacyAmountMinor) && legacyAmountMinor >= 0
                    ? new Intl.NumberFormat(getLang() === 'de' ? 'de-DE' : 'en-GB', {
                      style: 'currency',
                      currency: subscription.legacy_display?.currency || subscription.currency || 'EUR',
                    }).format(legacyAmountMinor / 100)
                    : null;
                  const isFirstActive = subscriptionIndex === 0 && activeSubscriptions.length > 0;
                  const isFirstPrevious = subscriptionIndex === activeSubscriptions.length && previousSubscriptions.length > 0;
                  const subscriptionEntity = legalEntities.find((entity) => entity.id === subscription.legal_entity_id) || null;
                  const matchingSubscriptionChanges = subscriptionChanges.filter((change) =>
                    OPEN_SUBSCRIPTION_CHANGE_STATUSES.includes(change.status)
                    && String(change.source_subscription_id) === String(subscription.id)
                  );
                  const planChangePending = matchingSubscriptionChanges.length > 0;
                  const paymentAction = getSubscriptionPaymentAction(subscription);
                  const isPaymentRecoveryTarget = paymentRecoveryRequested && String(subscription.id) === String(paymentRecoverySubscriptionId);
                  const isImmediatePaymentRecovery = paymentAction === 'recover' || isPaymentRecoveryTarget;
                  return (<React.Fragment key={subscription.id}>
                  {isFirstActive ? <h3 className="subscription-section-heading">{ui('Aktive Abonnements', 'Active subscriptions')}</h3> : null}
                  {isFirstPrevious ? <div className="subscription-section-heading subscription-section-heading--previous">
                    <button
                      type="button"
                      className="subscription-history-toggle"
                      aria-expanded={showPreviousSubscriptions}
                      aria-controls={previousSubscriptions.map((item) => `previous-subscription-${item.id}`).join(' ')}
                      onClick={() => setShowPreviousSubscriptions((current) => !current)}
                    >
                      <span>{ui('Frühere Abonnements', 'Previous subscriptions')} ({previousSubscriptions.length})</span>
                      <span aria-hidden="true">{showPreviousSubscriptions ? '−' : '+'}</span>
                    </button>
                  </div> : null}
                  <article
                    className={`customer-dashboard-card subscription-card${isPaymentRecoveryTarget ? ' customer-dashboard-card--payment-recovery' : ''}${planChangePending ? ' customer-dashboard-card--has-open-change' : ''}`}
                    key={subscription.id}
                    id={paymentAction === 'ended' ? `previous-subscription-${subscription.id}` : undefined}
                    hidden={paymentAction === 'ended' && !showPreviousSubscriptions}
                    aria-current={isPaymentRecoveryTarget ? 'true' : undefined}
                  >
                    <h3>{currentPlan ? `YourGermanAddress ${currentPlan.name}` : ui('Altvertrag', 'Legacy contract')}</h3>
                    {subscriptionEntity ? <p className="subscription-scope-label">{ui('Unternehmen', 'Company')}: <strong>{legalEntityLabel(subscriptionEntity)}</strong></p> : null}
                    {currentPlan ? <p className="subscription-current-price"><strong>{currentPlan.price}</strong> {ui('pro Monat', 'per month')}</p> : null}
                    {!currentPlanKnown ? <div className="subscription-legacy-label">
                      {subscription.legacy_display?.title ? <span>{ui('Bisherige Bezeichnung', 'Previous name')}: <strong>{subscription.legacy_display.title}</strong></span> : null}
                      {legacyPrice ? <span>{ui('Bisheriger Preis', 'Previous price')}: <strong>{legacyPrice} / {billingIntervalLabel(subscription.legacy_display?.billing_interval || subscription.billing_interval)}</strong></span> : null}
                      <span>{ui('Diese Angaben stammen aus dem bestehenden Vertrag. Es erfolgt keine automatische Zuordnung zu einem heutigen Paket.', 'These details come from the existing contract. It is not automatically mapped to a current plan.')}</span>
                    </div> : null}
                    <p>{ui('Abo-ID', 'Subscription ID')}: {subscription.id}</p>
                    <div className="status-grid status-grid--stack-mobile">
                      <div className="status-box">
                        <span>Status</span>
                        <SubscriptionStatusBadge status={subscription.status} />
                      </div>
                      <div className="status-box">
                        <span>{ui('Intervall', 'Interval')}</span>
                        <strong>{billingIntervalLabel(subscription.billing_interval)}</strong>
                      </div>
                    </div>
                    <p>
                      {ui('Bestell-ID', 'Order ID')}: <strong>{subscription.order_id || '-'}</strong>
                      <br />
                      {ui('Nächste Abrechnung', 'Next billing date')}: <strong>{formatDate(subscription.next_billing_date)}</strong>
                      <br />
                      {ui('Zugang läuft bis', 'Access ends')}: <strong>{formatDate(subscription.access_ends_at)}</strong>
                      {subscription.cancelled_on ? (
                        <><br />{ui('Gekündigt am', 'Cancelled on')}: <strong>{formatDate(subscription.cancelled_on)}</strong></>
                      ) : null}
                      {subscription.billing_provider === 'stripe' ? (
                        <><br />{ui('Zahlungsart', 'Payment method')}: <strong>{paymentMethodLabel(subscription.payment_method_type)}</strong></>
                      ) : null}
                    </p>
                    {(subscription.billing_provider === 'stripe' || subscription.billing_provider === 'shopify_seal') && ['recover', 'manage'].includes(paymentAction) ? (
                      <>
                        <button
                          type="button"
                          className="action-button action-button--secondary"
                          disabled={openingPaymentMethodId === subscription.id || (subscription.billing_provider === 'shopify_seal' && (!currentPlanKnown || !systemChangeQuote))}
                          onClick={() => openStripePaymentMethod(subscription)}
                        >
                          {openingPaymentMethodId === subscription.id
                            ? ui(`Zahlungsart wird geöffnet… (${checkoutPreparationSeconds} s)`, `Opening payment method… (${checkoutPreparationSeconds}s)`)
                            : isImmediatePaymentRecovery
                              ? ui('Zahlung jetzt klären', 'Resolve payment now')
                              : ui('Zahlungsart ändern', 'Change payment method')}
                        </button>
                        {subscription.billing_provider === 'shopify_seal' && !currentPlanKnown ? (
                          <div className="info-box">{ui('Dieser Altvertrag ist noch keinem heutigen Tarif eindeutig zugeordnet. Ein automatischer Zahlungsartenwechsel bleibt deshalb gesperrt, bis Preis und Leistungen klassifiziert wurden.', 'This legacy contract has not yet been mapped unambiguously to a current plan. Automatic payment-method migration remains locked until price and services have been classified.')}</div>
                        ) : null}
                        {subscription.billing_provider === 'shopify_seal' && currentPlanKnown && !systemChangeQuote ? (
                          <div className="info-box">{ui('Die bisherigen und neuen Abrechnungskonditionen konnten nicht sicher verglichen werden. Der Wechsel bleibt geschützt gesperrt.', 'The current and new billing terms could not be compared safely. The migration remains locked.')}</div>
                        ) : null}
                        {subscription.billing_provider === 'shopify_seal' && paymentMethodConfirmationId === subscription.id ? (
                          <div className="info-box payment-method-confirmation" role="region" aria-label={ui('Zahlungsartenwechsel bestätigen', 'Confirm payment method change')}>
                            {systemChangeQuote?.requires_price_change_consent ? <>
                              <p><strong>{ui('Preis- und Währungsänderung', 'Price and currency change')}</strong></p>
                              <p>{ui(
                                isImmediatePaymentRecovery
                                  ? `Bisher: ${formatQuoteMoney(systemChangeQuote.source_amount_minor, systemChangeQuote.source_currency)} pro ${/month|monat/.test(systemChangeQuote.source_interval) ? 'Monat' : systemChangeQuote.source_interval}. Neu: ${formatQuoteMoney(systemChangeQuote.target_amount_minor, systemChangeQuote.target_currency)} pro Monat. Nach Bestätigung Ihrer neuen Zahlungsart wird der aktuell offene Zeitraum sofort zum neuen Preis abgerechnet.`
                                  : `Bisher: ${formatQuoteMoney(systemChangeQuote.source_amount_minor, systemChangeQuote.source_currency)} pro ${/month|monat/.test(systemChangeQuote.source_interval) ? 'Monat' : systemChangeQuote.source_interval}. Neu: ${formatQuoteMoney(systemChangeQuote.target_amount_minor, systemChangeQuote.target_currency)} pro Monat. Die erste Abbuchung zum neuen Preis erfolgt${nextBillingDate !== '-' ? ` am ${nextBillingDate}` : ' zum nächsten Abrechnungstermin'}. Heute erfolgt keine Belastung.`,
                                isImmediatePaymentRecovery
                                  ? `Current price: ${formatQuoteMoney(systemChangeQuote.source_amount_minor, systemChangeQuote.source_currency)} per ${/month|monat/.test(systemChangeQuote.source_interval) ? 'month' : systemChangeQuote.source_interval}. New price: ${formatQuoteMoney(systemChangeQuote.target_amount_minor, systemChangeQuote.target_currency)} per month. After confirming your new payment method, the currently outstanding period will be charged immediately at the new price.`
                                  : `Current price: ${formatQuoteMoney(systemChangeQuote.source_amount_minor, systemChangeQuote.source_currency)} per ${/month|monat/.test(systemChangeQuote.source_interval) ? 'month' : systemChangeQuote.source_interval}. New price: ${formatQuoteMoney(systemChangeQuote.target_amount_minor, systemChangeQuote.target_currency)} per month. The first charge at the new price is due${nextBillingDate !== '-' ? ` on ${nextBillingDate}` : ' on the next billing date'}. You will not be charged today.`
                              )}</p>
                              <label className="checkbox-row">
                                <input
                                  type="checkbox"
                                  checked={paymentTermsAcceptedId === subscription.id}
                                  onChange={(event) => setPaymentTermsAcceptedId(event.target.checked ? subscription.id : null)}
                                />
                                <span>{ui(
                                  `Ich stimme dem neuen Preis von ${formatQuoteMoney(systemChangeQuote.target_amount_minor, systemChangeQuote.target_currency)} pro Monat ausdrücklich zu.`,
                                  `I explicitly agree to the new price of ${formatQuoteMoney(systemChangeQuote.target_amount_minor, systemChangeQuote.target_currency)} per month.`
                                )}</span>
                              </label>
                            </> : <p>{ui(
                              isImmediatePaymentRecovery
                                ? 'Preis, Währung und monatlicher Abrechnungsrhythmus bleiben unverändert. Nach Bestätigung Ihrer neuen Zahlungsart wird der aktuell offene Zeitraum sofort abgerechnet.'
                                : `Preis, Währung und monatlicher Abrechnungsrhythmus bleiben unverändert. Ihre neue Zahlungsart gilt ab dem nächsten Abrechnungstermin${nextBillingDate !== '-' ? ` am ${nextBillingDate}` : ''}.`,
                              isImmediatePaymentRecovery
                                ? 'Price, currency and monthly billing interval remain unchanged. After confirming your new payment method, the currently outstanding period will be charged immediately.'
                                : `Price, currency and monthly billing interval remain unchanged. Your new payment method applies from the next billing date${nextBillingDate !== '-' ? ` on ${nextBillingDate}` : ''}.`
                            )}</p>}
                            <div className="button-row">
                              <button type="button" className="action-button" disabled={systemChangeQuote?.requires_price_change_consent && paymentTermsAcceptedId !== subscription.id} onClick={() => openStripePaymentMethod(subscription, true)}>
                                {isImmediatePaymentRecovery ? ui('Offene Zahlung jetzt klären', 'Resolve outstanding payment now') : ui('Zahlungsart sicher bestätigen', 'Confirm payment method securely')}
                              </button>
                              <button type="button" className="action-button action-button--secondary" onClick={() => { setPaymentMethodConfirmationId(null); setPaymentTermsAcceptedId(null); }}>
                                {ui('Abbrechen', 'Cancel')}
                              </button>
                            </div>
                          </div>
                        ) : null}
                      </>
                    ) : null}
                    {paymentAction === 'ended' ? (
                      <div className="info-box">{ui('Dieses Abonnement ist beendet. Dafür kann keine Zahlungsart mehr geändert werden.', 'This subscription has ended. Its payment method can no longer be changed.')}</div>
                    ) : null}
                    {matchingSubscriptionChanges.map((change) => renderSubscriptionChange(change))}
                    {(subscription.can_cancel ?? subscription.is_active) && !subscription.cancel_at_period_end && !planChangePending ? (
                      <div className="info-box">
                        <strong>{ui(
                          `Paket zum nächsten Abrechnungstermin${nextBillingDate !== '-' ? ` am ${nextBillingDate}` : ''} wechseln`,
                          `Change plan at the next billing date${nextBillingDate !== '-' ? ` on ${nextBillingDate}` : ''}`
                        )}</strong>
                        <p>{subscription.billing_provider === 'shopify_seal'
                          ? ui('Für den Wechsel bestätigen Sie einmalig Ihre Zahlungsart. Es wird keine Kündigung ausgelöst und kein neues Onboarding gestartet.', 'To make the change, confirm your payment method once. This does not trigger a cancellation or new onboarding.')
                          : ui('Das neue Paket beginnt am nächsten Abrechnungstermin. Es gibt keine anteilige Zwischenabrechnung.', 'The new plan starts on the next billing date. There is no prorated interim charge.')}</p>
                        {planChooserSubscriptionId !== subscription.id ? (
                          <button type="button" className="action-button action-button--secondary" onClick={() => setPlanChooserSubscriptionId(subscription.id)}>
                            {ui('Paket auswählen', 'Choose a plan')}
                          </button>
                        ) : <>
                        <div className="plan-choice-grid" role="radiogroup" aria-label={ui('Neues Paket', 'New plan')}>
                          {BUSINESS_PLANS.map((plan) => {
                            const isCurrentPlan = plan.code === currentPlanCode;
                            const isSelected = planChangeDrafts[subscription.id]?.target === plan.code;
                            return (
                              <label className={`plan-choice-card${isCurrentPlan ? ' plan-choice-card--current' : ''}${isSelected ? ' plan-choice-card--selected' : ''}`} key={plan.code}>
                                <span className="plan-choice-card__heading">
                                  <input
                                    type="radio"
                                    name={`target-plan-${subscription.id}`}
                                    value={plan.code}
                                    checked={isSelected}
                                    disabled={isCurrentPlan || planChangePending}
                                    onChange={(event) => setPlanChangeDrafts((current) => ({ ...current, [subscription.id]: { ...current[subscription.id], target: event.target.value } }))}
                                  />
                                  <strong>{plan.name}</strong>
                                </span>
                                {isCurrentPlan ? <span className="plan-choice-card__badge">{ui('Aktuelles Paket', 'Current plan')}</span> : null}
                                <span className="plan-choice-card__price">{plan.price} <small>{ui('/ Monat', '/ month')}</small></span>
                                <ul>
                                  <li>{ui('Ladungsfähige Geschäftsadresse', 'Summons-capable business address')}</li>
                                  <li>{ui(`Post-Digitalisierung innerhalb von ${plan.deadline}`, `Mail digitisation within ${plan.deadlineEn}`)}</li>
                                  <li>{ui('Behördlicher Empfangsservice verfügbar', 'Official-authority receiving service available')}</li>
                                </ul>
                              </label>
                            );
                          })}
                        </div>
                        <label className="checkbox-inline plan-change-confirmation">
                          <input
                            type="checkbox"
                            checked={planChangeDrafts[subscription.id]?.consent === true}
                            onChange={(event) => setPlanChangeDrafts((current) => ({ ...current, [subscription.id]: { ...current[subscription.id], consent: event.target.checked } }))}
                          />
                          <span>{ui(
                            `Ich bestätige den Wechsel zum nächsten Abrechnungstermin${nextBillingDate !== '-' ? ` am ${nextBillingDate}` : ''}.`,
                            `I confirm the change at the next billing date${nextBillingDate !== '-' ? ` on ${nextBillingDate}` : ''}.`
                          )}</span>
                        </label>
                        <button
                          type="button"
                          className="primary-button"
                          disabled={!planChangeDrafts[subscription.id]?.target || planChangeDrafts[subscription.id]?.consent !== true || !!changingPlanId || planChangePending}
                          onClick={() => requestPlanChange(subscription)}
                        >
                          {changingPlanId === subscription.id ? ui(`Wechsel wird vorbereitet… (${checkoutPreparationSeconds} s)`, `Preparing change… (${checkoutPreparationSeconds}s)`) : ui('Paketwechsel bestätigen', 'Confirm plan change')}
                        </button>
                        <button type="button" className="action-button action-button--secondary" disabled={changingPlanId === subscription.id} onClick={() => setPlanChooserSubscriptionId(null)}>
                          {ui('Paketauswahl schließen', 'Close plan selection')}
                        </button>
                        </>}
                      </div>
                    ) : null}
                    {subscription.lifecycle_status === 'cancelled_at_period_end' && subscription.is_effectively_active ? (
                      <div className="info-box">{ui('Dieses Abonnement ist gekündigt. Ihr Zugang bleibt bis zum Ende des bezahlten Zeitraums aktiv.', 'This subscription has been cancelled. Your access remains active until the end of the paid period.')}</div>
                    ) : null}
                    {subscription.can_withdraw ? (
                      <div className="info-box">
                        {subscription.cancel_at_period_end
                          ? ui('Ihre reguläre Kündigung ist bereits erfasst. Innerhalb der Widerrufsfrist können Sie weiterhin einen Widerruf anfragen.', 'Your regular cancellation has already been scheduled. You may still request a withdrawal within the withdrawal period.')
                          : ui('Sie können innerhalb der Widerrufsfrist widerrufen oder das Abonnement regulär zum Ende des bezahlten Zeitraums kündigen.', 'Within the withdrawal period you may request a withdrawal or cancel the subscription at the end of the paid period.')}
                      </div>
                    ) : null}
                    <div className="subscription-contract-actions">
                    {subscription.can_withdraw ? (
                      <button
                        type="button"
                        className="action-button action-button--danger"
                        disabled={withdrawingSubscriptionId === subscription.id || pendingWithdrawalSubscriptionIds.has(String(subscription.id))}
                        onClick={() => withdrawCustomerSubscription(subscription)}
                      >
                        {pendingWithdrawalSubscriptionIds.has(String(subscription.id))
                          ? ui('Widerruf angefragt', 'Withdrawal requested')
                          : withdrawingSubscriptionId === subscription.id
                            ? ui('Anfrage wird gesendet...', 'Sending request...')
                            : ui('Widerruf anfragen', 'Request withdrawal')}
                      </button>
                    ) : null}
                    {(subscription.can_cancel ?? subscription.is_active) && !subscription.cancel_at_period_end ? (
                      <button
                        type="button"
                        className="action-button action-button--danger"
                        disabled={cancellingSubscriptionId === subscription.id}
                        onClick={() => cancelCustomerSubscription(subscription)}
                      >
                        {cancellingSubscriptionId === subscription.id ? ui('Kündigung läuft...', 'Cancelling...') : ui('Abonnement kündigen', 'Cancel subscription')}
                      </button>
                    ) : subscription.is_effectively_active || subscription.billing_provider === 'stripe' ? null : (
                      <div className="info-box">{ui('Dieses Abonnement ist nicht aktiv oder bereits gekündigt.', 'This subscription is inactive or has already been cancelled.')}</div>
                    )}
                    </div>
                  </article>
                  </React.Fragment>);
                })}
              </div>
            )}
            <h2>{ui('Rechnungen', 'Invoices')}</h2>
            <p>
              {ui(
                'Hier finden Sie Ihre Originalrechnungen und Gutschriften aus sevDesk.',
                'Here you can find your original invoices and credit notes from sevDesk.'
              )}
            </p>
            {sevdeskInvoicesLoading ? (
              <div className="info-box">{ui('Rechnungen werden geladen …', 'Loading invoices …')}</div>
            ) : null}
            {sevdeskInvoicesError ? <div className="hint-box">{sevdeskInvoicesError}</div> : null}
            {sevdeskInvoicesEnabled === false ? (
              <div className="info-box">
                {ui('Der Rechnungsabruf ist derzeit noch nicht aktiviert.', 'Invoice access is not enabled yet.')}
              </div>
            ) : null}
            {sevdeskInvoicesEnabled && !sevdeskInvoicesLoading && !sevdeskInvoicesError && sevdeskInvoices.length === 0 ? (
              <p>{ui('Für dieses Kundenkonto wurden keine Rechnungen gefunden.', 'No invoices were found for this customer account.')}</p>
            ) : null}
            {sevdeskInvoices.length > 0 ? (
              <div className="customer-dashboard-grid">
                {sevdeskInvoices.map((invoice) => (
                  <article className="customer-dashboard-card" key={invoice.id}>
                    <h3>{invoice.invoice_number || ui(`Rechnung #${invoice.id}`, `Invoice #${invoice.id}`)}</h3>
                    {invoice.document_type === 'credit_note' ? <p><strong>{ui('Gutschrift', 'Credit note')}</strong></p> : null}
                    {invoice.header ? <p>{invoice.header}</p> : null}
                    <div className="status-grid status-grid--stack-mobile">
                      <div className="status-box">
                        <span>{ui('Betrag', 'Amount')}</span>
                        <strong>{formatInvoiceAmount(invoice)}</strong>
                      </div>
                      <div className="status-box">
                        <span>Status</span>
                        <strong>{sevdeskInvoiceStatus(invoice.status)}</strong>
                      </div>
                    </div>
                    <p>
                      {ui('Belegdatum', 'Document date')}: <strong>{formatDate(invoice.invoice_date)}</strong>
                    </p>
                    <button
                      type="button"
                      className="secondary-link-button"
                      disabled={downloadingInvoiceId === invoice.id}
                      onClick={() => downloadSevdeskInvoice(invoice)}
                    >
                      {downloadingInvoiceId === invoice.id
                        ? ui('Originalbeleg wird geladen …', 'Loading original document …')
                        : invoice.document_type === 'credit_note'
                          ? ui('Originalgutschrift herunterladen', 'Download original credit note')
                          : ui('Originalrechnung herunterladen', 'Download original invoice')}
                    </button>
                  </article>
                ))}
              </div>
            ) : null}
          </>
        )}

        {activeTab === 'company' && (
          <>
            <div className="card-section">
            <h2>{ui('Kundendaten', 'Account details')}</h2>
            <p><a href="?tab=guides#guide-companies" onClick={() => setActiveTab('guides')}>{ui('Bebilderte Anleitung: weitere Firma oder Gründung abschließen', 'Illustrated guide: another company or completing incorporation')}</a></p>
            <p>{ui('Ihre verifizierten Unternehmensdaten sind schreibgeschützt. Änderungen werden erst nach Prüfung übernommen.', 'Your verified company details are read-only. Changes are applied only after review.')}</p>
            <div className="status-grid status-grid--stack-mobile">
              <div className="status-box"><span>{ui('Unternehmensart', 'Company type')}</span><strong>{customerTypeLabel(activeCompanyData.customer_type)}</strong></div>
              <div className="status-box"><span>{ui('Firmenname', 'Company name')}</span><strong>{activeCompanyData.company_name || '-'}</strong></div>
              <div className="status-box"><span>{ui('Rechtsform', 'Legal form')}</span><strong>{activeCompanyData.legal_form || '-'}</strong></div>
              <div className="status-box"><span>{ui('Vertretung', 'Representative')}</span><strong>{activeCompanyData.representative_name || '-'}</strong></div>
            </div>
            {hasPendingCompanyRequest ? (
              <div className="info-box"><strong>{ui('Änderung beantragt / Prüfung läuft', 'Change requested / review in progress')}</strong><br />{ui('Ihre bisherigen Unternehmensdaten bleiben bis zur Freigabe aktiv.', 'Your existing company details remain active until approval.')}</div>
            ) : null}
            {companyWizardOpen ? (
              <CompanyDataWizard
                key={`${primaryVerification?.id || 'none'}-${isPlannedFounding ? 'founding' : 'change'}`}
                verification={primaryVerification}
                foundingCompletion={isPlannedFounding}
                busy={submittingCompany}
                ui={ui}
                onCancel={() => setCompanyWizardOpen(false)}
                onSubmit={submitCompanyRequest}
              />
            ) : (
              <button
                type="button"
                className="primary-button"
                disabled={hasPendingCompanyRequest || (!hasCompletedVerification && !canCompleteFounding)}
                onClick={() => setCompanyWizardOpen(true)}
              >
                {isPlannedFounding ? ui('Unternehmen gegründet', 'Company founded') : ui('Unternehmensdaten ändern', 'Change company details')}
              </button>
            )}
            <CompanyDataChangeHistory
              items={dashboard?.company_data_history || []}
              language={getLang()}
            />
            {primaryVerification?.legal_entity_id && primaryVerification?.identity_status === 'approved' && primaryVerification?.business_status === 'approved' ? (
              <section className="info-box" aria-labelledby="customer-agreement-generation-heading">
                <h3 id="customer-agreement-generation-heading">{ui('Neue Nutzungsvereinbarung', 'New usage agreement')}</h3>
                <p>{ui('Erzeugen Sie für dieses Unternehmensprofil eine neue, versionierte Vereinbarung aus den freigegebenen Firmendaten.', 'Generate a new versioned agreement for this company profile from the approved company data.')}</p>
                <label>{ui('Optional: Sollen Briefkastenschild oder Logo angepasst werden?', 'Optional: Should the mailbox name or logo be changed?')}
                  <select value={agreementDisplayDecision} onChange={(event) => setAgreementDisplayDecision(event.target.value)}>
                    <option value="">{ui('Keine Angabe', 'No answer')}</option>
                    <option value="unchanged">{ui('Nein, unverändert übernehmen', 'No, keep unchanged')}</option>
                    <option value="change_requested">{ui('Ja, zuerst anpassen', 'Yes, change first')}</option>
                  </select>
                </label>
                {agreementDisplayDecision === 'change_requested' ? <button type="button" className="secondary-button" onClick={() => setActiveTab('display')}>{ui('Schild & Logo jetzt öffnen', 'Open mailbox name & logo now')}</button> : null}
                <button type="button" className="primary-button" disabled={agreementGenerating} onClick={generateUsageAgreement}>
                  {agreementGenerating ? ui('Vereinbarung wird erzeugt …', 'Generating agreement …') : ui('Vereinbarung erzeugen', 'Generate agreement')}
                </button>
                {agreementMessage ? <p role="status">{agreementMessage}</p> : null}
              </section>
            ) : null}
            </div>
            <section className="card-section customer-billing-address" aria-labelledby="customer-billing-address-heading">
              <span className="eyebrow">{ui('Rechnungen', 'Billing')}</span>
              <h2 id="customer-billing-address-heading">{ui('Rechnungsadresse', 'Billing address')}</h2>
              <p>{ui(
                'Diese Anschrift wird ausschließlich für zukünftige Rechnungen und Zahlungsvorgänge verwendet. Bereits ausgestellte Rechnungen bleiben unverändert.',
                'This address is used only for future invoices and payment transactions. Existing invoices remain unchanged.'
              )}</p>
              {submittedFoundingRequest ? (
                <div className="info-box" role="status">
                  <strong>{ui('Gründung abgeschlossen: Bitte Rechnungsadresse prüfen', 'Formation completed: please review your billing address')}</strong><br />
                  {ui('Übernehmen Sie die neue Firmenanschrift nur, wenn sie bereits als Rechnungsadresse gelten soll.', 'Apply the new company address only if it should already be used as your billing address.')}
                </div>
              ) : null}
              <form className="form-grid" onSubmit={submitBillingAddress}>
                {stripeBillingSubscriptions.length ? (
                  <label className="full-width">{ui('Zugehöriger Vertrag', 'Related contract')}
                    <select value={billingSubscriptionId} onChange={(event) => { setBillingSubscriptionId(event.target.value); setBillingRecipientConfirmed(false); }} required>
                      <option value="">{ui('Bitte auswählen', 'Select')}</option>
                      {stripeBillingSubscriptions.map((subscription) => (
                        <option key={subscription.stripe_subscription_id} value={subscription.stripe_subscription_id}>
                          {subscription.plan_name || subscription.product_name || ui('Aktives Abonnement', 'Active subscription')} · {subscription.stripe_subscription_id}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                <label>{ui('Name', 'Name')}<input autoComplete="name" value={billingAddressDraft.name} onChange={(event) => setBillingAddressDraft((current) => ({ ...current, name: event.target.value }))} required /></label>
                <label>{ui('Firma (optional)', 'Company (optional)')}<input autoComplete="organization" value={billingAddressDraft.company} onChange={(event) => setBillingAddressDraft((current) => ({ ...current, company: event.target.value }))} /></label>
                <label className="full-width">{ui('Straße und Hausnummer', 'Street and number')}<input autoComplete="street-address" value={billingAddressDraft.street} onChange={(event) => setBillingAddressDraft((current) => ({ ...current, street: event.target.value }))} required /></label>
                <label className="full-width">{ui('Adresszusatz (optional)', 'Address line 2 (optional)')}<input value={billingAddressDraft.address_extra} onChange={(event) => setBillingAddressDraft((current) => ({ ...current, address_extra: event.target.value }))} /></label>
                <label>{ui('PLZ', 'Postal code')}<input autoComplete="postal-code" value={billingAddressDraft.postal_code} onChange={(event) => setBillingAddressDraft((current) => ({ ...current, postal_code: event.target.value }))} required /></label>
                <label>{ui('Ort', 'City')}<input autoComplete="address-level2" value={billingAddressDraft.city} onChange={(event) => setBillingAddressDraft((current) => ({ ...current, city: event.target.value }))} required /></label>
                <label>{ui('Bundesland/Region (optional)', 'State/region (optional)')}<input autoComplete="address-level1" value={billingAddressDraft.state} onChange={(event) => setBillingAddressDraft((current) => ({ ...current, state: event.target.value }))} /></label>
                <label>{ui('Land (ISO-Code)', 'Country (ISO code)')}<input autoComplete="country" inputMode="text" minLength="2" maxLength="2" value={billingAddressDraft.country} onChange={(event) => setBillingAddressDraft((current) => ({ ...current, country: event.target.value.toUpperCase() }))} required /></label>
                <div className="full-width billing-sync-status" aria-live="polite">
                  {activeBillingAddress?.synchronization ? (
                    <div className="status-grid status-grid--stack-mobile">
                      {(activeBillingAddress.synchronization.mode === 'controlled_contract' ? ['recipient_profile']
                        : ['stripe', 'sevdesk', ...(activeBillingAddress.synchronization.mode === 'legacy_seal_shopify' ? ['renewal_source'] : [])]).map((provider) => {
                        const result = activeBillingAddress.synchronization.results?.[provider] || 'not_connected';
                        const label = result === 'succeeded' ? ui('Synchronisiert', 'Synchronized')
                          : result === 'requires_review' ? ui('Übernahme für Folgeabrechnungen noch offen', 'Renewal billing update still pending')
                          : result === 'failed' ? ui('Fehlgeschlagen', 'Failed')
                            : result === 'awaiting_provider_switch' ? ui('Beim Wechsel zu Stripe vorgemerkt', 'Saved for a future Stripe switch')
                            : ui('Nicht verbunden', 'Not connected');
                        return <div className="status-box" key={provider}><span>{provider === 'recipient_profile' ? ui('Rechnungsempfänger Ihres Vertrags', 'Contract invoice recipient') : provider === 'stripe' ? 'Stripe' : provider === 'renewal_source' ? 'Seal / Shopify' : 'sevDesk'}</span><strong>{label}</strong></div>;
                      })}
                    </div>
                  ) : null}
                  {activeBillingAddress?.synchronization?.mode === 'legacy_seal_shopify' ? (
                    <p className="field-hint">{ui(
                      'Bei Seal/Shopify ist eine gespeicherte Adresse noch keine bestätigte Änderung für Folgeabrechnungen. Solange deren Übernahme offen ist, bleibt der Vorgang zur Bearbeitung offen. Bereits ausgestellte Rechnungen werden hierdurch nicht geändert.',
                      'For Seal/Shopify, saving an address does not confirm its use for renewals. The request remains open until renewal billing has been checked. Previously issued invoices are not changed.'
                    )}</p>
                  ) : null}
                  {billingAddressMessage ? <p className="info-box" role="status">{billingAddressMessage}</p> : null}
                </div>
                {stripeBillingSubscriptions.length ? (
                  <label className="full-width checkbox-row">
                    <input type="checkbox" checked={billingRecipientConfirmed} onChange={(event) => setBillingRecipientConfirmed(event.target.checked)} required />
                    <span>{ui(
                      'Ich bestätige, dass diese Anschrift für den ausgewählten Vertrag als Rechnungsempfänger für zukünftige Belege verwendet werden soll.',
                      'I confirm that this address is the invoice recipient for future documents under the selected contract.'
                    )}</span>
                  </label>
                ) : null}
                <button className="primary-button" type="submit" disabled={billingAddressSaving || (stripeBillingSubscriptions.length > 0 && (!billingSubscriptionId || !billingRecipientConfirmed))}>
                  {billingAddressSaving ? ui('Wird synchronisiert …', 'Synchronizing …') : ui('Rechnungsadresse bestätigen', 'Confirm billing address')}
                </button>
                {['partial', 'failed'].includes(activeBillingAddress?.status) ? (
                  <button className="secondary-link-button" type="button" onClick={retryBillingAddress} disabled={billingAddressSaving}>
                    {ui('Synchronisierung erneut versuchen', 'Retry synchronization')}
                  </button>
                ) : null}
              </form>
              <p className="field-hint">{ui(
                'Zuordnungen erfolgen ausschließlich über bestätigte technische Kundenkennungen. Namen oder ähnliche E-Mail-Adressen werden nicht zur Zuordnung verwendet.',
                'Accounts are matched only by verified technical customer identifiers. Names or similar email addresses are never used for matching.'
              )}</p>
            </section>
            <div className="card-section customer-email-settings">
            <div className="customer-email-settings__heading">
              <div>
                <span className="eyebrow">{ui('Konto & Kommunikation', 'Account & communication')}</span>
                <h2>{ui('E-Mail-Adressen', 'Email addresses')}</h2>
                <p>{ui(
                  'Eine Adresse wird nur einmal angezeigt. Die zugehörigen Verwendungsbereiche stehen direkt darunter.',
                  'Each address is shown once, with its associated uses listed directly below.'
                )}</p>
              </div>
            </div>
            <div className="customer-email-groups">
              {accountEmailGroups.map((group) => (
                <div className="customer-email-group" key={group.email}>
                  <strong>{group.email}</strong>
                  <div className="customer-email-group__labels">
                    {group.labels.map((label) => <span key={label}>{label}</span>)}
                  </div>
                </div>
              ))}
            </div>
            <p className="field-hint">{ui(
              'Sie entscheiden selbst, in welchen Bereichen eine bestätigte neue Adresse übernommen wird. Bestehende Bestellungen und Rechnungen bleiben unverändert.',
              'You decide where a confirmed new address is applied. Existing orders and invoices remain unchanged.'
            )}</p>
            <details className="customer-email-change">
              <summary>{ui('E-Mail-Adresse ändern', 'Change email address')}</summary>
              <form className="form-grid" onSubmit={requestEmailChange}>
                <label className="full-width">
                  {ui('Neue E-Mail-Adresse', 'New email address')}
                  <input type="email" value={newEmail} onChange={(event) => setNewEmail(event.target.value)} required />
                </label>
                <fieldset className="full-width">
                  <legend>{ui('Neue Adresse übernehmen für', 'Use the new address for')}</legend>
                  {[
                    ['login', ui('Anmeldung im Business Hub', 'Business Hub sign-in'), true],
                    ['contact', ui('Allgemeine Mitteilungen', 'General communication'), true],
                    ['billing', ui('Künftige Rechnungen', 'Future invoices'), true],
                    ['stripe', ui('Zahlungskonto', 'Payment account'), dashboard.account.email_addresses?.available_targets?.stripe !== false],
                    ['shopify_seal', ui('Bestehende Abonnements', 'Existing subscriptions'), dashboard.account.email_addresses?.available_targets?.shopify_seal],
                    ['nextcloud', ui('Dokumentenportal', 'Document portal'), nextcloudEmailTargetAvailable],
                  ].map(([target, label, available]) => (
                    <label key={target} className="checkbox-row">
                      <input
                        type="checkbox"
                        checked={emailChangeTargets.includes(target)}
                        disabled={!available}
                        onChange={() => toggleEmailChangeTarget(target)}
                      />
                      <span>{label}{!available ? ui(' (nicht verbunden)', ' (not connected)') : ''}</span>
                    </label>
                  ))}
                  {!nextcloudEmailTargetAvailable ? (
                    <div className="hint-box full-width">
                      {ui(
                        'Das persönliche Dokumentenkonto ist noch nicht eindeutig zugeordnet. Bitte kontaktieren Sie den Support, damit wir das richtige Konto einmalig sicher verknüpfen. Danach können Sie die Verbindung selbst verwalten.',
                        'Your personal document account has not yet been linked unambiguously. Please contact support so we can securely link the correct account once. You can manage the connection yourself afterwards.'
                      )}{' '}
                      <a href="mailto:info@yourgermanaddress.com">info@yourgermanaddress.com</a>
                    </div>
                  ) : null}
                </fieldset>
                <label>
                  {ui('Neue E-Mail-Adresse wiederholen', 'Repeat new email address')}
                  <input type="email" value={newEmailConfirmation} onChange={(event) => setNewEmailConfirmation(event.target.value)} autoComplete="email" required />
                </label>
                <label>
                  {ui('Aktuelles Passwort', 'Current password')}
                  <input type="password" value={emailChangePassword} onChange={(event) => setEmailChangePassword(event.target.value)} autoComplete="current-password" required />
                </label>
                {emailChangeMessage ? <div className="info-box full-width">{emailChangeMessage}</div> : null}
                <button className="primary-button full-width" type="submit" disabled={requestingEmailChange}>
                  {requestingEmailChange ? ui('Link wird versendet...', 'Sending link...') : ui('Neue E-Mail-Adresse bestätigen lassen', 'Send confirmation to new email')}
                </button>
              </form>
            </details>
            </div>
            <section className="card-section customer-notification-preferences" aria-labelledby="customer-notification-heading">
              <span className="eyebrow">{ui('Konto & Kommunikation', 'Account & communication')}</span>
              <h2 id="customer-notification-heading">{ui('Benachrichtigungen auswählen', 'Choose notifications')}</h2>
              <p>{ui(
                'Wichtige Hinweise zu Sicherheit, Zahlungen, Verträgen, Zugang, Verifizierung und handlungsnotwendiger Post bleiben immer aktiv.',
                'Important security, payment, contract, access, verification and action-required mail notices always remain enabled.'
              )}</p>
              {notificationPreferencesLoading ? <p role="status">{ui('Einstellungen werden geladen …', 'Loading settings …')}</p> : null}
              {notificationPreferences ? (
                <form onSubmit={saveNotificationPreferences}>
                  <fieldset className="notification-preference-group">
                    <legend>{ui('Immer aktiv', 'Always enabled')}</legend>
                    {[
                      ui('Sicherheit und Kontozugang', 'Security and account access'),
                      ui('Zahlungen und Rechnungen', 'Payments and billing'),
                      ui('Verträge und rechtliche Hinweise', 'Contracts and legal notices'),
                      ui('Verifizierung und erforderliche Aktionen', 'Verification and required actions'),
                      ui('Wichtige Post mit Handlungsbedarf', 'Important mail requiring action'),
                    ].map((label) => (
                      <label className="notification-preference-row notification-preference-row--locked" key={label}>
                        <input type="checkbox" checked disabled />
                        <span><strong>{label}</strong><small>{ui('Kann zu Ihrer Sicherheit nicht deaktiviert werden.', 'Cannot be disabled for your safety.')}</small></span>
                      </label>
                    ))}
                  </fieldset>
                  <fieldset className="notification-preference-group">
                    <legend>{ui('Optional', 'Optional')}</legend>
                    {[
                      ['service_updates', ui('Optionale Service-Neuigkeiten', 'Optional service news'), ui('Nicht dringende Hinweise zu Verbesserungen und neuen Möglichkeiten.', 'Non-urgent information about improvements and new options.')],
                      ['product_tips', ui('Produktinformationen und Nutzungstipps', 'Product information and usage tips'), ui('Hilfreiche Tipps ohne Einfluss auf Ihren Vertrag oder Zugang.', 'Helpful tips that do not affect your contract or access.')],
                    ].map(([key, label, description]) => (
                      <label className="notification-preference-row" key={key}>
                        <input
                          type="checkbox"
                          checked={notificationPreferences.optional?.[key] === true}
                          onChange={(event) => setNotificationPreferences((current) => ({
                            ...current,
                            optional: { ...current.optional, [key]: event.target.checked },
                          }))}
                        />
                        <span><strong>{label}</strong><small>{description}</small></span>
                      </label>
                    ))}
                  </fieldset>
                  {notificationPreferencesMessage ? <p className="info-box" role="status">{notificationPreferencesMessage}</p> : null}
                  <button type="submit" className="primary-button" disabled={notificationPreferencesSaving}>
                    {notificationPreferencesSaving ? ui('Wird gespeichert …', 'Saving …') : ui('Auswahl speichern', 'Save choices')}
                  </button>
                </form>
              ) : null}
            </section>
          </>
        )}

        {activeTab === 'logistics' && (
          <>
            <h2>{ui('Zusatzleistungen', 'Additional services')}</h2>
            {additionalServicesAttentionCount > 0 ? (
              <div className="hint-box additional-services-payment-alert">
                <strong>{ui(
                  `${countText(additionalServicesAttentionCount, 'Zahlung ist', 'Zahlungen sind', 'payment is', 'payments are')} noch offen.`,
                  `${countText(additionalServicesAttentionCount, 'Zahlung ist', 'Zahlungen sind', 'payment is', 'payments are')} still open.`
                )}</strong>
                <span>{ui(
                  'Hier finden Sie sowohl offene Servicegebühren als auch später berechnete tatsächliche Versandkosten.',
                  'This includes open service fees and actual postage calculated later.'
                )}</span>
              </div>
            ) : null}
            {mailForwardingRequests.length > 0 ? (
              <div className="card-section">
                <div className="mail-forwarding-orders-heading">
                  <div>
                    <span className="eyebrow">{ui('Originale per Post', 'Originals by mail')}</span>
                    <h3>{ui('Briefversandaufträge', 'Mail forwarding requests')}</h3>
                  </div>
                  <span className="status-pill status-pill--info">
                    {countText(mailForwardingRequests.length, 'Auftrag', 'Aufträge', 'request', 'requests')}
                  </span>
                </div>
                {mailForwardingRequests.map((request) => {
                  const payments = request.additional_service_payments || [];
                  const serviceFee = payments.find((payment) => payment.payment_stage === 'service_fee');
                  const postage = payments.find((payment) => payment.payment_stage === 'actual_postage');
                  const serviceFeeExpired = isExpiredCheckout(serviceFee);
                  const postageExpired = isExpiredCheckout(postage);
                  return (
                    <div className="info-box additional-service-order" key={`mail-payment-${request.id}`}>
                      <strong>{ui(`Versandauftrag #${request.id}`, `Forwarding request #${request.id}`)}</strong>
                      <p>
                        {ui('Servicegebühr', 'Service fee')}: <strong>{serviceFee?.status === 'paid' ? ui('Bezahlt', 'Paid') : ui('Offen', 'Open')}</strong>
                        {' · '}
                        {ui('Tatsächliche Versandkosten', 'Actual postage')}: <strong>{postage?.status === 'paid' ? ui('Bezahlt', 'Paid') : postage ? ui('Offen', 'Open') : ui('Noch nicht berechnet', 'Not calculated yet')}</strong>
                      </p>
                      <div className="action-row">
                        {serviceFee?.status !== 'paid' && serviceFee?.checkout_url && !serviceFeeExpired ? (
                          <a className="primary-link-button" href={serviceFee.checkout_url}>{ui('Servicegebühr sicher bezahlen', 'Pay service fee securely')}</a>
                        ) : null}
                        {(!serviceFee || serviceFeeExpired || ['failed', 'expired', 'cancelled'].includes(serviceFee.status)) ? (
                          <>
                            <div className="hint-box">
                              {ui(
                                'Der bisherige Zahlungslink ist abgelaufen. Sie können denselben Auftrag fortsetzen oder stornieren. Ohne Entscheidung wird er nach sieben Tagen beendet und die Post zurück in Ihren Posteingang gelegt.',
                                'The previous payment link has expired. You can continue the same request or cancel it. If you take no action, it will end after seven days and the mail will return to your inbox.'
                              )}
                            </div>
                            <button
                              type="button"
                              className="action-button"
                              disabled={creatingMailPaymentId === request.id || cancellingMailRequestId === request.id}
                              onClick={() => recreateMailServiceCheckout(request.id)}
                            >
                              {creatingMailPaymentId === request.id
                                ? ui('Zahlungslink wird erstellt...', 'Creating payment link...')
                                : ui('Neuen Zahlungslink erstellen', 'Create a new payment link')}
                            </button>
                            <button
                              type="button"
                              className="secondary-button"
                              disabled={creatingMailPaymentId === request.id || cancellingMailRequestId === request.id}
                              onClick={() => cancelUnpaidMailForwarding(request.id)}
                            >
                              {cancellingMailRequestId === request.id
                                ? ui('Wird storniert...', 'Cancelling...')
                                : ui('Versandauftrag stornieren', 'Cancel forwarding request')}
                            </button>
                          </>
                        ) : null}
                        {postage?.status !== 'paid' && postage?.checkout_url && !postageExpired ? (
                          <a className="primary-link-button" href={postage.checkout_url}>{ui('Versandkosten sicher bezahlen', 'Pay postage securely')}</a>
                        ) : null}
                        {(serviceFee?.hosted_invoice_url || serviceFee?.invoice_pdf_url) ? (
                          <a className="secondary-link-button" href={serviceFee.invoice_pdf_url || serviceFee.hosted_invoice_url} target="_blank" rel="noreferrer">
                            {ui('Rechnung Servicegebühr', 'Service fee invoice')}
                          </a>
                        ) : null}
                        {(postage?.hosted_invoice_url || postage?.invoice_pdf_url) ? (
                          <a className="secondary-link-button" href={postage.invoice_pdf_url || postage.hosted_invoice_url} target="_blank" rel="noreferrer">
                            {ui('Rechnung Versandkosten', 'Postage invoice')}
                          </a>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}
            <div className="card-section">
              <h3>{ui('Paketweiterleitung beauftragen', 'Request parcel forwarding')}</h3>
              <p>{ui(
                'Wählen Sie die Gewichtsklasse. Die Servicegebühr wird jetzt bezahlt; die tatsächlichen DHL-Versandkosten erhalten Sie später separat.',
                'Choose the weight class. The service fee is paid now; actual DHL postage will be charged separately later.'
              )}</p>
              <div className="action-row">
                {[
                  ['bis_5kg', ui('Bis 5 kg · 10,00 €', 'Up to 5 kg · €10.00')],
                  ['bis_10kg', ui('Bis 10 kg · 20,00 €', 'Up to 10 kg · €20.00')],
                  ['bis_20kg', ui('Bis 20 kg · 30,00 €', 'Up to 20 kg · €30.00')],
                ].map(([weightClass, label]) => (
                  <button
                    key={weightClass}
                    type="button"
                    className="primary-button"
                    disabled={!!parcelCheckoutWeight}
                    onClick={() => openParcelForwardingCheckout(weightClass)}
                  >
                    {parcelCheckoutWeight === weightClass
                      ? ui('Sichere Zahlungsseite wird geöffnet…', 'Opening secure payment page…')
                      : label}
                  </button>
                ))}
              </div>
              {parcelCheckoutFormOpen ? (
                <div className="mail-forwarding-step" role="dialog" aria-modal="true" aria-labelledby="parcel-forwarding-title">
                  <form
                    className="mail-forwarding-form mail-forwarding-form--large"
                    onSubmit={async (event) => {
                      event.preventDefault();
                      try {
                        if (saveParcelForwardingAddress) {
                          await createAddressBookEntry({
                            address: parcelForwardingAddress,
                            label: parcelForwardingAddressLabel,
                          });
                          setSaveParcelForwardingAddress(false);
                        }
                        await startParcelForwardingCheckout(selectedParcelWeight, parcelForwardingAddress);
                      } catch (submitError) {
                        setError(submitError.message || ui('Paketweiterleitung konnte nicht vorbereitet werden.', 'Could not prepare parcel forwarding.'));
                      }
                    }}
                  >
                    <div className="mail-forwarding-form__header">
                      <div>
                        <span className="eyebrow">{ui('Paketweiterleitung', 'Parcel forwarding')}</span>
                        <h2 id="parcel-forwarding-title">{ui('Versandadresse festlegen', 'Choose shipping address')}</h2>
                        <p className="field-hint">{ui(
                          'Prüfen Sie die Zieladresse. Im nächsten Schritt bezahlen Sie die Servicegebühr über die sichere Zahlungsseite.',
                          'Check the destination address. In the next step you pay the service fee through the secure payment page.'
                        )}</p>
                      </div>
                      <button type="button" className="secondary-button" onClick={() => {
                        setParcelCheckoutFormOpen(false);
                        setSelectedParcelWeight('');
                      }}>
                        {ui('Abbrechen', 'Cancel')}
                      </button>
                    </div>
                    <ShippingAddressFields
                      address={parcelForwardingAddress}
                      setAddress={setParcelForwardingAddress}
                      addresses={addresses}
                    />
                    <label className="checkbox-row mail-forwarding-save-address">
                      <input
                        type="checkbox"
                        checked={saveParcelForwardingAddress}
                        onChange={(event) => setSaveParcelForwardingAddress(event.target.checked)}
                      />
                      <span>{ui('Diese Adresse zusätzlich im zentralen Adressbuch speichern', 'Also save this address in the central address book')}</span>
                    </label>
                    {saveParcelForwardingAddress ? (
                      <label>
                        {ui('Name der Adresse im Adressbuch', 'Address book label')}
                        <input
                          required
                          placeholder={ui('z. B. Büro Berlin', 'e.g. Berlin office')}
                          value={parcelForwardingAddressLabel}
                          onChange={(event) => setParcelForwardingAddressLabel(event.target.value)}
                        />
                      </label>
                    ) : null}
                    <div className="mail-forwarding-form__summary">
                      <span>{ui('Gewichtsklasse', 'Weight class')}</span>
                      <strong>{({
                        bis_5kg: ui('Bis 5 kg · 10,00 € Servicegebühr', 'Up to 5 kg · €10.00 service fee'),
                        bis_10kg: ui('Bis 10 kg · 20,00 € Servicegebühr', 'Up to 10 kg · €20.00 service fee'),
                        bis_20kg: ui('Bis 20 kg · 30,00 € Servicegebühr', 'Up to 20 kg · €30.00 service fee'),
                      })[selectedParcelWeight]} {ui('+ tatsächliches Porto', '+ actual postage')}</strong>
                    </div>
                    <button className="primary-button" type="submit" disabled={!!parcelCheckoutWeight}>
                      {parcelCheckoutWeight
                        ? ui('Sichere Zahlungsseite wird geöffnet…', 'Opening secure payment page…')
                        : ui('Weiter zur sicheren Zahlung', 'Continue to secure payment')}
                    </button>
                  </form>
                </div>
              ) : null}
            </div>
            {logisticsRequests.length === 0 ? (
              <p>{ui('Sie haben derzeit keine offenen oder abgeschlossenen Versandaufträge.', 'You currently have no open or completed forwarding requests.')}</p>
            ) : (
              <div className="customer-dashboard-grid">
                {logisticsRequests.map((request) => (
                  <article className="customer-dashboard-card" key={request.id}>
                    <h3>{logisticsServiceLabel(request.service_type, request.package_weight_class)}</h3>
                    <p>{request.shopify_order_name || request.shopify_order_id || ui('Bestellung', 'Order')}</p>
                    <div className="status-grid status-grid--stack-mobile">
                      <div className="status-box">
                        <span>Status</span>
                        <LogisticsStatusBadge status={request.status} />
                      </div>
                      <div className="status-box">
                        <span>DHL</span>
                        <strong>{request.dhl_tracking_number_outbound || 'Noch nicht versendet'}</strong>
                      </div>
                    </div>
                    {request.dhl_label_url ? (
                      <p>
                      <a href={request.dhl_label_url} target="_blank" rel="noreferrer">{ui('DHL-Information öffnen', 'Open DHL information')}</a>
                      </p>
                    ) : null}
                    {(request.additional_service_payments || []).map((payment) => (
                      <div className="info-box" key={payment.id}>
                        <strong>
                          {payment.payment_stage === 'service_fee'
                            ? ui('Servicegebühr', 'Service fee')
                            : ui('Versandkosten', 'Postage')}
                          : {(Number(payment.amount_cents || 0) / 100).toFixed(2)} {payment.currency || 'EUR'}
                        </strong>
                        <p>{ui('Zahlungsstatus', 'Payment status')}: {statusLabel(payment.status)}</p>
                        {payment.status !== 'paid' && payment.checkout_url && !isExpiredCheckout(payment) ? (
                          <a className="primary-link-button" href={payment.checkout_url}>
                            {ui('Jetzt sicher bezahlen', 'Pay securely now')}
                          </a>
                        ) : null}
                        {payment.payment_stage === 'service_fee' && payment.status !== 'paid' && isExpiredCheckout(payment) ? (
                          <button
                            type="button"
                            className="action-button"
                            disabled={creatingLogisticsPaymentId === request.id}
                            onClick={() => recreateLogisticsServiceCheckout(request.id)}
                          >
                            {creatingLogisticsPaymentId === request.id
                              ? ui('Zahlungslink wird erstellt...', 'Creating payment link...')
                              : ui('Neuen Zahlungslink erstellen', 'Create a new payment link')}
                          </button>
                        ) : null}
                        {payment.invoice_pdf_url ? (
                          <a href={payment.invoice_pdf_url} target="_blank" rel="noreferrer">
                            {ui('Rechnung herunterladen', 'Download invoice')}
                          </a>
                        ) : null}
                      </div>
                    ))}
                    <Link className="primary-link-button" to={`/logistics/${encodeURIComponent(request.token)}`}>
                        {ui('Logistikformular öffnen', 'Open logistics form')}
                    </Link>
                  </article>
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'display' && (
          <div className="card-section">
            <h2>{ui('Briefkasten', 'Mailbox')}</h2>
            <p>{ui('Sie bestimmen selbst, welcher Name und welches optionale Logo am Briefkasten angezeigt wird.', 'You choose the name and optional logo shown on the mailbox display.')}</p>
            <div className="info-box">
              <strong>{ui('Hinweise zur Beschriftung', 'Mailbox labelling tips')}</strong>
              <ul>
                <li>{ui('Bei Einzelunternehmen steht üblicherweise der persönliche Vor- und Nachname am Briefkasten; ein Geschäftsname kann ergänzt werden.', 'For sole proprietorships, the owner’s first and last name is usually shown; a business name may be added.')}</li>
                <li>{ui('Bei Gesellschaften sollte die vollständige Firmierung einschließlich Rechtsform erscheinen, zum Beispiel „Muster GmbH“ oder „Muster UG (haftungsbeschränkt)“.', 'Companies should use their full registered name including legal form.')}</li>
                <li>{ui('Wählen Sie eine eindeutige, gut lesbare Bezeichnung, damit Post sicher zugeordnet werden kann.', 'Choose a clear, legible name so mail can be assigned reliably.')}</li>
              </ul>
            </div>
            {primaryDisplayProfile ? (
              <div className="info-box">
                {primaryDisplayProfile?.logo_url ? (
                  <CustomerDisplayLogo logoUrl={primaryDisplayProfile?.logo_url} />
                ) : null}
                {ui('Aktuell gespeichert', 'Currently saved')}: <strong>{primaryDisplayProfile?.display_name || '-'}</strong>
              </div>
            ) : null}
            <form className="form-grid" onSubmit={submitDisplayRequest}>
              <label className="full-width">
                {ui('Gewünschter Briefkasten-Name', 'Desired mailbox name')}
                <input
                  type="text"
                  value={displayDraft.display_name}
                  onChange={(event) => setDisplayDraft((prev) => ({ ...prev, display_name: event.target.value }))}
                />
              </label>
              <label className="full-width">
                {ui('Hinweis / Wunsch', 'Note / request')}
                <textarea
                  rows="4"
                  value={displayDraft.display_note}
                  onChange={(event) => setDisplayDraft((prev) => ({ ...prev, display_note: event.target.value }))}
                />
              </label>
              <label className="full-width">
                {ui('Logo optional', 'Logo (optional)')}
                <span className="field-hint">
                  {ui('Bitte möglichst quadratisch hochladen, empfohlen 800 x 800 px, PNG oder JPG, maximal 2 MB. Logos mit transparentem Hintergrund wirken am besten.', 'Upload a square image if possible: 800 x 800 px recommended, PNG or JPG, maximum 2 MB. Logos with a transparent background work best.')}
                </span>
                <input type="file" accept="image/*,.pdf" onChange={(event) => setDisplayLogo(event.target.files?.[0] || null)} />
              </label>
              <button type="submit" className="primary-button full-width" disabled={submittingDisplay}>
                {submittingDisplay ? ui('Wird gespeichert...', 'Saving...') : ui('Briefkasten speichern', 'Save mailbox')}
              </button>
            </form>
          </div>
        )}
    </BusinessHubShell>
  );
}

